import { Sprite, Texture, Text, NineSliceSprite, Graphics, Container, Rectangle, TextureSource } from 'pixi.js';
import { AREA_HEIGHT, AREA_WIDTH, getObjectId, KnyttArea } from '../../engine/KnyttArea';
import { KnyttPoint } from '../../engine/KnyttPoint';
import { Collectable, parseFlag, PowerNames, type Flag, type JuniValues } from '../../engine/JuniValues';
import { parseIniInt } from '../../engine/KnyttUtil';
import { KnyttWarp } from '../../engine/KnyttWarp';
import { TILE_SIZE } from '../../render/Tileset';
import type { Game } from '../Game';
import type { Action } from '../Input';
import type { AreaView } from '../../render/AreaView';
import { KnyttObject } from './KnyttObject';
import {
  collectEntrySheets,
  getEntry,
  loadAnim,
  loadEntryAnim,
  loadFrames,
  loadObjectFrames,
  type AnimEntry,
  type EmitterConfig,
  type MuffParams,
  type ObjectEntry,
  type PathConfig,
  type SpikerConfig,
} from './ObjectGraphics';
import { objectName } from './objectLookup';
import { C } from '../Player';
import { getParticleConfig, type ParticleConfig } from '../Particles';

const DEFAULT_FPS = 10;
const MUFF_SPEED_SCALE = 50 / 8; // Muff.SPEED_SCALE

interface BouncerParams {
  initialSpeed: number;
  gravity: number;
  speedValues: number[];
  random: boolean;
  counter: number;
}
/**
 * Bouncer.cs's per-variant .tscn overrides (bank10 Bouncer1/2/Cauldron/Green =
 * 10,1/2/3/8). It treats `speed` as px-per-physics-tick at Godot's fixed 60Hz
 * tick (`Translate(0, speed)`, not `*delta`) while gravity accrues in real
 * px/s² (`speed += gravity*50*delta`). Converted to a real-seconds model: real
 * accel = gravity*50*60 px/s², real bounce speed = speedValues[i]*60 px/s.
 */
const BOUNCER_PARAMS: Record<string, BouncerParams> = {
  '10,1': { initialSpeed: 0, gravity: 0.2, speedValues: [2, 3, 4, 5, 6], random: false, counter: 0 },
  '10,2': { initialSpeed: 3, gravity: 0.2, speedValues: [2, 3, 4, 5, 6], random: true, counter: 0 },
  '10,3': { initialSpeed: 0, gravity: 0.2, speedValues: [2, 3, 4, 5, 6], random: false, counter: 1 },
  '10,8': { initialSpeed: 0, gravity: 0.17, speedValues: [2, 3, 4, 5], random: false, counter: 0 },
};
const BOUNCER_ACCEL_SCALE = 50 * 60;
const BOUNCER_SPEED_SCALE = 60;
const BOUNCER_BOUNCE_FPS = 25; // Bouncer1.tscn's "bounce" anim speed

interface WalkingBouncerParams {
  initialJumpSpeed: number;
  jumpGravity: number;
  smart: boolean; // true = SmartWalkingBouncer (jumps when Juni jumps nearby); false = RandomWalkingBouncer (random timer)
}
/**
 * WalkingBouncer.cs (base of SmartWalkingBouncer 10,6 / RandomWalkingBouncer
 * 10,7): a Muff patroller that also hops vertically. `jumpSpeed`/`jumpGravity`
 * use the same real-units-scaled-by-JUMP_SCALE(50) convention on both the
 * velocity-integration and position-integration steps (mirrored 1:1 below),
 * so — unlike Bouncer.cs — no extra derived scale factor is needed.
 * Values match the current .tscn overrides (upstream 10ef3cf4 re-tuned them from
 * screen recordings and dropped the JUMP_SCALE 45 fudge back to a clean 50).
 */
const WALKING_BOUNCER_PARAMS: Record<string, WalkingBouncerParams> = {
  '10,6': { initialJumpSpeed: 5.76, jumpGravity: 0.33, smart: true },
  '10,7': { initialJumpSpeed: 4.6, jumpGravity: 0.4, smart: false },
};
const WALKING_BOUNCER_JUMP_SCALE = 50;
const WALKING_BOUNCER_JUMP_CHANCE_INTERVAL = 0.25; // RandomWalkingBouncer's JumpTimer
const WALKING_BOUNCER_JUMP_REACT_RANGE = 150; // + random 0..80

interface HomingParams {
  initialSpeed: number;
  wallSpeed: number;
  airSpeed: number;
  defaultFps: number;
  wallFps: number;
}
/**
 * HomingEnemy.cs (HomingCircle 2,11 / HomingRect 2,12): inertia-smoothed
 * homing toward Juni's apparent position (flies through walls, unaffected by
 * collision — only its look/speed changes while overlapping one). Both
 * variants' sheets flatten to 10 "default" + 8 "wall" frames.
 */
const HOMING_PARAMS: Record<string, HomingParams> = {
  '2,11': { initialSpeed: 10, wallSpeed: 3, airSpeed: 7, defaultFps: 11, wallFps: 25 },
  '2,12': { initialSpeed: 10, wallSpeed: 4, airSpeed: 10, defaultFps: 10, wallFps: 25 },
};
const HOMING_SPEED_SCALE = 48 / 8;
const HOMING_MIN_DIST = 6;
const HOMING_DEFAULT_FRAME_COUNT = 10;

/**
 * LiquidPoolDeadly variants (bank1): the Area2D's CollisionShape2D is a thin
 * strip near the bottom of the tile (the animated wave band), not the whole
 * 24×24 cell — e.g. LiquidPool1.tscn's shape is extents(12,5) at local
 * (12,19), i.e. y 14..24. The generic SpriteObject default (`[x+2,y+2,
 * TILE_SIZE-4,TILE_SIZE-4]`) is much taller, so Juni died ~10-12px above the
 * visible water surface. `[y, h]` per id, full tile width (x 0..24).
 */
/**
 * Animation de-synchronization (audit #26): LiquidPool.cs randomizes SpeedScale
 * 0.6–1.0, starts at a random frame, and PING-PONGs playback (plays backwards on
 * animation_finished, then forwards, alternating); Waterfall.cs randomizes SpeedScale
 * 0.8–1.2; Updraft.cs starts at a random frame 0–20. WaterBlock.cs has none of this.
 */
const POOL_ANIM_IDS = new Set(['1,1', '1,2', '1,5', '1,6', '1,10', '1,12', '1,13', '1,14', '1,16', '1,19', '1,22']);
const WATERFALL_ANIM_IDS = new Set(['1,3', '1,4', '1,15', '1,20', '1,23']);
const UPDRAFT_ID = '15,5';

const LIQUID_POOL_DEADLY_BAND: Record<string, [number, number]> = {
  '1,1': [14, 10],
  '1,2': [14, 10],
  '1,5': [12, 12],
  '1,10': [12, 12],
  '1,12': [12, 12],
  '1,14': [14, 10],
  '1,19': [20, 4],
  '1,22': [18, 6],
};

/**
 * Deadly objects whose real Godot Area2D collision shape is meaningfully
 * smaller/offset from the tile — found by comparing each id's `deadly`
 * Area2D/CollisionShape2D (or CollisionPolygon2D bounding box) against the
 * generic `[x+2,y+2,20,20]` fallback. `[x,y,w,h]` per id, local tile coords
 * (some — RoofBlob/GroundBlob/GhostEater — are wider than one tile, since
 * their sprites span two tiles; that's fine, the rect is just offset from
 * the object's own tile origin, not clamped to it).
 */
const CUSTOM_DEADLY_BOX: Record<string, [number, number, number, number]> = {
  // HomingCircle/HomingRect (bank2): CircleShape2D r=8 at (12.5,13.5); RectangleShape2D extents(8,5) at (12,13).
  '2,11': [4.5, 5.5, 16, 16],
  '2,12': [4, 8, 16, 10],
  // RoofBlob (bank12): RectangleShape2D extents(18,4) at (24,4) on a 48-wide sprite.
  '12,6': [6, 0, 36, 8],
  // GroundBlob (bank12): RectangleShape2D extents(19,3) at (24,21).
  '12,9': [5, 18, 38, 6],
  // GhostEater (bank12, common/BaseEater.tscn): RectangleShape2D extents(15.777,1.5) at (24,22.5).
  '12,10': [8.22, 21, 31.55, 3],
  // RedJelly (bank14/Jelly.cs, common/Muff.tscn): CollisionPolygon2D bbox.
  '14,1': [1.75, 13, 20.375, 11],
  // DangerousInsect (bank14/Insect.tscn): CollisionPolygon2D bbox.
  '14,9': [1, 10.83, 22, 13.17],
  // BigRunThing/SmallRunThing (bank14/RunThing.tscn): CollisionPolygon2D bbox.
  '14,13': [2.02, 10.2, 19.97, 13.77],
  '14,14': [4.01, 12.23, 15.97, 11.81],
  // FastHomingSquare (bank2): RectangleShape2D extents(6.5,6.5) at (12.5,13.5).
  '2,32': [6, 7, 13, 13],
  // FlySpike1/2 (bank2, FlySpikeBase.tscn JuniHitArea): RectangleShape2D extents(7,5.5) at (12,13.5).
  '2,20': [5, 8, 14, 11],
  '2,21': [5, 8, 14, 11],
  // ---- second audit round (scripted sweep of every deadly Area2D vs the port rect) ----
  // RollerMuff2/8 (bank3, RollerMuff.tscn): CircleShape2D r=7 at (12,17).
  '3,2': [5, 10, 14, 14],
  '3,8': [5, 10, 14, 14],
  // bank4 crawlers/runners — per-scene Area2D shapes (lower-body boxes/polygons).
  '4,1': [4.1, 7.2, 15.5, 16.6], // CircleCrawler
  '4,2': [0, 3.9, 15, 16.3], // SideCrawler
  '4,4': [7, 17, 10, 7], // SixLegChild (tiny bug)
  '4,5': [9, 17, 6, 7], // SixLegBaby (tinier bug)
  '4,8': [0, 4.9, 14.7, 14], // SlowSideCrawler
  '4,9': [8, 4, 32, 20], // BigSpider (48-wide sprite)
  '4,13': [9.3, 3.9, 14.7, 15.9], // SideCrawler2
  '4,15': [5.2, 7.2, 13.5, 16.8], // AnotherStrangeRunner
  '4,17': [1, 10.8, 22, 13.2], // Hedgehog
  // ShadowWitch (5,1): RectangleShape2D extents(8,22) at (12,26) — two tiles tall.
  '5,1': [4, 4, 16, 44],
  // TrapCeiling (6,1): extents(45.9,9.7) at (48.1,12.9) — the full 4-tile-wide trap.
  '6,1': [2.3, 3.2, 91.8, 19.3],
  // Eater (6,5, common/BaseEater.tscn): same mouth band as GhostEater 12,10.
  '6,5': [8.22, 21, 31.55, 3],
  // PermanentSpikes (6,14-17): a 3px strip along the spiked edge only.
  '6,14': [1, 21, 22, 3], // Up (spikes on the floor)
  '6,15': [1, 0, 22, 3], // Down (ceiling)
  '6,16': [21, 1, 3, 22], // Left (spikes point left, on the tile's right edge)
  '6,17': [0, 1, 3, 22], // Right
  // Smart/RandomWalkingBouncer (10,6/10,7): lower-body boxes.
  '10,6': [2, 6, 20, 18],
  '10,7': [4, 10, 16, 14],
  // Purple/RedSpikedDisk (11,4/11,5): the disk rolls along the ground — bottom band.
  '11,4': [6.2, 14.1, 11.6, 9.9],
  '11,5': [4, 14, 16, 10],
  // SmallSpider (17,2): extents(4,3) at (12,21) — a genuinely tiny spider.
  '17,2': [8, 18, 8, 6],
  // bank-19 collectable creatures (wrapped enemy bases' own deadly areas).
  '19,7': [5, 8, 14, 11],
  '19,14': [4, 12, 16, 12],
  '19,23': [7, 17, 10, 7],
  '19,28': [0, 8, 24, 16],
  '19,31': [8.22, 21, 31.55, 3],
  '19,32': [0, 14, 24, 2],
};

/**
 * RedElemental/YellowElemental (bank2 2,19/18, Elemental.cs): harmless while
 * idle (cycling small idle frames), until Juni's Manhattan distance is < 66px,
 * then plays a one-shot "launch" burst and becomes deadly. The extractor
 * flattened both anims into one array with the big launch frames first (5),
 * then the small 24×24 idle frames — `launchFrameCount` is that split point.
 * Ported as a single-fire trigger (the C# teleport-immunity edge case, which
 * only matters if Juni warps directly adjacent to one, isn't replicated).
 */
const ELEMENTAL_LAUNCH_FRAME_COUNT: Record<string, number> = {
  '2,18': 5,
  '2,19': 5,
};
const ELEMENTAL_TRIGGER_DIST = 66;

interface CrawlerParams {
  horizontal: boolean;
  speed: number;
  keepDistance: number;
  radarTop: number;
  radarBottom: number;
}
/**
 * Crawler.cs (base of FloorDropCrawler 4,6 / CeilingDropCrawler 4,7 / SmileBall
 * 14,12): chases Juni's apparent position along one axis, stopping within
 * `keepDistance` or outside the perpendicular `radarTop`/`radarBottom` window
 * (0 = unlimited). All three bundled-behavior ids are horizontal-only, so the
 * vertical branch (and its walk/default/smile anim-state switching, which the
 * extractor doesn't split out per-name for these ids) isn't ported — only the
 * chase movement, which was the actual reported gap (frozen/inert enemies).
 */
const CRAWLER_PARAMS: Record<string, CrawlerParams> = {
  '4,6': { horizontal: true, speed: 50, keepDistance: 4, radarTop: 0, radarBottom: 0 },
  '4,7': { horizontal: true, speed: 50, keepDistance: 4, radarTop: 0, radarBottom: 0 },
  '14,12': { horizontal: true, speed: 50, keepDistance: 64, radarTop: 0, radarBottom: 0 },
};

/**
 * Renders an animated object sheet and drives the data-extracted behaviours:
 * ghost (Eye-gated visibility + flicker), muff (patrol movement), deadly
 * (kill on contact), solid (collision). Also the base for the special objects.
 */
class SpriteObject extends KnyttObject {
  protected sprite: Sprite | null = null;
  protected frames: Texture[] = [];
  protected fps = DEFAULT_FPS;
  protected animTime = 0;
  protected animIndex = 0;
  protected entry: ObjectEntry | null = null;
  // Per-instance playback variety (LiquidPool/Waterfall randomize SpeedScale;
  // pools also ping-pong so same-id tiles don't animate in lockstep with a loop seam).
  private speedScale = 1;
  private pingpong = false;
  private animDir = 1;
  // LiquidPool's optional "Halo" child sprite: its own looping animation clock.
  private halo: Sprite | null = null;
  private haloFrames: Texture[] = [];
  private haloFps = DEFAULT_FPS;
  private haloTime = 0;
  private haloIndex = 0;

  // muff movement
  private muff: MuffParams | null = null;
  private mx = 0;
  private my = 0;
  private speed = 0;
  private dir = 1;
  private decel = 0;
  private speedTimer = 0;
  private dirTimer = 0;

  // RollerMuff (bank3 3,2/3,8): periodic "charge" — speed snaps to 24 towards Juni
  // and plays "roll" until colliding, then resumes the normal speed/direction timers.
  private rollTimer = 0;
  private rolling = false;
  private rollFrames: Texture[] = [];
  private rollFps = 12;

  // GhostWalker (bank12 GhostWalker.cs): speeds up + flickers faster while phasing
  // through a wall (tracked via a collision probe at the leading edge, replacing its
  // Area2D enter/exit signals).
  private inWall = false;

  // DistanceMuff (common/DistanceMuff.cs): speed/direction timers pause while Juni is
  // within range (SpikerMuff also layers `spiker` on top of this for the visible spikes).
  private haltedByDistance = false;

  // RunThing (bank14 BigRunThing/SmallRunThing): fight-or-flight — chase within
  // safeDistance at attackSpeed, flee beyond it at a random patrol speed.
  private runThingTimer = 0;

  // WalkingShooter (Ninja/DarkRandomizer/StrangeRunner/HomingRunner): periodic
  // walk→prepare→shoot→aftershot cycle around the Muff patrol + its own emitter.
  private wsPhase: 'walk' | 'prepare' | 'aftershot' = 'walk';
  private wsTimer = 0;
  private wsShotDelay = 0;
  private wsPrepareFrames: Texture[] = [];
  private wsAftershotFrames: Texture[] = [];
  private wsPrepareFps = DEFAULT_FPS;
  private wsAftershotFps = DEFAULT_FPS;

  // WallNinja (Left|RightNinja, Left|Right(Super)Stranger): vertical wall-climber,
  // periodic prepare→volley→aftershot cycle; climb/slide anims swap with direction.
  private wnPhase: 'walk' | 'prepare' | 'aftershot' = 'walk';
  private wnTimer = 0;
  private wnClimbFrames: Texture[] = [];
  private wnSlideFrames: Texture[] = [];
  private wnPrepareFrames: Texture[] = [];
  private wnAftershotFrames: Texture[] = [];
  private wnPrepareFps = DEFAULT_FPS;
  private wnAftershotFps = DEFAULT_FPS;

  // ghost flicker
  private ghost = false;
  private flipTime = 0.065;
  private flipCounter = 0;
  private flipTarget = 0.45;
  private modAlpha = 0.45;

  private deadly = false;
  private organic = false; // triggers Juni's enemy-detector glow

  // BaseEater (bank6 Eater 6,5 / bank12 GhostEater 12,10): plays a one-shot "eat"
  // animation + Chomp sfx on contact (BaseEater.cs kills Juni the same frame — the
  // poof takes over rendering immediately after, so this is a same-frame flourish).
  protected eatFrames: Texture[] = [];
  protected eatFps = 20;

  // BaseWaterMonster (WaterEye 17,3 / WaterMonster 18,6): periodic prepare→volley→
  // aftershot cycle around the object's own emitter (idle between cycles).
  private wmPhase: 'idle' | 'prepare' | 'aftershot' = 'idle';
  private wmTimer = 0;
  private wmPrepareFrames: Texture[] = [];
  private wmAftershotFrames: Texture[] = [];
  private wmPrepareFps = DEFAULT_FPS;
  private wmAftershotFps = DEFAULT_FPS;

  // BuzzFlyer (Flything family): random 8-direction buzz flight.
  private bzX = 0;
  private bzY = 0;
  private bzDx = 0;
  private bzDy = 0;

  // Fly (2,3/2,4): startled buzz-flight once Juni is near; lands on solids.
  private flyStartled = false;
  private flyAirborne = false;
  private flyUpTime = 0; // upward bias while > 0 (FlyUpTimer, 1s)
  private flyX = 0;
  private flyY = 0;
  private flyFlyFrames: Texture[] = [];
  private flyStopFrames: Texture[] = [];

  // Firefly (2,8/13/23): drifting random walk; speed/direction/opacity all buzz.
  private ffX = 0;
  private ffY = 0;
  private ffSpeed = 0;
  private ffDir = 0;
  private ffOpacity = 1;
  private ffAnims: { idle: Texture[]; fly: Texture[]; run: Texture[] } | null = null;

  // Insect (14,11) / Jelly (RedJelly 14,1) Muff nuances.
  private insectTimer = 0;
  private jellyTimer = 0;

  // Humbird (2,25): faces Juni; periodic 112px vertical arc over 2.9s.
  private hbTimer = 0;
  private hbTime = 0;
  private hbAirborne = false;
  private hbY = 0;
  private hbFlyFrames: Texture[] = [];
  private hbStopFrames: Texture[] = [];

  // Spider run-bursts (BigSpider 4,9 / TriLegSpider 4,14).
  private srX = 0;
  private srDir = 1;
  private srRunning = false;
  private srTimer = 0;
  private srWalkFrames: Texture[] = [];
  private srWalkFps = DEFAULT_FPS;
  private srIdleFrames: Texture[] = [];

  // ShadowWitch (5,1): anger meter — deadly + visible only while angry.
  private swValue = 0;
  private swAngry = false;
  private swAngryFrames: Texture[] = [];
  private swIdleFrames: Texture[] = [];

  // ProximityBlock / FalseBlock / FadeBlock alpha effects.
  private flsFading = false;
  private fadePos = 0;

  // Crumble (15,37/38): collapses after Juni lands on it (or steps off early).
  private crState: 'idle' | 'armed' | 'gone' = 'idle';
  private crTimer = 0;
  private crDestroyFrames: Texture[] = [];
  private crDestroyFps = DEFAULT_FPS;
  private crDisappearFrames: Texture[] = [];
  private crDisappearFps = DEFAULT_FPS;
  private crActivateFrame: Texture | null = null;

  // spiker (DistanceMod/SpikerMod): spikes extend when Juni is near, retract when
  // far; deadly only while extended. Replaces the always-deadly behaviour.
  private spiker: SpikerConfig | null = null;
  private spikerOpen: Texture[] = [];
  private spikerIdle: Texture | null = null;
  private spikerExtended = false;
  // Juni is inside a retracted spiker's area with a hologram out (SpikerMod's
  // HologramStopped subscription): dismissing the decoy extends + kills instantly.
  private spikerHoloArmed = false;
  private spikerIndex = 0; // position within the open animation (0=retracted)
  private spikerTimer = 0;

  // path following (FlyBot etc.)
  private path: number[][] | null = null;
  private pathSpeed = 0;
  private pathSegLen: number[] = [];
  private pathTotal = 0;
  private pathOffset = 0;
  private px = 0;
  private py = 0;

  // PathCreature (Fish1-5, Birdie/Birdie1/Birdie2): ping-pong/loop with stop/flip/run
  // pauses at path ends (PathCreature.cs), on top of the shared path polyline.
  private pcForward = true;
  private pcStopped = false;
  private pcStage: 'flip' | 'run' | null = null;
  private pcWait = 0;
  private pcFlip = false;
  private pcStopFrames: Texture[] = [];
  private pcDefaultFrames: Texture[] = [];
  private pcDefaultFps = DEFAULT_FPS;

  // bouncer (Bouncer.cs: free-fall + bounce off the ground)
  private bouncer: BouncerParams | null = null;
  private bounceCounter = 0;
  private by = 0;
  private bvy = 0;
  private bounceAnimTimer = 0;
  private bounceIndex = 0;

  // walking bouncer (WalkingBouncer.cs: Muff patrol + vertical hop)
  private walkingBouncer: WalkingBouncerParams | null = null;
  private wbInAir = false;
  private wbVel = 0; // internal units — matches C#'s `jumpSpeed` before *JUMP_SCALE
  private wbJumpTimer = 0;
  private wbUnsub: (() => void) | null = null;
  private wbJumpFrames: Texture[] = [];
  private wbWalkFrames: Texture[] = [];
  private wbIdleFrame: Texture | null = null;
  private wbJumpFps = 35;
  private wbWalkFps = 25;
  private wbAnimTime = 0;
  private wbAnimIndex = 0;

  // crawler (Crawler.cs: chase Juni along one axis)
  private crawler: CrawlerParams | null = null;
  private crx = 0;
  private cry = 0;

  // SelfDropper (bank6 6,8): harmless until Juni is near-x, then drops straight down
  // under a mild gravity, its deadly Area2D disabled until the drop starts and its
  // width shrinking per-frame to match the "launch" animation (SelfDropper.cs).
  private selfDropper = false;
  private sdDropping = false;
  private sdDropped = false;
  private sdY = 0;
  private sdVy = 0;
  private static readonly SD_TRIGGER_DIST = 12;
  private static readonly SD_SCALE = [0.4, 0.4, 0.5, 0.9, 1];

  // GesturesObject (common/GesturesObject.cs): idle-cycles a random non-"_"-prefixed
  // animation every [minTime,maxTime]s, optionally flipping to a random facing.
  private gestures: { minTime: number; maxTime: number; bidirectional: boolean; anims: Record<string, Texture[]> } | null = null;
  private gesturesFps: Record<string, number> = {};
  private gestureTimer = 0;

  // AttackBlock (bank12 12,14): harmless + retracted until Juni approaches, then
  // plays the "open" growth animation once and stays deadly, hitbox growing
  // upward from the base to match the animated spike height.
  private attackBlock = false;
  private abTriggered = false;
  private abIndex = 0;
  private abTimer = 0;
  private static readonly ATTACK_BLOCK_OPEN_DIST = 60;
  private static readonly ATTACK_BLOCK_HEIGHT = 72;
  private static readonly ATTACK_BLOCK_FPS = 25;

  // FlyingBomb (bank12 12,15): patrols the area edges, dive-bombs when Juni is
  // within x-range, then arcs back up to its original height.
  private flyingBomb = false;
  private fbx = 0;
  private fby = 0;
  private fbXSpeed = 0;
  private fbYSpeed = 0;
  private fbInAttack = false;
  private fbAttackCooldown = 0;
  private fbWalkFrames: Texture[] = [];
  private fbAttackFrames: Texture[] = [];
  private fbWalkFps = 10;
  private fbAttackFps = 37;
  private fbAnimTime = 0;
  private fbAnimIndex = 0;

  // RedElemental/YellowElemental (bank2 2,19/18): harmless until Juni is close, then a one-shot deadly launch burst.
  private elemental = false;
  private elementalLaunched = false;
  private elementalImmune = false; // Elemental.cs: a warp landing Juni on it doesn't kill
  private elementalPrevJx = 0;
  private elementalPrevJy = 0;
  private elementalLaunchFrames: Texture[] = [];

  // LabyrinthSpike family (bank6 6,2-4): wanders cardinal directions, turning
  // 90° at random when blocked. RandomLabyrinthSpike also rerolls its speed.
  private labyrinthSpike: { randomSpeed: boolean } | null = null;
  private lsx = 0;
  private lsy = 0;
  private lsDirX = 0;
  private lsDirY = -1; // Vector2.Up
  private lsSpeed = 50;

  // TrapCeiling (bank6 6,1): oscillates between floor and ceiling.
  private trapCeiling = false;
  private tcMovingUp = true;
  private tcy = 0;

  // HomingEnemy (bank2 2,11/12): inertia-smoothed homing, flies through walls.
  private homing: HomingParams | null = null;
  private hex = 0;
  private hey = 0;
  private heVx = 0;
  private heVy = 0;
  private heSpeed = 0;
  private heInWall = false;
  private heAnimTime = 0;
  private heAnimIndex = 0;

  // LookMuff/GhostLookMuff (bank3 3,14/26/38/41, bank12 4/7): stationary, flips
  // to face Juni's apparent x (with an 8px dead zone, per LookMuff.cs).
  private look = false;

  // BackgroundBird (2,31): cosmetic screen-wrap parallax decoration.
  private bird = false;
  private birdX = 0;
  private birdDir = 1;
  private birdSpeed = 0;
  private birdFly0: Texture[] = [];
  private birdFly1: Texture[] = [];
  private static readonly BIRD_SCREEN_WIDTH = 600;

  // bullet emitter
  private emitter: EmitterConfig | null = null;
  private fireTimer = 0;
  // RollerGenerator.cs (13,6): prepare/shot sounds run on their own timers after each
  // emit (Sound1Timer 1.44s → 'Roller Shot A', Sound2Timer 2.3s → 'Roller Shot B');
  // FirstShotTimer plays the shot sound 0.1s after load.
  private rgSndA = 0;
  private rgSndB = 0;
  private rgInit = false;
  // DropFlything spray state + StuffShooter's one-shot launch animation.
  private dvTicksLeft = 0;
  private dvTimer = 0;
  private emLaunchFrames: Texture[] = [];
  private emLaunchFps = DEFAULT_FPS;
  private emLaunchTimer = 0;
  private emBaseFrames: Texture[] = [];
  // TrapFlower gate pose: walks the "open" frames in while Juni is in range, out when not.
  private gaFrames: Texture[] = [];
  private gaFps = DEFAULT_FPS;
  private gaIdx = 0;
  private gaTimer = 0;

  // FlySpike (2,20/2,21): horizontal dash / flip / wait cycle.
  private fsX = 0;
  private fsFacing = 1;
  private fsWait = 0;
  private fsSpeed = 200;
  // Cannon burst state: emit `count` bullets at `rate` once per `period`.
  private burstLeft = 0;
  private burstTimer = 0;
  private burstIndex = 0;

  // TimedFlower/SmallTimedFlower (bank9 9,2/9,5): toggles open/closed, firing while open.
  private toggleShoot: { toggleInterval: number; shotInterval: number; shots: number | null } | null = null;
  private tsOpen = false;
  private tsToggleTimer = 0;
  private tsShotTimer = 0;
  private tsShotsLeft = 0;
  private tsOpenFrames: Texture[] = [];
  private tsOpenFps = 15;

  /** Current world position (path > muff > tile origin). */
  protected curX(): number {
    if (this.path) return this.px;
    if (this.muff) return this.mx;
    if (this.crawler) return this.crx;
    if (this.flyingBomb) return this.fbx;
    if (this.labyrinthSpike) return this.lsx;
    if (this.homing) return this.hex;
    if (this.bird) return this.birdX;
    if (this.entry?.buzz) return this.bzX;
    if (this.entry?.fly) return this.flyX;
    if (this.entry?.firefly) return this.ffX;
    if (this.entry?.spiderRun) return this.srX;
    if (this.entry?.flySpike) return this.fsX;
    return this.worldX;
  }
  protected curY(): number {
    if (this.path) return this.py;
    if (this.muff) return this.my;
    if (this.bouncer) return this.by;
    if (this.crawler) return this.cry;
    if (this.flyingBomb) return this.fby;
    if (this.labyrinthSpike) return this.lsy;
    if (this.trapCeiling) return this.tcy;
    if (this.homing) return this.hey;
    if (this.selfDropper) return this.sdY;
    if (this.entry?.buzz) return this.bzY;
    if (this.entry?.fly) return this.flyY;
    if (this.entry?.firefly) return this.ffY;
    if (this.entry?.humbird) return this.hbY;
    return this.worldY;
  }

  override async load(): Promise<void> {
    const entry = getEntry(this.objectId);
    this.entry = entry;
    // Eye-gate: ghost objects only exist when Juni has the Eye power.
    if (entry?.ghost && !this.game.values.getPower(PowerNames.Eye)) {
      this.remove();
      return;
    }

    const gfx = await loadObjectFrames(this.game, this.objectId);
    if (gfx) {
      this.frames = gfx.frames;
      this.fps = gfx.fps;
      this.sprite = new Sprite(gfx.frames[0]);
      // Godot scenes may place the art away from the tile top-left. A non-centered
      // sprite gets its extracted position+offset (`spriteOffset`, e.g. Eater's art at
      // (0,-24) spanning the tile above); a *centered* one re-centres every frame around
      // its point (`spriteCenter`, e.g. Elemental's 24px idle + 96px launch both centred
      // on the tile) — anchor(.5,.5) reproduces that for any frame size.
      if (gfx.spriteCenter) {
        this.sprite.anchor.set(0.5, 0.5);
        this.sprite.position.set(gfx.spriteCenter[0], gfx.spriteCenter[1]);
      } else {
        this.sprite.anchor.set(0, 0);
        if (gfx.spriteOffset) this.sprite.position.set(gfx.spriteOffset[0], gfx.spriteOffset[1]);
      }
      if (gfx.additive) this.sprite.blendMode = 'add';
      this.container.addChild(this.sprite);
    }

    // Optional halo sprite behind the main animation (LiquidPool 1,5/1,10/1,12/1,22 —
    // LiquidPool.cs plays both; Halo is node index 1, the main sprite index 2).
    if (entry?.halo && this.sprite) {
      const halo = await loadEntryAnim(this.game, entry.halo);
      if (halo) {
        const haloSprite = new Sprite(halo.frames[0]);
        haloSprite.position.set(entry.halo.offset[0], entry.halo.offset[1]);
        haloSprite.alpha = entry.halo.alpha;
        if (entry.halo.additive) haloSprite.blendMode = 'add';
        this.container.addChildAt(haloSprite, this.container.getChildIndex(this.sprite));
        // Upstream 9683f0bf turned the halos into real animations (1,10/1,12: 2
        // frames @25fps, 1,22: 10 @8fps), so they need their own frame clock.
        this.halo = haloSprite;
        this.haloFrames = halo.frames;
        this.haloFps = entry.halo.fps ?? DEFAULT_FPS;
      }
    }

    // LiquidPool/Waterfall/Updraft animation variety (see POOL_ANIM_IDS above).
    if (this.frames.length > 1) {
      const key = this.objectId.key;
      if (POOL_ANIM_IDS.has(key)) {
        this.speedScale = 0.6 + Math.random() * 0.4;
        this.pingpong = true;
        this.animIndex = Math.floor(Math.random() * this.frames.length);
      } else if (WATERFALL_ANIM_IDS.has(key)) {
        this.speedScale = 0.8 + Math.random() * 0.4;
      } else if (key === UPDRAFT_ID) {
        this.animIndex = Math.floor(Math.random() * Math.min(21, this.frames.length));
      }
      if (this.animIndex > 0 && this.sprite) this.sprite.texture = this.frames[this.animIndex];
    }

    if (entry?.solid && !this.neutralized)
      this.game.collision.stampRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
    this.deadly = !!entry?.deadly && !this.neutralized;
    this.ghost = !!entry?.ghost;
    if (this.ghost && this.sprite) this.sprite.alpha = this.modAlpha;

    if (entry?.eat) {
      const eat = await loadEntryAnim(this.game, entry.eat);
      if (eat) {
        this.eatFrames = eat.frames;
        this.eatFps = eat.fps;
      }
    }

    if (entry?.waterMonster) {
      const wm = entry.waterMonster;
      const prepare = await loadEntryAnim(this.game, wm.prepare);
      if (prepare) {
        this.wmPrepareFrames = prepare.frames;
        this.wmPrepareFps = prepare.fps;
      }
      if (wm.aftershot) {
        const aftershot = await loadEntryAnim(this.game, wm.aftershot);
        if (aftershot) {
          this.wmAftershotFrames = aftershot.frames;
          this.wmAftershotFps = aftershot.fps;
        }
      }
      // yknytt's firstDelay=0.3 fires the first cycle almost immediately.
      this.wmTimer = 0.3;
    }

    if (entry?.buzz) {
      this.bzX = this.worldX;
      this.bzY = this.worldY;
      this.buzzChangeDirection();
    }

    if (entry?.fly) {
      this.flyX = this.worldX;
      this.flyY = this.worldY;
      const fly = await loadAnim(this.game, this.objectId, `fly${this.objectId.y}`);
      const stop = await loadAnim(this.game, this.objectId, `stop${this.objectId.y}`);
      if (fly) this.flyFlyFrames = fly.frames;
      if (stop) this.flyStopFrames = stop.frames;
      if (this.sprite && this.flyStopFrames.length) {
        this.frames = this.flyStopFrames;
        this.sprite.texture = this.frames[0];
      }
    }

    if (entry?.firefly) {
      const f = entry.firefly;
      this.ffX = this.worldX;
      this.ffY = this.worldY;
      this.ffDir = Math.random() * 2 * Math.PI;
      const la = async (a: AnimEntry | null) => (a ? (await loadEntryAnim(this.game, a))?.frames ?? [] : []);
      this.ffAnims = { idle: await la(f.idle), fly: await la(f.fly), run: await la(f.run) };
      // Fireflies rotate their tiny sprite along the travel direction — pivot at centre.
      if (this.sprite) {
        this.sprite.anchor.set(0.5, 0.5);
        this.sprite.position.set(12, 12);
      }
    }

    if (entry?.humbird) {
      this.hbY = this.worldY;
      this.hbTimer = entry.humbird.interval;
      const fly = await loadAnim(this.game, this.objectId, 'fly');
      const stop = await loadAnim(this.game, this.objectId, 'stop');
      if (fly) this.hbFlyFrames = fly.frames;
      if (stop) this.hbStopFrames = stop.frames;
      if (this.sprite && this.hbStopFrames.length) {
        this.frames = this.hbStopFrames;
        this.sprite.texture = this.frames[0];
      }
    }

    if (entry?.spiderRun) {
      const sr = entry.spiderRun;
      this.srX = this.worldX;
      // BigSpider._Ready(): starts facing Juni.
      this.srDir = this.game.player.x < this.worldX + 12 ? -1 : 1;
      this.srTimer = sr.interval;
      const walk = sr.walk ? await loadEntryAnim(this.game, sr.walk) : null;
      const idle = sr.idle ? await loadEntryAnim(this.game, sr.idle) : null;
      if (walk) {
        this.srWalkFrames = walk.frames;
        this.srWalkFps = walk.fps;
      }
      if (idle) this.srIdleFrames = idle.frames;
      if (this.sprite && this.srIdleFrames.length) {
        this.frames = this.srIdleFrames;
        this.sprite.texture = this.frames[0];
        this.sprite.scale.x = this.srDir < 0 ? -1 : 1;
        this.sprite.x = this.srDir < 0 ? this.frames[0].width : 0;
      }
    }

    if (entry?.shadowWitch) {
      const angry = await loadAnim(this.game, this.objectId, 'angry');
      const idle = await loadAnim(this.game, this.objectId, 'default');
      if (angry) this.swAngryFrames = angry.frames;
      if (idle) this.swIdleFrames = idle.frames;
      if (this.sprite) this.sprite.alpha = 0; // invisible until angry (alpha = value/128)
    }

    if (entry?.proximity && this.sprite) this.sprite.alpha = entry.proximity.real ? 0 : 1;

    if (entry?.crumble) {
      // solid was stamped from entry.solid above; the crumble cycle clears it later.
      const c = entry.crumble;
      const activate = c.activate ? await loadEntryAnim(this.game, c.activate) : null;
      const destroy = c.destroy ? await loadEntryAnim(this.game, c.destroy) : null;
      const disappear = c.disappear ? await loadEntryAnim(this.game, c.disappear) : null;
      this.crActivateFrame = activate?.frames[0] ?? null;
      if (destroy) {
        this.crDestroyFrames = destroy.frames;
        this.crDestroyFps = destroy.fps;
      }
      if (disappear) {
        this.crDisappearFrames = disappear.frames;
        this.crDisappearFps = disappear.fps;
      }
      const idle = this.frames[0];
      if (idle) {
        this.frames = [idle];
        if (this.sprite) this.sprite.texture = idle;
      }
    }

    if (entry?.randomFrame && this.frames.length) {
      const pick = this.frames[Math.floor(Math.random() * this.frames.length)];
      this.frames = [pick];
      if (this.sprite) this.sprite.texture = pick;
    }

    const launchCount = ELEMENTAL_LAUNCH_FRAME_COUNT[this.objectId.key];
    if (launchCount != null && this.frames.length > launchCount) {
      this.elemental = true;
      this.elementalPrevJx = this.game.player.x;
      this.elementalPrevJy = this.game.player.y;
      this.elementalLaunchFrames = this.frames.slice(0, launchCount);
      this.frames = this.frames.slice(launchCount); // animate() now cycles just the idle frames
      if (this.sprite) this.sprite.texture = this.frames[0];
    }

    if (entry?.muff) {
      this.muff = entry.muff;
      this.mx = this.worldX;
      this.my = this.worldY;
      this.changeSpeed(this.muff.initialSpeed);
      this.dir = Math.random() < 0.5 ? -1 : 1;
      this.speedTimer = this.muff.speedChangeTime;
      this.dirTimer = this.muff.directionChangeTime;

      const wb = WALKING_BOUNCER_PARAMS[this.objectId.key];
      if (wb) {
        this.walkingBouncer = wb;
        const jump = await loadAnim(this.game, this.objectId, 'jump');
        const walk = await loadAnim(this.game, this.objectId, 'walk');
        const idle = await loadAnim(this.game, this.objectId, 'default');
        if (jump) {
          this.wbJumpFrames = jump.frames;
          this.wbJumpFps = jump.fps;
        }
        if (walk) {
          this.wbWalkFrames = walk.frames;
          this.wbWalkFps = walk.fps;
        }
        this.wbIdleFrame = idle?.frames[0] ?? null;
        if (wb.smart) this.wbUnsub = this.game.player.onJump((real) => this.onJuniJumpWB(real));
        else this.wbJumpTimer = WALKING_BOUNCER_JUMP_CHANCE_INTERVAL;
      }

      if (this.muff.roller) {
        this.rollTimer = entry?.selector
          ? (this.game.objectLayers?.selector.sharedDelay(this, this.muff.roller.interval) ?? this.muff.roller.interval)
          : this.muff.roller.interval;
        const roll = await loadAnim(this.game, this.objectId, 'roll');
        if (roll) {
          this.rollFrames = roll.frames;
          this.rollFps = roll.fps;
        }
      }
      if (this.muff.runThing) this.runThingTimer = this.muff.runThing.interval;
      if (this.muff.insect) this.insectTimer = this.muff.insect.interval;

      if (this.muff.walkingShooter) {
        const ws = this.muff.walkingShooter;
        const prepare = await loadAnim(this.game, this.objectId, 'prepare');
        const aftershot = await loadAnim(this.game, this.objectId, 'aftershot');
        if (prepare) {
          this.wsPrepareFrames = prepare.frames;
          this.wsPrepareFps = prepare.fps;
        }
        if (aftershot) {
          this.wsAftershotFrames = aftershot.frames;
          this.wsAftershotFps = aftershot.fps;
        }
        if (ws.startWithShoot) this.wsEnterPrepare();
        else this.wsTimer = ws.interval;
      }

      if (this.muff.wallNinja) {
        const wn = this.muff.wallNinja;
        const climb = await loadAnim(this.game, this.objectId, 'climb');
        const slide = await loadAnim(this.game, this.objectId, 'slide');
        const prepare = await loadAnim(this.game, this.objectId, 'prepare');
        const aftershot = await loadAnim(this.game, this.objectId, 'aftershot');
        if (climb) this.wnClimbFrames = climb.frames;
        if (slide) this.wnSlideFrames = slide.frames;
        if (prepare) {
          this.wnPrepareFrames = prepare.frames;
          this.wnPrepareFps = prepare.fps;
        }
        if (aftershot) {
          this.wnAftershotFrames = aftershot.frames;
          this.wnAftershotFps = aftershot.fps;
        }
        this.wnTimer = entry?.selector
          ? (this.game.objectLayers?.selector.sharedDelay(this, wn.interval) ?? wn.interval)
          : wn.interval;
      }
    }

    if (entry?.selector) this.game.objectLayers?.selector.register(this);

    if (entry?.emitter) {
      this.emitter = entry.emitter;
      this.game.bullets.preload(this.emitter.bullet);
      // Selector groups share one synchronized delay so their fire rounds line up
      // (TimerExt syncedRandomDelay); everything else staggers randomly.
      const period = this.emitter.dropVolley?.period ?? this.emitter.burst?.period ?? this.emitter.interval;
      this.fireTimer = entry.selector
        ? (this.game.objectLayers?.selector.sharedDelay(this, period) ?? Math.random() * period)
        : Math.random() * this.emitter.interval;
      if (this.emitter.launch) {
        const launch = await loadEntryAnim(this.game, this.emitter.launch);
        if (launch) {
          this.emLaunchFrames = launch.frames;
          this.emLaunchFps = launch.fps;
        }
      }
      if (this.emitter.gateAnim) {
        const ga = await loadEntryAnim(this.game, this.emitter.gateAnim);
        if (ga) {
          this.gaFrames = ga.frames;
          this.gaFps = ga.fps;
        }
      }
    }

    if (entry?.flySpike) {
      this.fsX = this.worldX;
      this.fsFacing = entry.flySpike.facingRight ? 1 : -1;
      if (this.sprite) {
        this.sprite.scale.x = this.fsFacing;
        this.sprite.x = this.fsFacing < 0 ? TILE_SIZE : 0;
      }
    }

    if (entry?.toggleShoot) {
      this.toggleShoot = entry.toggleShoot;
      this.tsToggleTimer = this.toggleShoot.toggleInterval;
      const open = await loadAnim(this.game, this.objectId, 'open');
      if (open) {
        this.tsOpenFrames = open.frames;
        this.tsOpenFps = open.fps;
      }
    }

    this.organic = !!entry?.organic;

    const crawler = CRAWLER_PARAMS[this.objectId.key];
    if (crawler) {
      this.crawler = crawler;
      this.crx = this.worldX;
      this.cry = this.worldY;
    }

    if (entry?.lookMuff) this.look = true;

    if (entry?.backgroundBird) {
      this.bird = true;
      this.birdX = this.worldX;
      this.birdDir = Math.random() < 0.5 ? -1 : 1;
      const fly0 = await loadAnim(this.game, this.objectId, 'fly0');
      const fly1 = await loadAnim(this.game, this.objectId, 'fly1');
      this.birdFly0 = fly0?.frames ?? [];
      this.birdFly1 = fly1?.frames ?? [];
      if (this.sprite) this.sprite.alpha = 0.627451;
      this.birdRandomize();
    }

    if (this.objectId.key === '12,14') this.attackBlock = true;

    if (this.objectId.key === '12,15') {
      this.flyingBomb = true;
      this.fbx = this.worldX;
      this.fby = this.worldY;
      this.fbXSpeed = Math.random() < 0.5 ? 100 : -100;
      const walk = await loadAnim(this.game, this.objectId, 'walk');
      const attack = await loadAnim(this.game, this.objectId, 'attack');
      if (walk) {
        this.fbWalkFrames = walk.frames;
        this.fbWalkFps = walk.fps;
      }
      if (attack) {
        this.fbAttackFrames = attack.frames;
        this.fbAttackFps = attack.fps;
      }
      if (this.sprite) {
        if (this.fbWalkFrames.length) this.sprite.texture = this.fbWalkFrames[0];
        this.sprite.scale.x = this.fbXSpeed < 0 ? -1 : 1;
      }
    }

    if (this.objectId.x === 6 && this.objectId.y >= 2 && this.objectId.y <= 4) {
      this.labyrinthSpike = { randomSpeed: this.objectId.y === 3 };
      this.lsx = this.worldX;
      this.lsy = this.worldY;
      this.lsSpeed = this.objectId.y === 4 ? 100 : 50;
    }
    if (this.objectId.key === '6,1') {
      this.trapCeiling = true;
      this.tcy = this.worldY;
    }

    const homing = HOMING_PARAMS[this.objectId.key];
    if (homing) {
      this.homing = homing;
      this.hex = this.worldX;
      this.hey = this.worldY;
      this.heSpeed = homing.initialSpeed;
    }

    const bouncer = BOUNCER_PARAMS[this.objectId.key];
    if (bouncer) {
      this.bouncer = bouncer;
      this.bounceCounter = bouncer.counter;
      this.by = this.worldY;
      this.bvy = -bouncer.initialSpeed * BOUNCER_SPEED_SCALE;
    }

    if (entry?.path && entry.path.points.length >= 2) {
      this.path = entry.path.points;
      this.pathSpeed = entry.path.speed;
      let total = 0;
      for (let i = 0; i < this.path.length - 1; i++) {
        const len = Math.hypot(this.path[i + 1][0] - this.path[i][0], this.path[i + 1][1] - this.path[i][1]);
        this.pathSegLen.push(len);
        total += len;
      }
      this.pathTotal = total;
      this.px = this.worldX + this.path[0][0];
      this.py = this.worldY + this.path[0][1];

      if (entry.path.creature) {
        const stop = await loadAnim(this.game, this.objectId, 'stop');
        if (stop) this.pcStopFrames = stop.frames;
        this.pcDefaultFrames = this.frames;
        this.pcDefaultFps = this.fps;
        // PathCreature._Ready(): with a RunTimer it starts stopped, waiting one
        // run interval before the first move; otherwise it moves immediately.
        if (entry.path.creature.runTime != null) this.pcSetStopped('run', entry.path.creature.runTime);
      }
    }

    if (entry?.spiker) {
      this.spiker = entry.spiker;
      const open = await loadFrames(this.game, entry.spiker.sheet, entry.spiker.open);
      if (open && open.length) {
        this.spikerOpen = open;
        if (entry.spiker.idle) {
          const idle = await loadFrames(this.game, entry.spiker.sheet, [entry.spiker.idle]);
          this.spikerIdle = idle?.[0] ?? null;
        }
        // Multi-tile sheets (BigSpiker) draw with an offset so the fan centres on the tile.
        if (this.sprite && entry.spiker.offset) {
          this.sprite.position.set(entry.spiker.offset[0], entry.spiker.offset[1]);
        }
        // Start retracted (safe). Replaces the generic looping sprite.
        if (this.sprite) this.sprite.texture = this.spikerIdle ?? this.spikerOpen[0];
      } else {
        this.spiker = null; // no open frames → fall back to plain deadly object
      }
    }

    if (entry?.selfDropper) {
      this.selfDropper = true;
      this.sdY = this.worldY;
      const launch = await loadAnim(this.game, this.objectId, 'launch');
      if (launch) {
        this.frames = launch.frames; // animate() plays this once the drop starts
        this.fps = launch.fps;
      }
      const idle = await loadAnim(this.game, this.objectId, 'default');
      if (this.sprite && idle?.frames.length) this.sprite.texture = idle.frames[0];
    }

    if (entry?.gestures) {
      const anims: Record<string, Texture[]> = {};
      for (const [name, e] of Object.entries(entry.gestures.anims)) {
        const a = await loadEntryAnim(this.game, e);
        if (a) {
          anims[name] = a.frames;
          this.gesturesFps[name] = a.fps;
        }
      }
      const names = Object.keys(anims);
      if (names.length > 1) {
        this.gestures = { ...entry.gestures, anims };
        this.nextGesture();
      }
    }
  }

  override makeSafe(): void {
    // GDKnyttBaseObject.makeSafe: safe=true (no kill), OrganicEnemy=false (no detector
    // glow), and a StaticBody's CollisionShape2D is disabled — a Hurts=false solid
    // delegate becomes passable. Called after load(), so unstamp what load() stamped.
    this.deadly = false;
    this.spiker = null;
    this.organic = false;
    if (this.entry?.solid && !this.neutralized)
      this.game.collision.clearRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
  }

  private changeSpeed(s: number): void {
    this.speed = s;
    this.decel = this.muff ? (s * this.muff.deceleration) / 20 : 0;
  }

  private chooseDir(mode: number): number {
    const cx = this.mx + 12;
    const px = this.game.player.apparentX; // hologram decoy fools targeting
    if (mode === 1) return px > cx ? 1 : -1; // towards Juni
    if (mode === 2) return px > cx ? -1 : 1; // away from Juni
    return Math.random() < 0.5 ? -1 : 1; // random
  }

  override update(dt: number): void {
    if (this.deleted) return;
    if (this.muff && !this.wbInAir) this.muffUpdate(dt);
    if (this.walkingBouncer) this.walkingBouncerPhysics(dt);
    if (this.path) this.pathUpdate(dt);
    if (this.bouncer) this.bouncerPhysics(dt);
    if (this.crawler) this.crawlerUpdate(dt);
    if (this.flyingBomb) this.flyingBombPhysics(dt);
    if (this.labyrinthSpike) this.labyrinthSpikeUpdate(dt);
    if (this.trapCeiling) this.trapCeilingUpdate(dt);
    if (this.homing) this.homingUpdate(dt);
    if (this.ghost) this.flickerUpdate(dt);
    if (this.look) this.lookUpdate();
    if (this.selfDropper) this.selfDropperUpdate(dt);
    if (this.entry?.buzz) this.buzzUpdate(dt);
    if (this.entry?.fly) this.flyUpdate(dt);
    if (this.entry?.firefly) this.fireflyUpdate(dt);
    if (this.entry?.humbird) this.humbirdUpdate(dt);
    if (this.entry?.spiderRun) this.spiderRunUpdate(dt);
    if (this.entry?.flySpike) this.flySpikeUpdate(dt);
    if (this.entry?.shadowWitch) this.shadowWitchUpdate(dt);
    if (this.entry?.proximity) this.proximityUpdate();
    if (this.entry?.falseBlock) this.falseBlockUpdate(dt);
    if (this.entry?.fadeBlock) this.fadeBlockUpdate(dt);
    if (this.entry?.crumble) this.crumbleUpdate(dt);
    if (this.spiker) this.spikerUpdate(dt);
    else if (this.bouncer) this.bouncerAnim(dt);
    else if (this.walkingBouncer) this.walkingBouncerAnim(dt);
    else if (this.attackBlock) this.attackBlockUpdate(dt);
    else if (this.flyingBomb) this.flyingBombAnim(dt);
    else if (this.homing) this.homingAnim(dt);
    else if (this.elemental) this.elementalUpdate(dt);
    else if (this.bird) this.birdUpdate(dt);
    else if (this.selfDropper) this.selfDropperAnim(dt);
    else if (this.gestures) this.gesturesUpdate(dt);
    else if (this.toggleShoot) { /* animated + fired in toggleShootUpdate below */ }
    else if (this.rolling) { /* animated in muffUpdate (rollFrames) */ }
    else if (this.muff?.walkingShooter && this.wsPhase !== 'walk') { /* animated in wsUpdate */ }
    else if (this.muff?.wallNinja && this.wnPhase !== 'walk') { /* animated in wnUpdate */ }
    else if (this.entry?.waterMonster) { /* animated + fired in wmUpdate below */ }
    else if (this.entry?.crumble && this.crState !== 'idle') { /* one-shot anims in crumbleUpdate */ }
    else if (this.entry?.flySpike && this.fsWait > 0) { /* frozen at frame 0 while waiting */ }
    else if (this.emitter?.gateAnim && this.gaFrames.length) { /* posed in emitterUpdate */ }
    else this.animate(dt);

    const x = this.curX();
    const y = this.curY();
    if (this.organic) this.game.player.reportOrganicEnemy(x + 12, y + 12);
    if (this.toggleShoot) this.toggleShootUpdate(dt, x, y);
    else if (this.entry?.waterMonster) this.wmUpdate(dt, x, y);
    else if (this.emitter) this.emitterUpdate(dt, x, y);
    if (!this.game.player.dead && this.isLethalNow()) {
      const [rx, ry, rw, rh] = this.deadlyRect(x, y);
      if (this.game.player.overlapsRect(rx, ry, rw, rh)) {
        if (this.eatFrames.length && this.sprite) {
          this.frames = this.eatFrames;
          this.fps = this.eatFps;
          this.animIndex = 0;
          this.animTime = 0;
          this.sprite.texture = this.frames[0];
          this.game.sfxAt('Chomp', this.curX() + 12, this.curY() + 12);
        }
        this.game.player.die();
      }
    }
  }

  override destroy(): void {
    this.wbUnsub?.();
    if (this.entry?.selector) this.game.objectLayers?.selector.unregister(this);
    super.destroy();
  }

  /** Selector fire gate: true when this object may fire this round (or isn't gated). */
  private selectorPick(): boolean {
    if (!this.entry?.selector) return true;
    return this.game.objectLayers?.selector.isSelected(this) ?? true;
  }

  /** Currently lethal? Spikers only kill while extended; others whenever deadly. */
  protected isLethalNow(): boolean {
    if (this.neutralized) return false; // Overlay layer: kill areas disabled
    if (this.spiker) {
      // SpikelessSpiker (14,20) animates like a spiker but instances the kill-less
      // DistanceMod — never deadly regardless of extension.
      if (!this.deadly) return false;
      // BigSpiker (delayDeath): lethal only once the open animation completes, so
      // Juni can dash through while the multi-tile spikes are still extending.
      if (this.spiker.delayDeath) {
        return this.spikerExtended && this.spikerIndex >= this.spikerOpen.length - 1;
      }
      return this.spikerExtended;
    }
    if (this.attackBlock) return this.abTriggered;
    // Elemental.cs kills ONCE, instantly at trigger time (in elementalUpdate) — there is
    // no Area2D and no contact kill during or after the launch animation.
    if (this.elemental) return false;
    if (this.entry?.shadowWitch) return this.swAngry;
    if (this.selfDropper) return this.sdDropping || this.sdDropped;
    return this.deadly;
  }

  /** The deadly hitbox in world coords (spikers use their tip area; others the tile). */
  protected deadlyRect(x: number, y: number): [number, number, number, number] {
    if (this.spiker?.area) {
      const [ax, ay, aw, ah] = this.spiker.area;
      return [x + ax, y + ay, aw, ah];
    }
    if (this.attackBlock) {
      // The spike grows upward from the base as the "open" animation advances.
      const t = this.abIndex / Math.max(1, this.frames.length - 1);
      const h = Math.max(2, Math.round(t * SpriteObject.ATTACK_BLOCK_HEIGHT));
      return [x + 2, y + SpriteObject.ATTACK_BLOCK_HEIGHT - h, TILE_SIZE - 4, h];
    }
    if (this.selfDropper) {
      // SelfDropper.cs: a 24×2 strip at tile-local (12,15) whose width scales per
      // launch frame ([0.4, 0.4, 0.5, 0.9, 1]) — narrow at first, full once dropped.
      const scale = this.sdDropped
        ? 1
        : SpriteObject.SD_SCALE[Math.min(this.animIndex, SpriteObject.SD_SCALE.length - 1)];
      const w = 24 * scale;
      return [x + 12 - w / 2, y + 14, w, 2];
    }
    const band = LIQUID_POOL_DEADLY_BAND[this.objectId.key];
    if (band) return [x, y + band[0], TILE_SIZE, band[1]];
    const custom = CUSTOM_DEADLY_BOX[this.objectId.key];
    if (custom) return [x + custom[0], y + custom[1], custom[2], custom[3]];
    return [x + 2, y + 2, TILE_SIZE - 4, TILE_SIZE - 4];
  }

  /**
   * Distance-toggled spikes (DistanceMod): extend when Juni comes within openDist,
   * retract when she leaves closeDist. Uses her apparent position so a hologram
   * decoy fools the spiker. Animates the open frames in/out.
   */
  private spikerUpdate(dt: number): void {
    const s = this.spiker!;
    const cx = this.curX() + 12;
    const cy = this.curY() + 12;
    const dx = this.game.player.apparentX - cx;
    const dy = this.game.player.apparentY - cy;
    const dist =
      s.method === 1 ? Math.abs(dx) + Math.abs(dy) : s.method === 2 ? Math.abs(dx) : Math.hypot(dx, dy);

    // SpikerMod.hologramStopped: Juni standing in a retracted spiker's area is only
    // safe because the decoy holds its distance check — the moment the hologram is
    // dismissed the spikes snap fully out (no delayDeath grace) and kill her.
    const p = this.game.player;
    if (!this.spikerExtended && !p.dead) {
      const [rx, ry, rw, rh] = this.deadlyRect(this.curX(), this.curY());
      const inside = p.overlapsRect(rx, ry, rw, rh);
      if (this.spikerHoloArmed && !p.hologramActive && inside) {
        this.spikerExtended = true;
        this.spikerIndex = this.spikerOpen.length - 1;
        if (!s.silent) this.game.sfxAt('Spike Up', cx, cy);
        if (this.deadly) p.die(); // SpikelessSpiker snaps out but has no kill area
      }
      this.spikerHoloArmed = inside && p.hologramActive;
    } else {
      this.spikerHoloArmed = false;
    }

    // Toggle on the hysteresis thresholds; play the extend/retract SFX on the edge
    // (yknytt SpikerMod.updateSpikes: OpenPlayer="Spike Up", ClosePlayer="Spike Down").
    if (!this.spikerExtended && dist < s.openDist) {
      this.spikerExtended = true;
      if (!s.silent) this.game.sfxAt('Spike Up', cx, cy);
    } else if (this.spikerExtended && dist > s.closeDist) {
      this.spikerExtended = false;
      if (!s.silent) this.game.sfxAt('Spike Down', cx, cy);
    }

    // Advance the open animation toward extended (forward) or retracted (backward).
    const last = this.spikerOpen.length - 1;
    this.spikerTimer += dt;
    const step = 1 / Math.max(1, s.fps);
    while (this.spikerTimer >= step) {
      this.spikerTimer -= step;
      if (this.spikerExtended && this.spikerIndex < last) this.spikerIndex++;
      else if (!this.spikerExtended && this.spikerIndex > 0) this.spikerIndex--;
      else break;
    }
    if (this.sprite) {
      this.sprite.texture =
        this.spikerIndex === 0 && this.spikerIdle ? this.spikerIdle : this.spikerOpen[this.spikerIndex];
    }
  }

  /**
   * TimedFlower.cs: toggles open/closed on `toggleInterval`, firing one bullet per
   * `shotInterval` while open (playing "open" forward on open, backward on close).
   * SmallTimedFlower.cs (`shots` set): the toggle instead opens for a fixed burst of
   * `shots` shots, then auto-closes without waiting for the next toggle.
   */
  private toggleShootUpdate(dt: number, x: number, y: number): void {
    const ts = this.toggleShoot!;
    this.tsToggleTimer -= dt;
    if (this.tsToggleTimer <= 0) {
      if (ts.shots != null) {
        // SmallTimedFlower: (re)open for a fixed-count burst; ShotTimer below closes it.
        this.tsToggleTimer = ts.toggleInterval;
        this.tsOpen = true;
        this.tsShotsLeft = ts.shots;
        this.tsShotTimer = 0;
      } else {
        // TimedFlower: flip open/closed indefinitely.
        this.tsToggleTimer = ts.toggleInterval;
        this.tsOpen = !this.tsOpen;
      }
      this.animIndex = 0;
      this.animTime = 0;
    }
    if (this.tsOpenFrames.length && this.sprite) {
      this.animTime += dt;
      const step = 1 / this.tsOpenFps;
      const last = this.tsOpenFrames.length - 1;
      while (this.animTime >= step) {
        this.animTime -= step;
        if (this.tsOpen && this.animIndex < last) this.animIndex++;
        else if (!this.tsOpen && this.animIndex > 0) this.animIndex--;
        else break;
      }
      this.sprite.texture = this.tsOpenFrames[this.animIndex];
    }
    if (!this.tsOpen || !this.emitter) return;
    this.tsShotTimer -= dt;
    if (this.tsShotTimer > 0) return;
    this.tsShotTimer += this.toggleShoot!.shotInterval;
    this.game.bullets.emit(this.emitter, x, y, 0, this.neutralized, this.container.zIndex - 1);
    if (this.emitter.sound) this.game.sfxAt(this.emitter.sound, x + 12, y + 12);
    if (ts.shots != null) {
      this.tsShotsLeft--;
      if (this.tsShotsLeft <= 0) this.tsOpen = false; // auto-close (plays "open" backwards)
    }
  }

  /**
   * Fire bullets. Cannon-class emitters fire a **burst** of `count` bullets at `rate`
   * spacing once per `period` (the burst counter raises MMF2 velocity via velPerI —
   * RobotCannon `10 + i`); others emit one bullet per `interval`. Ports Cannon.cs's
   * TotalTimer→BulletTimer model.
   */
  private emitterUpdate(dt: number, x: number, y: number): void {
    // WalkingShooter fires manually via wsFire() on its own prepare/aftershot cycle.
    if (this.muff?.walkingShooter) return;
    const e = this.emitter!;
    // RollerGenerator sound timers (see field comment).
    const roller = this.objectId.key === '13,6';
    if (roller) {
      if (!this.rgInit) {
        this.rgInit = true;
        this.rgSndB = 0.1; // FirstShotTimer
      }
      if (this.rgSndA > 0) {
        this.rgSndA -= dt;
        if (this.rgSndA <= 0) this.game.sfxAt('Roller Shot A', x + 12, y + 12);
      }
      if (this.rgSndB > 0) {
        this.rgSndB -= dt;
        if (this.rgSndB <= 0) this.game.sfxAt('Roller Shot B', x + 12, y + 12);
      }
    }
    // TrapFlower gate pose: walk the "open" frames in while Juni is within range,
    // back out when she leaves (DistanceMod's forward/backward "open" playback).
    if (e.gate && this.gaFrames.length && this.sprite) {
      const gdx = this.game.player.apparentX - (x + 12);
      const gdy = this.game.player.apparentY - (y + 12);
      const gdist =
        e.gate.method === 1 ? Math.abs(gdx) + Math.abs(gdy) : e.gate.method === 2 ? Math.abs(gdx) : Math.hypot(gdx, gdy);
      const open = gdist <= e.gate.dist;
      this.gaTimer += dt;
      const step = 1 / this.gaFps;
      while (this.gaTimer >= step) {
        this.gaTimer -= step;
        if (open && this.gaIdx < this.gaFrames.length - 1) this.gaIdx++;
        else if (!open && this.gaIdx > 0) this.gaIdx--;
      }
      this.sprite.texture = this.gaFrames[this.gaIdx];
    }
    // Bee.cs: each tick fires only with 1/chance probability — one bullet angled by
    // the walk direction (MMF2 19/27 + rand(3), spawn offset ±5).
    if (e.bee) {
      this.fireTimer -= dt;
      if (this.fireTimer <= 0) {
        this.fireTimer = e.interval;
        if (Math.floor(Math.random() * e.bee.chance) === 0) {
          const left = this.dir < 0;
          this.game.bullets.emit(
            { ...e, dir: (left ? 19 : 27) + Math.floor(Math.random() * 3), tx: 12 + (left ? -5 : 5), ty: 24 },
            x,
            y,
            0,
            this.neutralized,
            this.container.zIndex - 1,
          );
          if (e.sound) this.game.sfxAt(e.sound, x + 12, y + 12);
        }
      }
      return;
    }
    // Distance-gated emitters hold fire until Juni is within range (apparent position).
    if (e.gate) {
      const dx = this.game.player.apparentX - (x + 12);
      const dy = this.game.player.apparentY - (y + 12);
      const dist = e.gate.method === 1 ? Math.abs(dx) + Math.abs(dy) : e.gate.method === 2 ? Math.abs(dx) : Math.hypot(dx, dy);
      if (dist > e.gate.dist) return;
    }
    // DropFlything spray: every `period`, the whole `directions` volley fires once per
    // `rate` tick, `ticks` times in a row.
    if (e.dropVolley) {
      const v = e.dropVolley;
      if (this.dvTicksLeft > 0) {
        this.dvTimer -= dt;
        while (this.dvTicksLeft > 0 && this.dvTimer <= 0) {
          for (const dir of v.directions) this.game.bullets.emit({ ...e, dir }, x, y, 0, this.neutralized, this.container.zIndex - 1);
          this.dvTicksLeft--;
          this.dvTimer += v.rate;
        }
      }
      this.fireTimer -= dt;
      if (this.fireTimer <= 0) {
        this.fireTimer = v.period;
        if (this.selectorPick()) {
          this.dvTicksLeft = v.ticks;
          this.dvTimer = 0;
          if (e.sound) this.game.sfxAt(e.sound, x + 12, y + 12);
        }
      }
      return;
    }
    if (e.burst) {
      if (this.burstLeft > 0) {
        this.burstTimer -= dt;
        while (this.burstLeft > 0 && this.burstTimer <= 0) {
          this.game.bullets.emit(e, x, y, this.burstIndex, this.neutralized, this.container.zIndex - 1);
          this.burstIndex++;
          this.burstLeft--;
          this.burstTimer += e.burst.rate;
        }
      }
      this.fireTimer -= dt;
      if (this.fireTimer <= 0) {
        this.fireTimer = e.burst.period;
        if (this.selectorPick()) {
          this.burstLeft = e.burst.count;
          this.burstIndex = 0;
          this.burstTimer = 0;
          if (e.sound) this.game.sfxAt(e.sound, x + 12, y + 12); // one shot-sound per volley
          // BadFlything/HomingFlything hover after shooting (currentDirection = Zero).
          if (this.entry?.buzz) {
            this.bzDx = 0;
            this.bzDy = 0;
          }
          // SuperShot: play the "_launch" flourish with each volley.
          if (this.emLaunchFrames.length && this.sprite) {
            if (!this.emBaseFrames.length) this.emBaseFrames = this.frames;
            this.setFrames(this.emLaunchFrames, this.emLaunchFps);
            this.emLaunchTimer = this.emLaunchFrames.length / this.emLaunchFps;
          }
        }
      }
      if (this.emLaunchTimer > 0) {
        this.emLaunchTimer -= dt;
        if (this.emLaunchTimer <= 0 && this.emBaseFrames.length) this.setFrames(this.emBaseFrames);
      }
      return;
    }
    this.fireTimer -= dt;
    if (this.fireTimer <= 0) {
      this.fireTimer = e.interval;
      if (this.selectorPick()) {
        this.game.bullets.emit(e, x, y, 0, this.neutralized, this.container.zIndex - 1);
        if (roller) {
          // Sounds trail the emit on their own timers instead of playing now.
          this.rgSndA = 1.44;
          this.rgSndB = 2.3;
        } else if (e.sound) {
          this.game.sfxAt(e.sound, x + 12, y + 12);
        }
        // StuffShooter: play the one-shot "launch" flourish on each shot.
        if (this.emLaunchFrames.length && this.sprite) {
          if (!this.emBaseFrames.length) this.emBaseFrames = this.frames;
          this.setFrames(this.emLaunchFrames, this.emLaunchFps);
          this.emLaunchTimer = this.emLaunchFrames.length / this.emLaunchFps;
        }
      }
    }
    if (this.emLaunchTimer > 0) {
      this.emLaunchTimer -= dt;
      if (this.emLaunchTimer <= 0 && this.emBaseFrames.length) this.setFrames(this.emBaseFrames);
    }
  }

  private pathUpdate(dt: number): void {
    if (!this.path || this.pathTotal <= 0) return;
    const pc = this.entry?.path?.creature;
    if (pc) {
      this.pathCreatureUpdate(dt, pc);
      return;
    }
    this.pathOffset = (this.pathOffset + this.pathSpeed * dt) % this.pathTotal;
    this.pathApplyOffset();
  }

  /** Interpolate px/py along the waypoint polyline at the current pathOffset. */
  private pathApplyOffset(): void {
    let d = this.pathOffset;
    let i = 0;
    while (i < this.pathSegLen.length && d > this.pathSegLen[i]) {
      d -= this.pathSegLen[i];
      i++;
    }
    if (i >= this.pathSegLen.length) i = this.pathSegLen.length - 1;
    const a = this.path![i];
    const b = this.path![i + 1] ?? a;
    const t = this.pathSegLen[i] > 0 ? d / this.pathSegLen[i] : 0;
    this.px = this.worldX + a[0] + (b[0] - a[0]) * t;
    this.py = this.worldY + a[1] + (b[1] - a[1]) * t;
    this.container.x = Math.round(this.px);
    this.container.y = Math.round(this.py);
  }

  /**
   * PathCreature.cs (Fish/Birdie): ping-pong (or loop) along the path; at each end,
   * FlipTimer pauses then flips the sprite, RunTimer pauses (playing "stop") before
   * the run resumes. Hitting a wall reverses (the C# has the same stuck-in-wall TODO).
   */
  private pathCreatureUpdate(dt: number, pc: NonNullable<PathConfig['creature']>): void {
    if (this.pcStopped) {
      this.animate(dt);
      this.pcWait -= dt;
      if (this.pcWait > 0) return;
      if (this.pcStage === 'flip') {
        this.pcFlip = !this.pcFlip;
        this.pcApplyFlip();
        if (pc.runTime != null) {
          this.pcStage = 'run';
          this.pcWait = pc.runTime;
          return;
        }
      }
      this.pcResume();
      return;
    }
    const oldX = this.px;
    let off = this.pathOffset + this.pathSpeed * dt * (this.pcForward ? 1 : -1);
    let ended = false;
    if (this.pcForward && off >= this.pathTotal) {
      off = pc.loop ? 0 : this.pathTotal;
      ended = true;
    } else if (!this.pcForward && off <= 0) {
      off = 0;
      ended = true;
    }
    this.pathOffset = off;
    this.pathApplyOffset();
    if (this.game.collision.solidAt(this.px + 12, this.py + 12)) this.pcForward = !this.pcForward;
    if (Math.abs(this.px - oldX) > 0.01) {
      this.pcFlip = this.px < oldX;
      this.pcApplyFlip();
    }
    this.animate(dt);
    if (ended) {
      if (!pc.loop) this.pcForward = !this.pcForward;
      if (pc.flipTime != null) this.pcSetStopped('flip', pc.flipTime);
      else if (pc.runTime != null) this.pcSetStopped('run', pc.runTime);
    }
  }

  private pcSetStopped(stage: 'flip' | 'run', wait: number): void {
    this.pcStopped = true;
    this.pcStage = stage;
    this.pcWait = wait;
    if (this.pcStopFrames.length) {
      this.frames = this.pcStopFrames;
      this.animIndex = 0;
      this.animTime = 0;
      if (this.sprite) this.sprite.texture = this.frames[0];
    }
  }

  private pcResume(): void {
    this.pcStopped = false;
    this.pcStage = null;
    if (this.pcDefaultFrames.length) {
      this.frames = this.pcDefaultFrames;
      this.fps = this.pcDefaultFps;
      this.animIndex = 0;
      this.animTime = 0;
      if (this.sprite) this.sprite.texture = this.frames[0];
    }
  }

  private pcApplyFlip(): void {
    if (!this.sprite) return;
    this.sprite.scale.x = this.pcFlip ? -1 : 1;
    this.sprite.x = this.pcFlip ? TILE_SIZE : 0;
  }

  /** Free-fall under gravity; bounce back up off the ground (Bouncer.cs). */
  private bouncerPhysics(dt: number): void {
    const b = this.bouncer!;
    this.bvy += b.gravity * BOUNCER_ACCEL_SCALE * dt;
    const ny = this.by + this.bvy * dt;
    // Approximates Bouncer1.tscn's circular Area2D (radius 11 @ tile-local (12,13)).
    const grounded =
      this.bvy > 0 && this.game.collision.boxOverlaps(this.worldX + 1, ny + 2, 22, 22);
    if (grounded) {
      let speed: number;
      if (b.random) {
        speed = b.speedValues[Math.floor(Math.random() * b.speedValues.length)];
      } else {
        speed = b.speedValues[this.bounceCounter % b.speedValues.length];
        this.bounceCounter++;
      }
      this.bvy = -speed * BOUNCER_SPEED_SCALE;
      this.bounceAnimTimer = (this.frames.length - 1) / BOUNCER_BOUNCE_FPS;
      this.bounceIndex = 0;
      this.animTime = 0;
      this.game.sfxAt('Bounce', this.curX() + 12, this.curY() + 12);
    } else {
      this.by = ny;
    }
    this.container.y = Math.round(this.by);
  }

  /** frames[0..len-2] = the one-shot "bounce" burst; frames[len-1] = idle. */
  private bouncerAnim(dt: number): void {
    if (!this.sprite || this.frames.length === 0) return;
    const idle = this.frames[this.frames.length - 1] ?? this.frames[0];
    if (this.bounceAnimTimer > 0) {
      this.bounceAnimTimer -= dt;
      this.animTime += dt;
      const step = 1 / BOUNCER_BOUNCE_FPS;
      while (this.animTime >= step && this.bounceIndex < this.frames.length - 2) {
        this.animTime -= step;
        this.bounceIndex++;
      }
      this.sprite.texture = this.frames[this.bounceIndex] ?? idle;
    } else {
      this.sprite.texture = idle;
    }
  }

  /** AttackBlock.cs: stay retracted until Juni is close, then grow (play "open") once and stay deadly. */
  private attackBlockUpdate(dt: number): void {
    if (!this.abTriggered) {
      const dx = this.game.player.apparentX - (this.worldX + 12);
      if (Math.abs(dx) >= SpriteObject.ATTACK_BLOCK_OPEN_DIST) return;
      this.abTriggered = true;
      this.game.sfxAt('Ghost Rock', this.worldX + 12, this.worldY + 12);
    }
    if (!this.sprite || this.abIndex >= this.frames.length - 1) return;
    this.abTimer += dt;
    const step = 1 / SpriteObject.ATTACK_BLOCK_FPS;
    while (this.abTimer >= step && this.abIndex < this.frames.length - 1) {
      this.abTimer -= step;
      this.abIndex++;
    }
    this.sprite.texture = this.frames[this.abIndex];
  }

  /** Elemental.cs: idle-cycle harmlessly until Juni is close, then a one-shot deadly launch burst. */
  private elementalUpdate(dt: number): void {
    if (!this.elementalLaunched) {
      const jx = this.game.player.x;
      const jy = this.game.player.y;
      const dx = jx - (this.worldX + 12);
      const dy = jy - (this.worldY + 12);
      // Elemental.cs also gates the trigger on GDArea.isIn(Juni, 32, 32) — Juni must be
      // at least 32px inside the area bounds (no triggering from a neighbouring screen).
      const lx = jx - this.game.areaOriginX;
      const ly = jy - this.game.areaOriginY;
      const inArea =
        lx >= 32 && lx <= AREA_WIDTH * TILE_SIZE - 32 && ly >= 32 && ly <= AREA_HEIGHT * TILE_SIZE - 32;
      if (Math.abs(dx) + Math.abs(dy) >= ELEMENTAL_TRIGGER_DIST || !inArea) {
        this.elementalPrevJx = jx;
        this.elementalPrevJy = jy;
        this.animate(dt); // idle cycling (this.frames was trimmed to just the idle frames on load)
        return;
      }
      this.elementalLaunched = true;
      // Elemental.cs: "can teleport into elemental with no harm" — if Juni's position
      // jumped since last frame (a warp/Shift), the launch still plays but doesn't kill.
      this.elementalImmune =
        Math.hypot(jx - this.elementalPrevJx, jy - this.elementalPrevJy) >= 1000 * dt;
      this.animIndex = 0;
      this.animTime = 0;
      this.game.sfxAt('Elemental B', this.worldX + 12, this.worldY + 12);
      // The kill is immediate at trigger time (juniDie right in the trigger branch) —
      // NOT a contact check against the launch animation (there is no Area2D at all).
      if (!this.elementalImmune && !this.game.player.dead) this.game.player.die();
    }
    if (!this.sprite || this.animIndex >= this.elementalLaunchFrames.length - 1) return;
    this.animTime += dt;
    const step = 1 / this.fps;
    while (this.animTime >= step && this.animIndex < this.elementalLaunchFrames.length - 1) {
      this.animTime -= step;
      this.animIndex++;
    }
    this.sprite.texture = this.elementalLaunchFrames[this.animIndex];
  }

  /** HomingEnemy.cs: inertia-smoothed homing toward Juni's apparent position; flies through walls (no collision), only its look/speed reacts to overlapping one. */
  private homingUpdate(dt: number): void {
    const h = this.homing!;
    const cx = this.hex + 12;
    const cy = this.hey + 12;
    const dx = this.game.player.apparentX - cx;
    const dy = this.game.player.apparentY - cy;
    const dist = Math.hypot(dx, dy);
    if (dist > HOMING_MIN_DIST) {
      const inertia = Math.max(0, Math.min(1, 0.9 - dt * 3));
      const targetVx = (this.heSpeed * HOMING_SPEED_SCALE * dx) / dist;
      const targetVy = (this.heSpeed * HOMING_SPEED_SCALE * dy) / dist;
      this.heVx = this.heVx * inertia + targetVx * (1 - inertia);
      this.heVy = this.heVy * inertia + targetVy * (1 - inertia);
    }
    this.hex += this.heVx * dt;
    this.hey += this.heVy * dt;

    const inWall = this.game.collision.boxOverlaps(this.hex, this.hey, TILE_SIZE, TILE_SIZE);
    if (inWall !== this.heInWall) {
      this.heInWall = inWall;
      this.heSpeed = inWall ? h.wallSpeed : h.airSpeed;
      if (this.sprite) this.sprite.alpha = inWall ? 0.5 : 1;
      this.heAnimIndex = 0;
      this.heAnimTime = 0;
    }
    this.container.x = Math.round(this.hex);
    this.container.y = Math.round(this.hey);
  }

  private homingAnim(dt: number): void {
    if (!this.sprite || this.frames.length < HOMING_DEFAULT_FRAME_COUNT) return;
    const h = this.homing!;
    const start = this.heInWall ? HOMING_DEFAULT_FRAME_COUNT : 0;
    const count = this.heInWall
      ? this.frames.length - HOMING_DEFAULT_FRAME_COUNT
      : HOMING_DEFAULT_FRAME_COUNT;
    if (count <= 0) return;
    const fps = this.heInWall ? h.wallFps : h.defaultFps;
    this.heAnimTime += dt;
    const step = 1 / fps;
    while (this.heAnimTime >= step) {
      this.heAnimTime -= step;
      this.heAnimIndex = (this.heAnimIndex + 1) % count;
    }
    this.sprite.texture = this.frames[start + this.heAnimIndex];
  }

  /** LabyrinthSpike.cs: wander in the current cardinal direction; turn 90° at random (up to 5 tries) when blocked. */
  private labyrinthSpikeUpdate(dt: number): void {
    const p = this.labyrinthSpike!;
    let nx = this.lsx + this.lsDirX * this.lsSpeed * dt;
    let ny = this.lsy + this.lsDirY * this.lsSpeed * dt;
    let blocked = this.game.collision.boxOverlaps(nx, ny, TILE_SIZE, TILE_SIZE);
    if (blocked) {
      for (let retry = 0; retry < 5 && blocked; retry++) {
        if (this.lsDirY !== 0) {
          this.lsDirY = 0;
          this.lsDirX = Math.random() < 0.5 ? -1 : 1;
        } else {
          this.lsDirX = 0;
          this.lsDirY = Math.random() < 0.5 ? -1 : 1;
        }
        nx = this.lsx + this.lsDirX * this.lsSpeed * dt;
        ny = this.lsy + this.lsDirY * this.lsSpeed * dt;
        blocked = this.game.collision.boxOverlaps(nx, ny, TILE_SIZE, TILE_SIZE);
      }
      if (blocked) return; // boxed in — stay put this tick
      if (p.randomSpeed) this.lsSpeed = 50 * (1 + Math.floor(Math.random() * 2));
      this.game.sfxAt('Machine Turn A', this.curX() + 12, this.curY() + 12);
      return; // reorient only; the original doesn't move on the same tick it turns
    }
    this.lsx = nx;
    this.lsy = ny;
    this.container.x = Math.round(this.lsx);
    this.container.y = Math.round(this.lsy);
  }

  /** TrapCeiling.cs: oscillate between the floor and ceiling, reversing on contact. */
  private trapCeilingUpdate(dt: number): void {
    const speed = this.tcMovingUp ? -50 : 100;
    const ny = this.tcy + speed * dt;
    if (this.game.collision.boxOverlaps(this.worldX, ny, TILE_SIZE, TILE_SIZE)) {
      this.tcMovingUp = !this.tcMovingUp;
      this.game.sfxAt(this.tcMovingUp ? 'Machine Turn A' : 'Machine Turn B', this.worldX + 12, this.tcy + 12);
      return;
    }
    this.tcy = ny;
    this.container.y = Math.round(this.tcy);
  }

  /** FlyingBomb.cs: patrol between area edges at a fixed height; dive-bomb when Juni is within x-range, then arc back up. */
  private flyingBombPhysics(dt: number): void {
    if (this.fbAttackCooldown > 0) this.fbAttackCooldown -= dt;
    const apparentX = this.game.player.apparentX;
    const cx = this.fbx + 12;
    if (!this.fbInAttack && Math.abs(apparentX - cx) < 100 && this.fbAttackCooldown <= 0) {
      this.fbInAttack = true;
      this.fbYSpeed = 150 + Math.random() * 50;
      this.fbAnimIndex = 0;
      this.fbAnimTime = 0;
    }
    if (this.fbInAttack) this.fbYSpeed -= 250 * dt;

    this.fbx += this.fbXSpeed * dt;
    this.fby += this.fbYSpeed * dt;

    const areaLeft = this.area.position.x * TILE_SIZE * AREA_WIDTH;
    const pos = this.fbx + 12 - areaLeft;
    if (pos < 10) {
      this.fbXSpeed = 100;
      if (this.sprite) this.sprite.scale.x = 1;
    } else if (pos > TILE_SIZE * AREA_WIDTH - 10) {
      this.fbXSpeed = -100;
      if (this.sprite) this.sprite.scale.x = -1;
    }

    if (this.fby < this.worldY) {
      this.fbInAttack = false;
      this.fby = this.worldY;
      this.fbYSpeed = 0;
      this.fbAttackCooldown = 1; // Godot Timer default wait_time
      this.fbAnimIndex = 0;
      this.fbAnimTime = 0;
    }
    this.container.x = Math.round(this.fbx);
    this.container.y = Math.round(this.fby);
  }

  private flyingBombAnim(dt: number): void {
    if (!this.sprite) return;
    const frames = this.fbInAttack ? this.fbAttackFrames : this.fbWalkFrames;
    const fps = this.fbInAttack ? this.fbAttackFps : this.fbWalkFps;
    if (!frames.length) return;
    this.fbAnimTime += dt;
    const step = 1 / fps;
    while (this.fbAnimTime >= step) {
      this.fbAnimTime -= step;
      this.fbAnimIndex = (this.fbAnimIndex + 1) % frames.length;
    }
    this.sprite.texture = frames[this.fbAnimIndex] ?? frames[0];
  }

  /** Chase Juni's apparent position along one axis; stop within keepDistance / outside the radar window / at a wall or area edge. */
  private crawlerUpdate(dt: number): void {
    const c = this.crawler!;
    const objCx = this.crx + 12;
    const objCy = this.cry + 12;
    const apparentX = this.game.player.apparentX;
    const apparentY = this.game.player.apparentY;
    const juniRadar = c.horizontal ? apparentY : apparentX;
    const objRadar = c.horizontal ? objCy : objCx;
    const outOfRadar =
      (c.radarTop !== 0 && juniRadar < objRadar - c.radarTop) ||
      (c.radarBottom !== 0 && juniRadar > objRadar + c.radarBottom);
    const juniCoord = c.horizontal ? apparentX : apparentY;
    const objCoord = c.horizontal ? objCx : objCy;
    if (outOfRadar || Math.abs(juniCoord - objCoord) < c.keepDistance) return;

    const dir = juniCoord < objCoord ? -1 : 1;
    const diff = c.speed * dir * dt;
    const nx = c.horizontal ? this.crx + diff : this.crx;
    const ny = c.horizontal ? this.cry : this.cry + diff;
    const areaLeft = this.area.position.x * TILE_SIZE * AREA_WIDTH;
    const areaTop = this.area.position.y * TILE_SIZE * AREA_HEIGHT;
    const ncx = nx + 12;
    const ncy = ny + 12;
    const withinArea =
      ncx > areaLeft + 10 &&
      ncx < areaLeft + TILE_SIZE * AREA_WIDTH - 10 &&
      ncy > areaTop + 10 &&
      ncy < areaTop + TILE_SIZE * AREA_HEIGHT - 10;
    if (!withinArea || this.game.collision.boxOverlaps(nx, ny, TILE_SIZE, TILE_SIZE)) return;

    this.crx = nx;
    this.cry = ny;
    this.container.x = Math.round(this.crx);
    this.container.y = Math.round(this.cry);
    if (this.sprite && c.horizontal) this.sprite.scale.x = dir < 0 ? -1 : 1;
  }

  private onJuniJumpWB(real: boolean): void {
    if (this.wbInAir || !real) return;
    const dist = Math.abs(this.game.player.apparentX - (this.mx + 12));
    if (dist < WALKING_BOUNCER_JUMP_REACT_RANGE + Math.random() * 80) this.wbJump();
  }

  private wbJump(): void {
    const wb = this.walkingBouncer!;
    this.wbInAir = true;
    this.wbVel = -wb.initialJumpSpeed;
    this.speed = 0; // pause Muff horizontal movement while airborne
    this.wbAnimIndex = 0;
    this.wbAnimTime = 0;
    this.game.sfxAt('Bounce Lite', this.curX() + 12, this.curY() + 12);
  }

  /** Vertical hop on top of the (paused-while-airborne) Muff patrol. */
  private walkingBouncerPhysics(dt: number): void {
    const wb = this.walkingBouncer!;
    if (!wb.smart) {
      this.wbJumpTimer -= dt;
      while (this.wbJumpTimer <= 0) {
        this.wbJumpTimer += WALKING_BOUNCER_JUMP_CHANCE_INTERVAL;
        if (!this.wbInAir && Math.random() < 1 / 8) this.wbJump();
      }
    }
    if (!this.wbInAir) return;
    this.wbVel += wb.jumpGravity * WALKING_BOUNCER_JUMP_SCALE * dt;
    const ny = this.my + this.wbVel * WALKING_BOUNCER_JUMP_SCALE * dt;
    if (this.wbVel >= 0 && ny >= this.worldY) {
      this.wbInAir = false;
      this.my = this.worldY;
      this.game.sfxAt('Bounce', this.curX() + 12, this.curY() + 12);
    } else {
      this.my = ny;
    }
    this.container.y = Math.round(this.my);
  }

  private walkingBouncerAnim(dt: number): void {
    if (!this.sprite) return;
    if (this.wbInAir) {
      if (this.wbJumpFrames.length) {
        this.wbAnimTime += dt;
        const step = 1 / this.wbJumpFps;
        while (this.wbAnimTime >= step && this.wbAnimIndex < this.wbJumpFrames.length - 1) {
          this.wbAnimTime -= step;
          this.wbAnimIndex++;
        }
        this.sprite.texture = this.wbJumpFrames[this.wbAnimIndex];
      }
      return;
    }
    if (this.speed !== 0 && this.wbWalkFrames.length) {
      this.wbAnimTime += dt;
      const step = 1 / this.wbWalkFps;
      while (this.wbAnimTime >= step) {
        this.wbAnimTime -= step;
        this.wbAnimIndex = (this.wbAnimIndex + 1) % this.wbWalkFrames.length;
      }
      this.sprite.texture = this.wbWalkFrames[this.wbAnimIndex];
    } else if (this.wbIdleFrame) {
      this.sprite.texture = this.wbIdleFrame;
    }
  }

  private muffUpdate(dt: number): void {
    const m = this.muff!;

    // WalkingShooter: ticks its own phase state machine regardless of halt state
    // (it's what decides when to halt) — must run before `halted` is computed.
    if (m.walkingShooter) this.wsUpdate(dt);
    if (m.wallNinja) this.wnUpdate(dt);

    // DistanceMod-gated halts (DistanceMuff.cs): pause the speed/direction timers +
    // zero speed while Juni is within range. SpikerMuff already halts via its spiker
    // extension; a plain DistanceMuff (no visible spikes) uses `haltDist` directly.
    if (m.haltDist && !this.spiker) {
      const cx = this.mx + 12;
      const cy = this.my + 12;
      const dx = this.game.player.apparentX - cx;
      const dy = this.game.player.apparentY - cy;
      const dist =
        m.haltDist.method === 1
          ? Math.abs(dx) + Math.abs(dy)
          : m.haltDist.method === 2
            ? Math.abs(dx)
            : Math.hypot(dx, dy);
      this.haltedByDistance = dist < m.haltDist.dist;
    }
    const halted =
      this.haltedByDistance ||
      (this.spiker && this.spikerExtended) ||
      (m.walkingShooter && this.wsPhase !== 'walk') ||
      (m.wallNinja && this.wnPhase !== 'walk');

    // RollerMuff (bank3 3,2/3,8): periodic charge — snap speed to 24 towards Juni and
    // play "roll" until colliding; the normal speed/direction timers pause meanwhile.
    if (m.roller && !this.rolling && !halted) {
      this.rollTimer -= dt;
      if (this.rollTimer <= 0) {
        this.rollTimer = m.roller.interval;
        if (this.selectorPick()) {
          this.rolling = true;
          this.changeSpeed(24);
          this.dir = this.chooseDir(1); // TowardsJuni
          this.animIndex = 0;
          this.animTime = 0;
        }
      }
    }

    if (halted) {
      this.speed = 0;
    } else if (!this.rolling) {
      if (m.runThing) this.runThingUpdate(dt, m.runThing);
      // Insect.cs: AttackTimer re-rolls speed + re-aims (0 random / 1 towards / 2 away).
      if (m.insect) {
        this.insectTimer -= dt;
        if (this.insectTimer <= 0) {
          this.insectTimer = m.insect.interval;
          if (m.speedValues?.length) {
            this.changeSpeed(m.speedValues[Math.floor(Math.random() * m.speedValues.length)]);
          }
          this.dir = this.chooseDir(m.insect.runTo);
        }
      }
      // Jelly.cs: hops — each animation cycle boosts speed to jellySpeed (deceleration
      // then brings it back to rest), picking a fresh random direction while stopped.
      if (m.jelly) {
        this.jellyTimer -= dt;
        if (this.jellyTimer <= 0) {
          this.jellyTimer = Math.max(0.2, this.frames.length / this.fps);
          if (this.speed === 0) this.dir = Math.random() < 0.5 ? -1 : 1;
          this.changeSpeed(m.jelly.speed);
        }
      }
      if (m.speedChangeTime > 0 && !m.runThing) {
        this.speedTimer -= dt;
        if (this.speedTimer <= 0) {
          this.speedTimer = m.speedChangeTime;
          if (m.speedValues && m.speedValues.length) {
            this.changeSpeed(m.speedValues[Math.floor(Math.random() * m.speedValues.length)]);
          }
        }
      }
      if (m.directionChangeTime > 0 && !m.runThing) {
        this.dirTimer -= dt;
        if (this.dirTimer <= 0) {
          this.dirTimer = m.directionChangeTime;
          this.dir = this.chooseDir(m.directionChange);
        }
      }
    }

    const diff = this.speed * this.dir * MUFF_SPEED_SCALE * dt;
    if (diff !== 0) {
      const cx = this.mx + 12;
      const cy = this.my + 12;
      let blocked = false;
      if (!m.vertical) {
        const lx = cx - this.game.areaOriginX + diff;
        if (lx < m.xBorder || lx > 600 - m.xBorder) blocked = true;
        else if (this.game.collision.solidAt(cx + diff + this.dir * 12, cy)) blocked = true;
        if (!blocked) this.mx += diff;
      } else {
        const ly = cy - this.game.areaOriginY + diff;
        if (ly < m.xBorder || ly > 240 - m.xBorder) blocked = true;
        else if (this.game.collision.solidAt(cx, cy + diff + this.dir * 12)) blocked = true;
        if (!blocked) this.my += diff;
      }
      if (blocked) {
        this.dir = -this.dir;
        // RollerMuff.collide(): a charge ends on the first collision (base collide()
        // still flips direction).
        if (this.rolling) {
          this.rolling = false;
          this.changeSpeed(0);
          this.animIndex = 0;
          this.animTime = 0;
        }
      }
    }
    if (this.decel > 0) {
      const ns = this.speed - this.decel * dt;
      this.speed = ns <= 0 ? 0 : ns;
    }

    // GhostWalker (bank12): while phasing through a wall tile at the leading edge,
    // speed up and flicker faster (its own Area2D senses the overlap in yknytt).
    if (m.ghostWalker) {
      const cx = this.mx + 12;
      const cy = this.my + 12;
      const wasInWall = this.inWall;
      this.inWall = this.game.collision.solidAt(cx + this.dir * 12, cy);
      if (this.inWall !== wasInWall) {
        if (this.inWall) {
          this.flipTarget = this.modAlpha; // resume from current alpha at the faster rate
          this.flipTime = 0.05;
        } else {
          this.flipTime = 0.065;
        }
      }
      if (this.inWall && m.speedChangeTime === 0) this.speed = m.ghostWalker.wallSpeed;
    }

    this.container.x = Math.round(this.mx);
    this.container.y = Math.round(this.my);
    if (this.sprite && !m.vertical) {
      if (this.rolling && this.rollFrames.length) {
        this.animTime += dt;
        const step = 1 / this.rollFps;
        while (this.animTime >= step) {
          this.animTime -= step;
          this.animIndex = (this.animIndex + 1) % this.rollFrames.length;
        }
        this.sprite.texture = this.rollFrames[this.animIndex];
      }
      this.sprite.scale.x = this.dir < 0 ? -1 : 1;
      this.sprite.x = this.dir < 0 ? TILE_SIZE : 0;
    }
    // WallNinja.changeDirection(): climb (up) / slide (down) anim swap, only while
    // actually patrolling (prepare/aftershot drive their own frames in wnUpdate).
    if (this.sprite && m.vertical && m.wallNinja && this.wnPhase === 'walk') {
      const frames = this.dir < 0 ? this.wnClimbFrames : this.wnSlideFrames;
      if (frames.length && this.frames !== frames) {
        this.frames = frames;
        this.animIndex = 0;
        this.animTime = 0;
      }
    }
  }

  /**
   * RunThing.cs's fight-or-flight AI: chase Juni within `safeDistance` at `attackSpeed`
   * (low `attackDeceleration`), else flee at a random patrol speed (`normalDeceleration`).
   * Ties break toward Juni's facing direction, mirroring the C#'s FacingRight branches.
   */
  private runThingUpdate(
    dt: number,
    rt: NonNullable<MuffParams['runThing']>,
  ): void {
    this.runThingTimer -= dt;
    if (this.runThingTimer > 0) return;
    this.runThingTimer = rt.interval;
    const m = this.muff!;
    const juniDist = this.game.player.apparentX - (this.mx + 12);
    const facingRight = this.game.player.facingRight;
    let direction: number;
    let speed: number;
    const anySpeed = m.speedValues?.length
      ? m.speedValues[Math.floor(Math.random() * m.speedValues.length)]
      : 0;
    if (juniDist >= rt.safeDistance) direction = 1;
    else if (juniDist <= -rt.safeDistance) direction = -1;
    else if (facingRight && juniDist < 0) direction = 1;
    else if (!facingRight && juniDist < 0) direction = -1;
    else if (facingRight && juniDist > 0) direction = 1;
    else direction = -1;
    if (Math.abs(juniDist) >= rt.safeDistance) speed = anySpeed;
    else if (facingRight && juniDist < 0) speed = anySpeed;
    else if (!facingRight && juniDist < 0) speed = rt.attackSpeed;
    else if (facingRight && juniDist > 0) speed = rt.attackSpeed;
    else speed = anySpeed;
    this.decel = (speed === rt.attackSpeed ? rt.attackDeceleration : rt.normalDeceleration) / 20;
    this.dir = direction;
    this.speed = speed;
  }

  /** WalkingShooter.cs: re-pick facing towards Juni and enter the "prepare" windup. */
  private wsEnterPrepare(): void {
    const ws = this.muff!.walkingShooter!;
    this.dir = this.chooseDir(this.muff!.directionChange);
    this.wsPhase = 'prepare';
    this.animIndex = 0;
    this.animTime = 0;
    this.wsShotDelay = ws.shotDelay;
    if (this.sprite && this.wsPrepareFrames.length) this.sprite.texture = this.wsPrepareFrames[0];
  }

  /** Fire `bulletsCount` bullets from the object's own emitter, then advance to aftershot/walk. */
  private wsFire(): void {
    const ws = this.muff!.walkingShooter!;
    if (this.emitter) {
      if (this.emitter.sound) this.game.sfxAt(this.emitter.sound, this.curX() + 12, this.curY() + 12);
      // limitBullets (DarkRandomizer): each bullet is Selector-gated individually.
      for (let i = 0; i < ws.bulletsCount; i++) {
        if (this.selectorPick()) this.game.bullets.emit(this.emitter, this.mx, this.my, i, this.neutralized, this.container.zIndex - 1);
      }
    }
    if (this.wsAftershotFrames.length) {
      this.wsPhase = 'aftershot';
      this.animIndex = 0;
      this.animTime = 0;
      if (this.sprite) this.sprite.texture = this.wsAftershotFrames[0];
    } else {
      this.wsPhase = 'walk';
      this.wsTimer = ws.interval;
    }
  }

  /**
   * WalkingShooter.cs's ShotTimer toggle + prepare→shoot→aftershot sequence. `waitForTimer`
   * fires after a fixed `shotDelay`; otherwise it fires once the "prepare" animation completes
   * one cycle (matching Godot's `animation_finished`, which fires each loop for looping anims too).
   */
  private wsUpdate(dt: number): void {
    const ws = this.muff!.walkingShooter!;
    if (this.wsPhase === 'walk') {
      this.wsTimer -= dt;
      if (this.wsTimer <= 0) this.wsEnterPrepare();
      return;
    }
    const frames = this.wsPhase === 'prepare' ? this.wsPrepareFrames : this.wsAftershotFrames;
    const fps = this.wsPhase === 'prepare' ? this.wsPrepareFps : this.wsAftershotFps;
    if (!frames.length) {
      if (this.wsPhase === 'prepare') this.wsFire();
      else {
        this.wsPhase = 'walk';
        this.wsTimer = ws.interval;
      }
      return;
    }
    this.animTime += dt;
    const step = 1 / fps;
    while (this.animTime >= step && this.animIndex < frames.length - 1) {
      this.animTime -= step;
      this.animIndex++;
    }
    if (this.sprite) this.sprite.texture = frames[this.animIndex];
    if (this.wsPhase === 'prepare' && ws.waitForTimer) {
      this.wsShotDelay -= dt;
      if (this.wsShotDelay <= 0) this.wsFire();
    } else if (this.wsPhase === 'prepare') {
      if (this.animIndex >= frames.length - 1) this.wsFire();
    } else if (this.animIndex >= frames.length - 1) {
      this.wsPhase = 'walk';
      this.wsTimer = ws.interval;
    }
  }

  /** WallNinja.cs's `_on_ShotTimer_timeout_ext`: freeze + play "prepare" before firing. */
  private wnEnterPrepare(): void {
    this.wnPhase = 'prepare';
    this.animIndex = 0;
    this.animTime = 0;
    if (this.sprite && this.wnPrepareFrames.length) this.sprite.texture = this.wnPrepareFrames[0];
  }

  /** Fire the whole `shotDirections` volley at once (or one random pick), then aftershot/walk. */
  private wnFire(): void {
    const wn = this.muff!.wallNinja!;
    if (wn.sound) this.game.sfxAt(wn.sound, this.curX() + 12, this.curY() + 12); // the tscn's ShotPlayer stream
    const count = wn.randomDirection ? 1 : wn.shotDirections.length;
    for (let i = 0; i < count; i++) {
      const dir = wn.randomDirection
        ? wn.shotDirections[Math.floor(Math.random() * wn.shotDirections.length)]
        : wn.shotDirections[i];
      const cfg: EmitterConfig = {
        bullet: wn.bulletScene,
        tx: wn.tx,
        ty: wn.ty,
        dir,
        vel: wn.bulletVelocity,
        gravity: wn.bulletGravity,
        decel: 0,
        pinball: false,
        interval: wn.interval,
      };
      this.game.bullets.emit(cfg, this.mx, this.my, i, this.neutralized, this.container.zIndex - 1);
    }
    if (this.wnAftershotFrames.length) {
      this.wnPhase = 'aftershot';
      this.animIndex = 0;
      this.animTime = 0;
      if (this.sprite) this.sprite.texture = this.wnAftershotFrames[0];
    } else {
      this.wnResumeWalk();
    }
  }

  /** Resume patrol and re-pick direction, mirroring the C#'s post-shot `_on_DirectionTimer_timeout()`. */
  private wnResumeWalk(): void {
    const m = this.muff!;
    this.wnPhase = 'walk';
    this.wnTimer = m.wallNinja!.interval;
    if (m.directionChangeTime > 0) {
      this.dir = this.chooseDir(m.directionChange);
      this.dirTimer = m.directionChangeTime;
    }
  }

  private wnUpdate(dt: number): void {
    const wn = this.muff!.wallNinja!;
    if (this.wnPhase === 'walk') {
      this.wnTimer -= dt;
      if (this.wnTimer <= 0) {
        this.wnTimer = wn.interval;
        if (this.selectorPick()) this.wnEnterPrepare();
      }
      return;
    }
    const frames = this.wnPhase === 'prepare' ? this.wnPrepareFrames : this.wnAftershotFrames;
    const fps = this.wnPhase === 'prepare' ? this.wnPrepareFps : this.wnAftershotFps;
    if (!frames.length) {
      if (this.wnPhase === 'prepare') this.wnFire();
      else this.wnResumeWalk();
      return;
    }
    this.animTime += dt;
    const step = 1 / fps;
    while (this.animTime >= step && this.animIndex < frames.length - 1) {
      this.animTime -= step;
      this.animIndex++;
    }
    if (this.sprite) this.sprite.texture = frames[this.animIndex];
    if (this.animIndex >= frames.length - 1) {
      if (this.wnPhase === 'prepare') this.wnFire();
      else this.wnResumeWalk();
    }
  }

  /**
   * BaseWaterMonster.cs's `_on_ShotTimer_timeout_ext`: every `interval`, play "prepare",
   * fire the whole `bullets` volley through the object's own emitter, play "aftershot",
   * then idle on the default animation until the next cycle.
   */
  private wmUpdate(dt: number, x: number, y: number): void {
    const wm = this.entry!.waterMonster!;
    if (this.wmPhase === 'idle') {
      this.animate(dt);
      this.wmTimer -= dt;
      if (this.wmTimer <= 0 && this.wmPrepareFrames.length) {
        this.wmTimer = wm.interval;
        if (!this.selectorPick()) return;
        this.wmPhase = 'prepare';
        this.animIndex = 0;
        this.animTime = 0;
        if (this.sprite) this.sprite.texture = this.wmPrepareFrames[0];
      }
      return;
    }
    const frames = this.wmPhase === 'prepare' ? this.wmPrepareFrames : this.wmAftershotFrames;
    const fps = this.wmPhase === 'prepare' ? this.wmPrepareFps : this.wmAftershotFps;
    this.animTime += dt;
    const step = 1 / fps;
    while (this.animTime >= step && this.animIndex < frames.length - 1) {
      this.animTime -= step;
      this.animIndex++;
    }
    if (this.sprite && frames.length) this.sprite.texture = frames[this.animIndex];
    if (this.animIndex < frames.length - 1 && frames.length) return;
    if (this.wmPhase === 'prepare') {
      if (this.emitter) {
        if (this.emitter.sound) this.game.sfxAt(this.emitter.sound, x + 12, y + 12);
        for (let i = 0; i < wm.bullets; i++) this.game.bullets.emit(this.emitter, x, y, i, this.neutralized, this.container.zIndex - 1);
      }
      if (this.wmAftershotFrames.length) {
        this.wmPhase = 'aftershot';
        this.animIndex = 0;
        this.animTime = 0;
        if (this.sprite) this.sprite.texture = this.wmAftershotFrames[0];
      } else {
        this.wmEndCycle();
      }
    } else {
      this.wmEndCycle();
    }
  }

  private wmEndCycle(): void {
    this.wmPhase = 'idle';
    this.wmTimer = this.entry!.waterMonster!.interval;
    this.animIndex = 0;
    this.animTime = 0;
    if (this.sprite && this.frames.length) this.sprite.texture = this.frames[0];
  }

  /** Swap the active looping animation (no-op if already on these frames). */
  private setFrames(frames: Texture[], fps?: number): void {
    if (!frames.length || this.frames === frames) return;
    this.frames = frames;
    if (fps != null) this.fps = fps;
    this.animIndex = 0;
    this.animTime = 0;
    if (this.sprite) this.sprite.texture = frames[0];
  }

  /** BuzzFlyer.cs changeDirection(): a random pick of the 9 directions (incl. hovering). */
  private buzzChangeDirection(): void {
    this.bzDx = Math.floor(Math.random() * 3) - 1;
    this.bzDy = Math.floor(Math.random() * 3) - 1;
  }

  /** BuzzFlyer.cs: drift in the current direction + per-frame jitter; re-pick on wall/screen edge. */
  private buzzUpdate(dt: number): void {
    const b = this.entry!.buzz!;
    const mvx = (this.bzDx * b.speed + (Math.random() * 2 - 1) * b.strength) * dt;
    const mvy = (this.bzDy * b.speed + (Math.random() * 2 - 1) * b.strength) * dt;
    const cx = this.bzX + 12 + mvx;
    const cy = this.bzY + 12 + mvy;
    const lx = cx - this.area.position.x * TILE_SIZE * AREA_WIDTH;
    const ly = cy - this.area.position.y * TILE_SIZE * AREA_HEIGHT;
    const offscreen =
      lx < b.border || lx > TILE_SIZE * AREA_WIDTH - b.border || ly < b.border || ly > TILE_SIZE * AREA_HEIGHT - b.border;
    if (offscreen || this.game.collision.solidAt(cx, cy)) {
      this.buzzChangeDirection();
    } else {
      this.bzX += mvx;
      this.bzY += mvy;
    }
    this.container.x = Math.round(this.bzX);
    this.container.y = Math.round(this.bzY);
    if (this.sprite && this.bzDx !== 0) {
      this.sprite.scale.x = this.bzDx < 0 ? -1 : 1;
      this.sprite.x = this.bzDx < 0 ? TILE_SIZE : 0;
    }
  }

  /** Fly.cs: startled into jittery buzz-flight when Juni is near; lands when solid below. */
  private flyUpdate(dt: number): void {
    const trigger = this.objectId.y === 3 ? 50 : 80;
    const xBuzz = this.objectId.y === 3 ? 120 : 66;
    const dist =
      Math.abs(this.game.player.x - (this.flyX + 12)) + Math.abs(this.game.player.y - (this.flyY + 12));
    if (!this.flyAirborne && dist < trigger) {
      if (!this.flyStartled) {
        this.flyStartled = true;
        this.flyUpTime = 1; // FlyUpTimer: an initial upward bias, first startle only
      }
      this.flyAirborne = true;
      this.setFrames(this.flyFlyFrames);
    }
    if (!this.flyAirborne) return;
    this.flyUpTime -= dt;
    const mvx = (Math.random() * 2 - 1) * xBuzz * dt;
    const mvy = (Math.random() * (78 + 53) - 53 - (this.flyUpTime > 0 ? 50 : 0)) * dt;
    if (!this.game.collision.solidAt(this.flyX + 12 + mvx, this.flyY + 12 + mvy)) {
      this.flyX += mvx;
      this.flyY += mvy;
    }
    if (this.game.collision.solidAt(this.flyX + 12, this.flyY + 24 + 1)) {
      this.setFrames(this.flyStopFrames);
      this.flyAirborne = false;
    }
    this.container.x = Math.round(this.flyX);
    this.container.y = Math.round(this.flyY);
  }

  /** Firefly.cs: speed/direction/opacity all random-walk; bounces off solids, homes back on-screen. */
  private fireflyUpdate(dt: number): void {
    const f = this.entry!.firefly!;
    this.ffSpeed += ((Math.random() * 2 - 1) * f.speedBuzz + f.speedOffset) * dt;
    if (this.ffSpeed > f.limitSpeed) this.ffSpeed = f.resetSpeed;
    this.ffDir += (Math.random() * 2 - 1) * f.directionBuzz * dt;
    this.ffOpacity = Math.max(0, Math.min(1, this.ffOpacity + (Math.random() * 2 - 1) * f.opacityBuzz * dt));
    const step = this.ffSpeed * MUFF_SPEED_SCALE * dt;
    const nx = this.ffX + Math.cos(this.ffDir) * step;
    const ny = this.ffY + Math.sin(this.ffDir) * step;
    if (this.game.collision.solidAt(nx + 12, ny + 12)) {
      this.ffDir += Math.PI; // approximate the C#'s bounce off the surface normal
    } else {
      this.ffX = nx;
      this.ffY = ny;
    }
    const ax = this.area.position.x * TILE_SIZE * AREA_WIDTH;
    const ay = this.area.position.y * TILE_SIZE * AREA_HEIGHT;
    const cx = this.ffX + 12;
    const cy = this.ffY + 12;
    if (cx < ax || cx > ax + TILE_SIZE * AREA_WIDTH || cy < ay || cy > ay + TILE_SIZE * AREA_HEIGHT) {
      this.ffDir = Math.atan2(ay + 120 - cy, ax + 300 - cx); // steer back toward the area centre
    }
    if (this.sprite) {
      this.sprite.rotation = this.ffDir;
      this.sprite.alpha = this.ffOpacity;
    }
    const anims = this.ffAnims!;
    const want =
      this.ffSpeed <= f.idleSpeed ? anims.idle : this.ffSpeed > f.runSpeed ? anims.run : anims.fly;
    const usable = want.length ? want : anims.run.length ? anims.run : anims.idle;
    if (usable.length) this.setFrames(usable); // no-op when unchanged
    this.container.x = Math.round(this.ffX);
    this.container.y = Math.round(this.ffY);
  }

  /** Humbird.cs: faces Juni; every `interval`, arcs up 112px and back over 2.9s. */
  private humbirdUpdate(dt: number): void {
    if (this.sprite) {
      const cx = this.worldX + 12;
      const px = this.game.player.apparentX;
      if (px - 8 > cx) this.sprite.scale.x = 1;
      else if (px + 8 < cx) this.sprite.scale.x = -1;
      this.sprite.x = this.sprite.scale.x < 0 ? TILE_SIZE : 0;
    }
    if (!this.hbAirborne) {
      this.hbTimer -= dt;
      if (this.hbTimer > 0) return;
      this.hbAirborne = true;
      this.hbTime = 0;
      this.setFrames(this.hbFlyFrames);
    }
    this.hbTime += dt;
    const T = 2.9;
    const ny = this.worldY - (1 - Math.abs((1 - this.hbTime / T) ** 2)) * 112;
    if (ny < this.worldY) {
      this.hbY = ny;
    } else {
      this.hbY = this.worldY;
      this.hbAirborne = false;
      this.hbTimer = this.entry!.humbird!.interval;
      this.setFrames(this.hbStopFrames);
    }
    this.container.y = Math.round(this.hbY);
  }

  /** FlySpike.cs: dash horizontally until a wall, then flip and wait 1.6-3.2s. */
  private flySpikeUpdate(dt: number): void {
    if (this.fsWait > 0) {
      this.fsWait -= dt;
      if (this.fsWait <= 0) this.fsSpeed = (3 + Math.floor(Math.random() * 2)) * 50;
      return;
    }
    const nx = this.fsX + this.fsSpeed * this.fsFacing * dt;
    const cx = nx + 12;
    const lx = cx - this.area.position.x * TILE_SIZE * AREA_WIDTH;
    if (
      lx < 2 ||
      lx > TILE_SIZE * AREA_WIDTH - 2 ||
      this.game.collision.solidAt(cx + this.fsFacing * 12, this.worldY + 12)
    ) {
      this.fsFacing = -this.fsFacing;
      this.fsWait = 1.6 + Math.random() * 1.6;
      this.animIndex = 0;
      this.animTime = 0;
      if (this.sprite) {
        this.sprite.texture = this.frames[0] ?? this.sprite.texture;
        this.sprite.scale.x = this.fsFacing;
        this.sprite.x = this.fsFacing < 0 ? TILE_SIZE : 0;
      }
    } else {
      this.fsX = nx;
    }
    this.container.x = Math.round(this.fsX);
  }

  /** Spider.cs: dash horizontally until a wall/area edge, then flip and go idle. */
  private spiderRunUpdate(dt: number): void {
    const sr = this.entry!.spiderRun!;
    if (!this.srRunning) {
      if (sr.radar) {
        // BigSpider: run whenever Juni's y is within [centerY+1, centerY+8].
        const jy = this.game.player.apparentY;
        const oy = this.worldY + 12;
        if (jy >= oy + 1 && jy <= oy + 8) this.srStartRun();
      } else {
        this.srTimer -= dt;
        if (this.srTimer <= 0) {
          this.srTimer = sr.interval;
          if (Math.floor(Math.random() * sr.chance) === 0) this.srStartRun();
        }
      }
      return;
    }
    const diff = sr.speed * this.srDir * dt;
    const cx = this.srX + 12 + diff;
    const lx = cx - this.area.position.x * TILE_SIZE * AREA_WIDTH;
    const blocked =
      lx < 12 || lx > TILE_SIZE * AREA_WIDTH - 12 ||
      this.game.collision.solidAt(cx + this.srDir * 12, this.worldY + 12);
    if (blocked) {
      this.srRunning = false;
      this.srDir = -this.srDir;
      this.setFrames(this.srIdleFrames.length ? this.srIdleFrames : this.frames);
    } else {
      this.srX += diff;
    }
    this.container.x = Math.round(this.srX);
    if (this.sprite && this.frames[0]) {
      this.sprite.scale.x = this.srDir < 0 ? -1 : 1;
      this.sprite.x = this.srDir < 0 ? this.frames[0].width : 0;
    }
  }

  private srStartRun(): void {
    this.srRunning = true;
    this.setFrames(this.srWalkFrames, this.srWalkFps);
    this.game.sfxAt('Spider Run', this.srX + 12, this.worldY + 12); // Spider.cs tryRun RunPlayer
  }

  /** ShadowWitch.cs: anger builds while Juni faces away; deadly + visible only angry. */
  private shadowWitchUpdate(dt: number): void {
    const cx = this.worldX + 12;
    const juniAtRight = this.game.player.apparentX > cx;
    const facingRight = this.game.player.facingRight;
    const facingAway = (facingRight && juniAtRight) || (!facingRight && !juniAtRight);
    this.swValue = Math.max(0, Math.min(128, this.swValue + (facingAway ? -50 : 50) * dt));
    this.swAngry = this.swValue > 120;
    this.setFrames(this.swAngry ? this.swAngryFrames : this.swIdleFrames);
    if (this.sprite) {
      this.sprite.alpha = this.swValue / 128;
      this.sprite.scale.x = juniAtRight ? 1 : -1;
      this.sprite.x = juniAtRight ? 0 : TILE_SIZE;
    }
  }

  /** ProximityBlock.cs: alpha by Juni's distance — the real one fades in, the fake fades out. */
  private proximityUpdate(): void {
    if (!this.sprite) return;
    const dist = Math.hypot(
      this.game.player.x - (this.worldX + 12),
      this.game.player.y - (this.worldY + 12),
    );
    const prox = Math.max(0, 1 - dist / 132);
    this.sprite.alpha = this.entry!.proximity!.real ? prox : 1 - prox;
  }

  /** FalseBlock.cs: fades out while Juni overlaps (revealing the wall is fake). */
  private falseBlockUpdate(dt: number): void {
    if (!this.sprite) return;
    const over = this.game.player.overlapsRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
    const target = over ? 0 : 1;
    const step = 4 * dt;
    this.sprite.alpha =
      Math.abs(target - this.sprite.alpha) <= step ? target : this.sprite.alpha + Math.sign(target - this.sprite.alpha) * step;
  }

  /** FadeBlock.cs: decorative alpha shimmer (0.16 + 0.16·sin, randomly-paced phase). */
  private fadeBlockUpdate(dt: number): void {
    this.fadePos += dt * (Math.floor(Math.random() * 5) - 1) * ((50 * Math.PI) / 90);
    if (this.sprite) this.sprite.alpha = 0.16 + 0.16 * Math.sin(this.fadePos);
  }

  /** Crumble.cs: arms when Juni lands on it; collapses after `delay` — or as she steps off. */
  private crumbleUpdate(dt: number): void {
    const c = this.entry!.crumble!;
    const standing =
      !this.game.player.dead &&
      this.game.player.overlapsRect(this.worldX, this.worldY - 3, TILE_SIZE, 4);
    if (this.crState === 'idle') {
      if (!standing) return;
      this.crState = 'armed';
      this.crTimer = c.delay;
      if (this.sprite && this.crActivateFrame) this.sprite.texture = this.crActivateFrame;
      return;
    }
    if (this.crState === 'armed') {
      if (!standing) {
        // Stepping off collapses it immediately (Crumble.cs _PhysicsProcess).
        this.crCollapse(this.crDisappearFrames, this.crDisappearFps);
        return;
      }
      this.crTimer -= dt;
      if (this.crTimer <= 0) this.crCollapse(this.crDestroyFrames, this.crDestroyFps, this.crDisappearFrames);
      return;
    }
    // gone: play the queued one-shot frames, then hold/hide.
    if (!this.sprite || this.animIndex >= this.frames.length - 1) {
      if (this.crChain) {
        this.setFrames(this.crChain, this.crDisappearFps);
        this.crChain = null;
      } else if (this.sprite && this.animIndex >= this.frames.length - 1) {
        this.sprite.visible = false;
      }
      return;
    }
    this.animTime += dt;
    const step = 1 / this.fps;
    while (this.animTime >= step && this.animIndex < this.frames.length - 1) {
      this.animTime -= step;
      this.animIndex++;
    }
    this.sprite.texture = this.frames[this.animIndex];
  }

  private crChain: Texture[] | null = null;

  private crCollapse(frames: Texture[], fps: number, chain?: Texture[]): void {
    this.crState = 'gone';
    this.game.collision.clearRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
    this.crChain = chain?.length ? chain : null;
    if (frames.length) this.setFrames(frames, fps);
    else if (this.sprite) this.sprite.visible = false;
  }

  /** BackgroundBird.cs randomize(): re-roll speed + fly animation (direction is fixed at spawn). */
  private birdRandomize(): void {
    this.birdSpeed = (4 + Math.floor(Math.random() * 5)) * (50 / 8);
    const useFly1 = Math.random() < 0.5;
    this.frames = (useFly1 ? this.birdFly1 : this.birdFly0) || this.frames;
    this.fps = useFly1 ? 25 : 12;
    this.animIndex = 0;
    if (this.sprite && this.frames.length) {
      this.sprite.texture = this.frames[0];
      this.sprite.scale.x = this.birdDir < 0 ? -1 : 1;
      this.sprite.x = this.birdDir < 0 ? TILE_SIZE : 0;
    }
  }

  private birdUpdate(dt: number): void {
    const cx = this.birdX - this.game.areaOriginX;
    if (cx + 12 > SpriteObject.BIRD_SCREEN_WIDTH + 40) {
      this.birdX = this.game.areaOriginX - 36 - 12;
      this.birdRandomize();
    } else if (cx + 12 < -40) {
      this.birdX = this.game.areaOriginX + SpriteObject.BIRD_SCREEN_WIDTH - 24 - 12;
      this.birdRandomize();
    }
    this.birdX += this.birdSpeed * this.birdDir * dt;
    this.container.x = Math.round(this.birdX);
    this.animate(dt);
  }

  /** SelfDropper.cs: drop straight down once Juni is near-x, until it lands. */
  private selfDropperUpdate(dt: number): void {
    if (!this.sdDropping && !this.sdDropped) {
      const dx = Math.abs(this.game.player.apparentX - (this.worldX + 12));
      if (dx >= SpriteObject.SD_TRIGGER_DIST) return;
      this.sdDropping = true;
      this.animIndex = 0;
      this.animTime = 0;
    }
    if (!this.sdDropping) return;
    this.sdVy = Math.min(450, this.sdVy + 14 * 50 * dt);
    const ny = this.sdY + this.sdVy * dt;
    if (this.game.collision.boxOverlaps(this.worldX + 1, ny + 22, 22, 2)) {
      this.sdDropping = false;
      this.sdDropped = true;
      this.game.sfxAt('Self Dropper Land', this.worldX + 12, this.sdY + 12);
    } else {
      this.sdY = ny;
    }
    this.container.y = Math.round(this.sdY);
  }

  /** frames[0..len-2] = the one-shot "launch" drop animation; frame len-1 (or "default") is idle. */
  private selfDropperAnim(dt: number): void {
    if (!this.sprite || this.frames.length === 0) return;
    if (!this.sdDropping) return; // idle frame was already set on load; dropped stays on the last frame
    this.animTime += dt;
    const step = 1 / this.fps;
    while (this.animTime >= step && this.animIndex < this.frames.length - 1) {
      this.animTime -= step;
      this.animIndex++;
    }
    this.sprite.texture = this.frames[this.animIndex];
  }

  /** LookMuff.cs: flip to face Juni's apparent x, with an 8px dead zone. */
  private lookUpdate(): void {
    if (!this.sprite) return;
    const cx = this.worldX + 12;
    const px = this.game.player.apparentX;
    if (px - 8 > cx) this.sprite.scale.x = 1;
    else if (px + 8 < cx) this.sprite.scale.x = -1;
    this.sprite.x = this.sprite.scale.x < 0 ? TILE_SIZE : 0;
  }

  /** GesturesObject.cs nextAnimation(): play a random gesture from frame 0, then idle. */
  private nextGesture(): void {
    const g = this.gestures!;
    // Star.cs: only the Selector-picked star of the group twinkles each round, at a
    // fresh random offset within its tile.
    if (this.entry?.selector) {
      if (!this.selectorPick()) {
        this.gestureTimer = g.minTime + Math.random() * (g.maxTime - g.minTime);
        return;
      }
      // Star.cs sets sprite.Offset = (rand 24, rand 24) on a *centered* sprite; the
      // sprite carries spriteCenter → anchor(.5,.5), so position IS the art's centre.
      if (this.sprite) this.sprite.position.set(Math.random() * 24, Math.random() * 24);
    }
    const names = Object.keys(g.anims);
    const name = names[Math.floor(Math.random() * names.length)];
    this.frames = g.anims[name];
    this.fps = this.gesturesFps[name] ?? DEFAULT_FPS;
    this.animIndex = 0;
    this.animTime = 0;
    if (this.sprite && this.frames.length) this.sprite.texture = this.frames[0];
    if (g.bidirectional && this.sprite) {
      const flip = Math.random() < 0.5;
      this.sprite.scale.x = flip ? -1 : 1;
      this.sprite.x = flip ? TILE_SIZE : 0;
    }
    this.gestureTimer = g.minTime + Math.random() * (g.maxTime - g.minTime);
  }

  /** Tick the idle timer (re-rolling the gesture on expiry), then animate the current one. */
  private gesturesUpdate(dt: number): void {
    this.gestureTimer -= dt;
    if (this.gestureTimer <= 0) this.nextGesture();
    this.animate(dt);
  }

  private flickerUpdate(dt: number): void {
    this.flipCounter += dt;
    while (this.flipCounter > this.flipTime) {
      this.flipCounter -= this.flipTime;
      this.flipTarget = Math.random() < 0.5 ? 0.1 : 0.8;
    }
    this.modAlpha += (this.flipTarget - this.modAlpha) * 8 * dt;
    if (this.sprite) this.sprite.alpha = this.modAlpha;
  }

  protected animate(dt: number): void {
    if (this.halo && this.haloFrames.length > 1) {
      this.haloTime += dt;
      const haloStep = 1 / this.haloFps;
      while (this.haloTime >= haloStep) {
        this.haloTime -= haloStep;
        this.haloIndex = (this.haloIndex + 1) % this.haloFrames.length;
        this.halo.texture = this.haloFrames[this.haloIndex];
      }
    }
    if (!this.sprite || this.frames.length <= 1) return;
    this.animTime += dt;
    const step = 1 / (this.fps * this.speedScale);
    while (this.animTime >= step) {
      this.animTime -= step;
      if (this.pingpong) {
        // LiquidPool.cs: on animation_finished, replay backwards, then forwards, alternating.
        if (this.animIndex + this.animDir < 0 || this.animIndex + this.animDir >= this.frames.length)
          this.animDir = -this.animDir;
        this.animIndex += this.animDir;
      } else {
        this.animIndex = (this.animIndex + 1) % this.frames.length;
      }
      this.sprite.texture = this.frames[this.animIndex];
    }
  }
}

/** An invisible deadly zone (bank-16 DeathZones) — kills on contact, no sprite. */
class HazardZoneObject extends KnyttObject {
  override update(): void {
    if (this.neutralized) return;
    if (!this.game.player.dead && this.overlapsPlayer()) this.game.player.die();
  }
}

/**
 * SecretDetector (bank-0 27/28): an invisible marker that drives Juni's enemy-detector
 * glow like an organic enemy, but with a small discrete range (26px) and a green tint —
 * a hint that a secret is nearby. Only glows when Juni has the Detector power.
 * Ports SecretDetector.cs (organic_enemy_max_distance=26, discrete_detector=true).
 */
class SecretDetectorObject extends KnyttObject {
  // ObjectID.y==27 → Color(0.2,0.6,0.4); else Color(0.4,1,0.8).
  private readonly color = this.objectId.y === 27 ? 0x339966 : 0x66ffcc;
  override update(): void {
    this.game.player.reportOrganicEnemy(this.worldX + 12, this.worldY + 12, 26, true, this.color);
  }
}

/**
 * EnemyDetector (bank-0 26): an invisible `OrganicEnemy=true` marker that drives Juni's
 * detector glow with the default organic params (170px range, non-discrete, red) — an
 * "enemy nearby" hint with no actual enemy. Ports EnemyDetector.tscn.
 */
class EnemyDetectorObject extends KnyttObject {
  override update(): void {
    this.game.player.reportOrganicEnemy(this.worldX + 12, this.worldY + 12);
  }
}

/**
 * LocalAmbient (bank-0 37/38): makes its atmosphere slot positional — loudest at the
 * object, fading with distance to Juni. Ports LocalAmbient.cs:
 * `Volume = max(Volume, -pow(40, dist/240))` (dB). The slot is marked custom-volume at
 * area load, so with no LocalAmbient nearby the track stays muted.
 */
class LocalAmbientObject extends KnyttObject {
  private readonly slot = this.objectId.y === 37 ? 0 : 1;
  override update(): void {
    const cx = this.worldX + 12;
    const cy = this.worldY + 12;
    const dist = Math.hypot(this.game.player.x - cx, this.game.player.y - cy);
    this.game.audio.requestAmbianceVolume(this.slot, -Math.pow(40, dist / 240));
  }
}

/**
 * MuteAmbient (bank-0 39/40): silences its atmosphere slot for the area. Ports
 * MuteAmbient.cs — it only marks the slot custom-volume (done in the area scan); with
 * no per-frame volume request the track stays muted. No per-frame behaviour needed.
 */
class MuteAmbientObject extends KnyttObject {}

/**
 * Bank-7 leaf/snow/dust emitter (Leaf.cs + SceneCPUParticles). A `generator` id spawns
 * particles continuously (driven by ObjectLayers' rotating per-type timer — yknytt's
 * `pickOne` Selector, so a screenful of leaf tiles spawns one particle per tick, not one
 * each); a `single` id emits one particle on load. Particles drift via Particles.ts.
 */
class LeafObject extends KnyttObject {
  readonly cfg: ParticleConfig;
  constructor(
    game: Game,
    area: KnyttArea,
    layerIndex: number,
    coords: KnyttPoint,
    objectId: KnyttPoint,
    cfg: ParticleConfig,
  ) {
    super(game, area, layerIndex, coords, objectId);
    this.cfg = cfg;
  }
  get isGenerator(): boolean {
    return this.cfg.isGenerator;
  }
  override load(): Promise<void> {
    this.game.particles.preload(this.cfg);
    if (!this.cfg.isGenerator) this.spawnOne();
    return Promise.resolve();
  }
  /** Emit one particle at the tile centre (SceneCPUParticles node sits at (12,12)). */
  spawnOne(): void {
    this.game.particles.spawn(this.cfg, this.worldX + 12, this.worldY + 12);
  }
}

/**
 * Rain (bank-7 8) / single Raindrop (7,9). The generator (8) spawns drops over time
 * (driven by ObjectLayers' rain group — yknytt's probabilistic Selector); the single
 * drop (9) falls once on load. Drops fall from this tile to the area floor (generator)
 * or 240px (single), splashing on impact. Ports Rain.cs + RaindropObject.tscn.
 */
class RainObject extends KnyttObject {
  get isGenerator(): boolean {
    return this.objectId.y === 8;
  }
  private get maxDistance(): number {
    // Generator: fall to the bottom of the area; single Raindrop: fixed 240px.
    return this.isGenerator ? (AREA_HEIGHT - this.coords.y) * TILE_SIZE : 240;
  }
  override load(): Promise<void> {
    this.game.particles.preloadRain();
    if (!this.isGenerator) this.spawnDrop();
    return Promise.resolve();
  }
  /** Emit one raindrop at a random x within the tile, just below its top edge. */
  spawnDrop(): void {
    const x = this.worldX + Math.random() * TILE_SIZE;
    this.game.particles.spawnRain(x, this.worldY + 16, this.maxDistance);
  }
}

/**
 * SunRay (bank-7 5): a light-shaft sprite (sun_ray_hd.png, scaled 0.25, top-left
 * anchored) whose alpha gently drifts between 0 and 0.25 — MoveTowards a random target
 * at 0.08/s, picking a new target on arrival. Ports SunRay.cs.
 */
class SunRayObject extends KnyttObject {
  private static readonly MAX_A = 0.25;
  private static readonly SPEED = 0.08;
  private sprite: Sprite | null = null;
  private alpha = Math.random() * 0.25;
  private target = Math.random() * 0.25;
  override async load(): Promise<void> {
    const tex = await this.game.assets.getTexture('objects/bank7/img/sun_ray_hd.png');
    if (!tex) return;
    this.sprite = new Sprite(tex);
    this.sprite.anchor.set(0, 0); // centered = false
    this.sprite.scale.set(0.25);
    this.sprite.alpha = this.alpha;
    this.container.addChild(this.sprite);
  }
  override update(dt: number): void {
    const step = SunRayObject.SPEED * dt;
    if (Math.abs(this.target - this.alpha) <= step) {
      this.alpha = this.target;
      this.target = Math.random() * SunRayObject.MAX_A; // chooseTarget
    } else {
      this.alpha += Math.sign(this.target - this.alpha) * step;
    }
    if (this.sprite) this.sprite.alpha = this.alpha;
  }
}

/**
 * Bubble (bank1 1,18): every 4–10s (first up to 10s), releases one bubble that
 * drifts up at ~12px/s for 2s, fading out. Ports Bubble.cs + its CPUParticles2D.
 */
class BubbleObject extends KnyttObject {
  private tex: Texture | null = null;
  private timer = Math.random() * 10;
  private bubbles: { sprite: Sprite; age: number }[] = [];

  override async load(): Promise<void> {
    this.tex = await this.game.assets.getTexture('objects/bank1/img/bubble.png');
  }

  override update(dt: number): void {
    this.timer -= dt;
    if (this.timer <= 0 && this.tex) {
      this.timer = 4 + Math.random() * 6;
      const sprite = new Sprite(this.tex);
      sprite.anchor.set(0.5, 0.5);
      sprite.position.set(12, 12);
      this.container.addChild(sprite);
      this.bubbles.push({ sprite, age: 0 });
    }
    for (let i = this.bubbles.length - 1; i >= 0; i--) {
      const b = this.bubbles[i];
      b.age += dt;
      b.sprite.y -= (12 + Math.random() * 1.2) * dt;
      b.sprite.alpha = Math.max(0, 1 - b.age / 2);
      if (b.age >= 2) {
        b.sprite.destroy();
        this.bubbles.splice(i, 1);
      }
    }
  }
}

/**
 * WhiteExplosion (bank-8 17): on area load, bursts 96 ShockWave bullets radially
 * (32 directions × 3), each decelerating to a stop and vanishing. Ports WhiteExplosion.cs.
 */
class WhiteExplosionObject extends KnyttObject {
  private fired = false;
  override load(): Promise<void> {
    this.game.bullets.preload('ShockWave');
    return Promise.resolve();
  }
  override update(): void {
    if (this.fired) return;
    if (!this.game.bullets.isLoaded('ShockWave')) return; // wait for frames
    this.fired = true;
    for (let i = 0; i < 32 * 3; i++) {
      const angle = (1 - (i % 32) / 16) * Math.PI;
      const cfg: EmitterConfig = {
        bullet: 'ShockWave',
        tx: 12 + Math.cos(angle) * -17, // yknytt: Translate((-17,0).Rotated(Direction))
        ty: 12 + Math.sin(angle) * -17,
        dir: i % 32,
        vel: 50 + Math.random() * 40,
        gravity: 0,
        decel: 12,
        pinball: false,
        interval: 0,
        disappearStopped: true,
      };
      this.game.bullets.emit(cfg, this.worldX, this.worldY, 0, this.neutralized, this.container.zIndex - 1);
    }
  }
}

/**
 * NoWall (bank-12 17): converts its layer-3 wall tile to "ghost terrain" — passable +
 * hidden without the Eye power, solid + visible with it. Ports NoWall.cs (move layer 3 →
 * ghost layer 4). Without Eye we clear the tile's collision and hide its sprite.
 */
class NoWallObject extends KnyttObject {
  override load(): Promise<void> {
    if (!this.game.values.getPower(PowerNames.Eye)) {
      this.game.collision.clearRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
      this.game.currentView?.hideTile(3, this.coords.x, this.coords.y);
    }
    return Promise.resolve();
  }
}

/** An invisible solid block (bank-16 InvisibleBlock/Barrier) — stamps collision. */
class InvisibleSolidObject extends KnyttObject {
  override load(): Promise<void> {
    if (!this.neutralized)
      this.game.collision.stampRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
    return Promise.resolve();
  }
}

/** A power-up. Removes itself if already owned; grants on touch. */
/** Sub-rect of a loaded sheet texture, or null if it falls outside the sheet. */
function subTex(sheet: Texture, x: number, y: number, w: number, h: number): Texture | null {
  if (x + w > sheet.width + 0.01 || y + h > sheet.height + 0.01) return null;
  return new Texture({
    source: sheet.source,
    frame: new Rectangle(sheet.frame.x + x, sheet.frame.y + y, w, h),
  });
}

/**
 * Per-world custom pickup sheet frames from `Custom Objects/<image>` (CustomObject.cs
 * fillAnimation): 24x24 tiles, 1-based frame indices `from..to`, row-major.
 */
async function loadCustomPickupFrames(
  game: Game,
  image: string,
  from: number,
  to: number,
): Promise<Texture[] | null> {
  const path = `Custom Objects/${image}`;
  if (!game.world.worldFileExists(path)) return null;
  const sheet = await game.assets.getTexture(path);
  if (!sheet) return null;
  const cols = Math.max(1, Math.floor(sheet.width / TILE_SIZE));
  const frames: Texture[] = [];
  for (let i = from; i <= to; i++) {
    const t = subTex(
      sheet,
      ((i - 1) % cols) * TILE_SIZE,
      Math.floor((i - 1) / cols) * TILE_SIZE,
      TILE_SIZE,
      TILE_SIZE,
    );
    if (!t) break;
    frames.push(t);
  }
  return frames.length ? frames : null;
}

class PowerItemObject extends SpriteObject {
  static readonly OBJ2POWER: Record<number, number> = {
    3: 0,
    4: 1,
    5: 2,
    6: 3,
    7: 4,
    8: 5,
    9: 6,
    10: 7,
    21: 8,
    22: 9,
    23: 10,
    24: 11,
    35: 12,
  };
  private power = 0;

  override async load(): Promise<void> {
    this.power = PowerItemObject.OBJ2POWER[this.objectId.y] ?? 0;
    if (this.game.values.getPower(this.power as PowerNames)) {
      this.remove();
      return;
    }
    await super.load();
    // [World] Powers=<sheet> (InfoPanel.cs:58-84 + PowerItem.cs:21-22): a per-world
    // sheet in Custom Objects/ overrides the pickup art — 8-frame rows 0-7 for
    // powers 0-7, row 8 for Map, and single-frame keys on row 9.
    const sheetName = this.game.world.iniData.getSection('World')?.get('Powers');
    if (!sheetName || !this.game.world.worldFileExists(`Custom Objects/${sheetName}`)) return;
    const sheet = await this.game.assets.getTexture(`Custom Objects/${sheetName}`);
    if (!sheet) return;
    const p = this.power;
    const frames: Texture[] = [];
    if (p >= 8 && p <= 11) {
      const t = subTex(sheet, TILE_SIZE * (p - 8), TILE_SIZE * 9, TILE_SIZE, TILE_SIZE);
      if (t) frames.push(t);
    } else {
      const row = p === 12 ? 8 : p;
      for (let i = 0; i < 8; i++) {
        const t = subTex(sheet, TILE_SIZE * i, TILE_SIZE * row, TILE_SIZE, TILE_SIZE);
        if (!t) break;
        frames.push(t);
      }
    }
    if (frames.length && this.sprite) {
      this.frames = frames;
      this.fps = 12;
      this.animIndex = 0;
      this.sprite.texture = frames[0];
    }
  }

  override update(dt: number): void {
    if (this.deleted) return;
    super.update(dt);
    if (this.overlapsPlayer()) {
      this.game.setPower(this.power as PowerNames, true);
      // PowerItem.cs:29 GDArea.playEffect(Coords): the pickup sparkle at the tile.
      this.game.objectLayers?.playEffect(this.worldX + TILE_SIZE / 2, this.worldY + TILE_SIZE / 2);
      this.game.audio.sfx('Powerup');
      this.remove();
    }
  }
}

class CollectableObject extends SpriteObject {
  private collectedAt = -1; // EaterCollectMod: linger through the "eat" anim before removal

  override async load(): Promise<void> {
    if (this.game.values.getCollectable(this.objectId.y)) {
      this.remove();
      return;
    }
    await super.load();
    // Per-world custom art: [World] Coin=<image> (Coin.cs:9-19, frames 1-8) and
    // [World] Artifact<N>=<image> (Artifact.cs, frames 1-14), sliced from
    // Custom Objects/ at 24x24, anim_speed 250 → 12.5 fps.
    const world = this.game.world.iniData.getSection('World');
    const y = this.objectId.y;
    let image: string | null = null;
    let to = 8;
    if (objectName(19, y) === 'Coin') {
      image = world?.get('Coin') ?? null;
    } else if (y >= 151) {
      image = world?.get(`Artifact${Math.floor((y - 151) / 7) + 1}`) ?? null;
      to = 14;
    }
    if (!image) return;
    const frames = await loadCustomPickupFrames(this.game, image, 1, to);
    if (frames && this.sprite) {
      this.frames = frames;
      this.fps = 12.5;
      this.animIndex = 0;
      this.sprite.texture = frames[0];
    }
  }
  override update(dt: number): void {
    if (this.deleted) return;
    super.update(dt);
    if (this.collectedAt >= 0) {
      this.collectedAt -= dt;
      if (this.collectedAt <= 0) this.remove();
      return;
    }
    if (this.overlapsPlayer()) {
      this.game.values.setCollectable(this.objectId.y, true);
      // CollectMod.cs:35 playEffect at the object — only the touched one sparkles —
      // then :38-41 QueueFrees EVERY same-id object in the area (duplicates vanish too).
      this.game.objectLayers?.playEffect(this.worldX + TILE_SIZE / 2, this.worldY + TILE_SIZE / 2);
      for (const o of this.game.objectLayers?.findObjects(this.objectId.x, this.objectId.y) ?? []) {
        if (o !== this) o.remove();
      }
      this.game.audio.sfx('Powerup');
      // EaterCollectMod (Collectable31): play the "eat" flourish before vanishing.
      if (this.eatFrames.length && this.sprite) {
        this.frames = this.eatFrames;
        this.fps = this.eatFps;
        this.animIndex = 0;
        this.animTime = 0;
        this.sprite.texture = this.frames[0];
        this.collectedAt = this.eatFrames.length / this.eatFps;
        return;
      }
      this.remove();
    }
  }
}

/**
 * Save point: pulsing additive disk (Idle), and when Juni holds Down on it,
 * saves once and plays the bright "Save" flash animation + sound (debounced so
 * it can't re-trigger every frame — which was the high-pitched buzz).
 */
class SavePointObject extends KnyttObject {
  private sprite: Sprite | null = null;
  private idle: Texture[] = [];
  private saveFrames: Texture[] = [];
  private idleFps = 12;
  private saveFps = 12;
  private frames: Texture[] = [];
  private fps = 12;
  private animTime = 0;
  private animIndex = 0;
  private saving = false;

  override async load(): Promise<void> {
    const idle = await loadAnim(this.game, this.objectId, 'Idle');
    const save = await loadAnim(this.game, this.objectId, 'Save');
    if (idle) {
      this.idle = idle.frames;
      this.idleFps = idle.fps;
    }
    if (save) {
      this.saveFrames = save.frames;
      this.saveFps = save.fps;
    }
    this.frames = this.idle;
    this.fps = this.idleFps;
    if (this.frames.length) {
      this.sprite = new Sprite(this.frames[0]);
      this.sprite.anchor.set(0, 0);
      this.sprite.blendMode = 'add'; // additive glow (no black box)
      this.container.addChild(this.sprite);
    }
  }

  override update(dt: number): void {
    // yknytt: saves while Down is HELD (so running/walking with Down works too),
    // debounced by the Save flash so it can't re-trigger every frame.
    if (!this.saving && this.overlapsPlayer() && this.game.input.isHeld('down')) {
      this.saving = true;
      // SavePoint.cs:27: saves the SAVEPOINT's tile (Coords), not Juni's.
      this.game.saveGame(true, this.area.position, this.coords);
      this.game.audio.sfx('Save Spot');
      this.frames = this.saveFrames.length ? this.saveFrames : this.idle;
      this.fps = this.saveFps;
      this.animIndex = 0;
      this.animTime = 0;
    }
    if (!this.sprite || this.frames.length === 0) return;
    this.animTime += dt;
    const step = 1 / this.fps;
    while (this.animTime >= step) {
      this.animTime -= step;
      this.animIndex++;
      if (this.animIndex >= this.frames.length) {
        if (this.saving) {
          this.saving = false; // flash done → back to idle
          this.frames = this.idle;
          this.fps = this.idleFps;
        }
        this.animIndex = 0;
      }
      this.sprite.texture = this.frames[this.animIndex] ?? this.frames[0];
    }
  }
}

/**
 * Toggleable laser hazard (bank13 13,7-12), ported from Laser.cs. 6 variants
 * keyed by objectId.y-7: indices 0-2 are horizontal beams (a 90°-rotated
 * strip), 3-5 vertical; index 2 and 5 are always-on; 1 and 4 start on but
 * still toggle. Non-always-on lasers toggle every 1.6s (first flip at 0.8s)
 * and are deadly ONLY while on, and only over the thin beam strip, not the
 * whole tile.
 */
class LaserObject extends KnyttObject {
  private sprite: Sprite | null = null;
  private offFrame: Texture | null = null;
  private onFrames: Texture[] = [];
  private onFps = 20;
  private animTime = 0;
  private animIndex = 0;

  private horizontal = false;
  private alwaysOn = false;
  private on = false;
  private timer = 0.8; // firstDelay

  override async load(): Promise<void> {
    const index = this.objectId.y - 7;
    this.horizontal = index < 3;
    this.alwaysOn = index === 2 || index === 5;
    const onAtStart = index === 1 || index === 4;
    this.on = this.alwaysOn || onAtStart;

    const off = await loadAnim(this.game, this.objectId, 'off');
    const on = await loadAnim(this.game, this.objectId, 'on');
    this.offFrame = off?.frames[0] ?? null;
    if (on) {
      this.onFrames = on.frames;
      this.onFps = on.fps;
    }
    this.sprite = new Sprite((this.on ? this.onFrames[0] : this.offFrame) ?? Texture.WHITE);
    this.sprite.anchor.set(0, 0);
    this.sprite.position.set(0, -1); // Laser.tscn: sprite offset (0,-1)
    if (this.horizontal) {
      this.sprite.anchor.set(0.5, 0.5);
      this.sprite.position.set(TILE_SIZE / 2, TILE_SIZE / 2);
      this.sprite.rotation = Math.PI / 2;
    }
    this.container.addChild(this.sprite);
    this.container.visible = this.on;
    // Laser.cs registers by_type — every toggling laser in the area shares one group,
    // so a bank of lasers plays a single switch sound per toggle, not a chorus.
    if (!this.alwaysOn) this.game.objectLayers?.selector.register(this, '__laser');
  }

  override update(dt: number): void {
    if (!this.alwaysOn) {
      this.timer -= dt;
      if (this.timer <= 0) {
        this.timer += 1.6;
        this.on = !this.on;
        this.container.visible = this.on;
        if (this.game.objectLayers?.selector.isSelected(this, '__laser') ?? true) {
          this.game.sfxAt('Laser Machine B', this.worldX + 12, this.worldY + 12);
        }
      }
    }
    if (this.on && this.onFrames.length && this.sprite) {
      this.animTime += dt;
      const step = 1 / this.onFps;
      while (this.animTime >= step) {
        this.animTime -= step;
        this.animIndex = (this.animIndex + 1) % this.onFrames.length;
      }
      this.sprite.texture = this.onFrames[this.animIndex];
    }
    if (this.on && !this.game.player.dead && !this.neutralized) {
      // Thin beam strip (~3.8px), not the full tile (Laser.tscn's CollisionShape2D).
      const [rx, ry, rw, rh] = this.horizontal
        ? [this.worldX, this.worldY + 10.1, TILE_SIZE, 3.8]
        : [this.worldX + 10.1, this.worldY, 3.8, TILE_SIZE];
      if (this.game.player.overlapsRect(rx, ry, rw, rh)) this.game.player.die();
    }
  }

  override destroy(): void {
    if (!this.alwaysOn) this.game.objectLayers?.selector.unregister(this, '__laser');
    super.destroy();
  }
}

/**
 * LaserRobot (13,3): a 1x4-tile-tall turret that patrols horizontally between
 * the area edges (or a wall), moving left at 100px/s while "off" (harmless)
 * and right at 50px/s while "on" (a thin vertical beam through its middle is
 * deadly). Ported from LaserRobot.cs (wall-collision checkers simplified to a
 * single collision-map probe at the leading edge).
 */
class LaserRobotObject extends KnyttObject {
  private sprite: Sprite | null = null;
  private offFrame: Texture | null = null;
  private onFrames: Texture[] = [];
  private onFps = 25;
  private animTime = 0;
  private animIndex = 0;
  private x = 0;
  private on = false;
  private speed = -100;

  override async load(): Promise<void> {
    this.x = this.worldX;
    const off = await loadAnim(this.game, this.objectId, 'off');
    const on = await loadAnim(this.game, this.objectId, 'on');
    this.offFrame = off?.frames[0] ?? null;
    if (on) {
      this.onFrames = on.frames;
      this.onFps = on.fps;
    }
    this.sprite = new Sprite(this.offFrame ?? Texture.WHITE);
    this.sprite.anchor.set(0, 0);
    this.sprite.position.set(-6, 0);
    this.container.addChild(this.sprite);
  }

  private setOn(on: boolean): void {
    if (this.on === on) return;
    this.on = on;
    this.speed = on ? 50 : -100;
    this.animIndex = 0;
    this.animTime = 0;
    if (this.sprite) this.sprite.texture = (on ? this.onFrames[0] : this.offFrame) ?? Texture.WHITE;
    this.game.sfxAt(on ? 'Laser Machine A' : 'Laser Machine B', this.x + 12, this.worldY + 12);
  }

  override update(dt: number): void {
    const areaLeft = this.area.position.x * TILE_SIZE * 25;
    const areaRight = areaLeft + TILE_SIZE * 25;
    const nx = this.x + this.speed * dt;
    if (nx < areaLeft) this.setOn(true);
    else if (nx + TILE_SIZE > areaRight) this.setOn(false);
    else if (this.game.collision.boxOverlaps(nx, this.worldY + 4, TILE_SIZE, 88)) {
      this.setOn(this.speed < 0);
    } else {
      this.x = nx;
      this.container.x = Math.round(this.x);
    }

    if (this.on && this.onFrames.length && this.sprite) {
      this.animTime += dt;
      const step = 1 / this.onFps;
      while (this.animTime >= step) {
        this.animTime -= step;
        this.animIndex = (this.animIndex + 1) % this.onFrames.length;
      }
      this.sprite.texture = this.onFrames[this.animIndex];
    }
    if (this.on && !this.game.player.dead && !this.neutralized) {
      // LaserRobot.tscn's LaserArea/CollisionShape2D: extents(1,42) at local (5,48) -> x 4..6, y 6..90.
      if (this.game.player.overlapsRect(this.x + 4, this.worldY + 6, 2, 84)) this.game.player.die();
    }
  }
}

/**
 * OneWayPlatform (bank16 16,29): solid from above (a thin strip at the tile's
 * top, like a normal platform), but passable when Juni jumps up into it from
 * below. Ported from OneWayPlatform.cs (the Area2D-disable trick approximated
 * here as: solid, unless Juni's box overlaps the tile while moving upward).
 * No sprite of its own — the "plank" graphic is a regular tile-layer tile;
 * this object only supplies the one-way collision behaviour.
 */
class OneWayPlatformObject extends KnyttObject {
  protected twoWay = false;
  private solid = true;
  private onTop = false; // down-button hint counted on the player (TwoWayPlatform.cs)
  private static readonly STRIP_H = 2;

  override load(): Promise<void> {
    if (!this.neutralized)
      this.game.collision.stampRect(this.worldX, this.worldY, TILE_SIZE, OneWayPlatformObject.STRIP_H);
    return Promise.resolve();
  }

  private setSolid(solid: boolean): void {
    if (this.solid === solid) return;
    this.solid = solid;
    if (solid) this.game.collision.stampRect(this.worldX, this.worldY, TILE_SIZE, OneWayPlatformObject.STRIP_H);
    else this.game.collision.clearRect(this.worldX, this.worldY, TILE_SIZE, OneWayPlatformObject.STRIP_H);
  }

  override update(): void {
    if (this.neutralized) return;
    const p = this.game.player;
    let wantSolid = true;
    if (p.overlapsRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE) && p.vy < 0) {
      wantSolid = false; // jumping up through from below
    }
    if (this.twoWay && this.solid) {
      // TwoWayPlatform.cs: Down+Jump while standing on top drops straight through.
      const standingOnTop = p.vy >= 0 && p.overlapsRect(this.worldX, this.worldY - 3, TILE_SIZE, 4);
      // Standing on top shows the green down-button hint (Juni.OnPlatform).
      if (standingOnTop !== this.onTop) {
        this.onTop = standingOnTop;
        p.onPlatform += standingOnTop ? 1 : -1;
      }
      if (
        standingOnTop &&
        this.game.input.isHeld('down') &&
        this.game.input.justPressed('jump')
      ) {
        wantSolid = false;
        p.vy = 0;
      }
    } else if (this.onTop) {
      this.onTop = false;
      p.onPlatform--;
    }
    this.setSolid(wantSolid);
  }

  override destroy(): void {
    if (this.onTop) this.game.player.onPlatform--;
    super.destroy();
  }
}

/** TwoWayPlatform (bank16 16,30): OneWayPlatform + Down+Jump drop-through. */
class TwoWayPlatformObject extends OneWayPlatformObject {
  override load(): Promise<void> {
    this.twoWay = true;
    return super.load();
  }
}

interface BouncerEnemyParams {
  gravity: number;
  jumpForce: number;
  extraGravity: number;
  extraJumpForce: number;
  /** Deadly hitbox in local tile coords [x,y,w,h] — the real CircleShape2D bounding box, not the full tile. */
  hitbox: [number, number, number, number];
}
/**
 * BouncerEnemy.tscn/SmartBouncer.tscn exported values (real px/s, px/s²).
 * `hitbox` is each variant's real Area2D CircleShape2D bounding box — much
 * smaller than the full tile (SmartBouncer: center (11,14) r=10; BouncerEnemy:
 * center (11.1,15.4) r=7.2) — the generic full-tile default made both die on
 * contact well before Juni's sprite visually touched them.
 */
const BOUNCER_ENEMY_PARAMS: Record<string, BouncerEnemyParams> = {
  '10,4': { gravity: 700, jumpForce: 160, extraGravity: 650, extraJumpForce: 235, hitbox: [1, 4, 20, 20] },
  '10,5': {
    gravity: 550,
    jumpForce: 100,
    extraGravity: 550,
    extraJumpForce: 100,
    hitbox: [3.94, 8.18, 14.4, 14.4],
  },
};
const JUMP_REACT_RANGE = 150; // + random 0..80, matching `150 + random.Next(80)`

/**
 * BouncerEnemy / SmartBouncer (bank10 10,5 / 10,4): a stationary launcher that
 * leaps straight up when Juni jumps within range (her apparent position, so a
 * hologram decoy fools it), falls back under gravity, and stops on landing.
 * DoubleJump gives Juni's jumps extra range via the enemy's own extra_gravity
 * / extra_jump_force. Ported from BouncerEnemy.cs.
 */
class BouncerEnemyObject extends SpriteObject {
  private bp: BouncerEnemyParams | null = null;
  private inAir = false;
  private vel = 0;
  private y = 0;
  private jumpFrames: Texture[] = [];
  private stopFrame: Texture | null = null;
  private jumpFps = 25;
  private jAnimTime = 0;
  private jAnimIndex = 0;
  private unsubscribe: (() => void) | null = null;

  override async load(): Promise<void> {
    await super.load();
    this.bp = BOUNCER_ENEMY_PARAMS[this.objectId.key] ?? null;
    this.y = this.worldY;
    const jump = await loadAnim(this.game, this.objectId, 'jump');
    const stop = await loadAnim(this.game, this.objectId, 'stop');
    if (jump) {
      this.jumpFrames = jump.frames;
      this.jumpFps = jump.fps;
    }
    this.stopFrame = stop?.frames[0] ?? this.frames[this.frames.length - 1] ?? null;
    this.unsubscribe = this.game.player.onJump((real) => this.onJuniJump(real));
  }

  protected override curY(): number {
    return this.y;
  }

  protected override deadlyRect(x: number, y: number): [number, number, number, number] {
    const [hx, hy, hw, hh] = this.bp?.hitbox ?? [2, 2, TILE_SIZE - 4, TILE_SIZE - 4];
    return [x + hx, y + hy, hw, hh];
  }

  private onJuniJump(real: boolean): void {
    if (this.inAir || !real || !this.bp) return;
    const dist = Math.abs(this.game.player.apparentX - (this.worldX + 12));
    if (dist < JUMP_REACT_RANGE + Math.random() * 80) this.launch();
  }

  private launch(): void {
    if (!this.bp) return;
    const doubleJump = this.game.values.getPower(PowerNames.DoubleJump);
    this.vel = -(doubleJump ? this.bp.extraJumpForce : this.bp.jumpForce);
    this.inAir = true;
    this.jAnimIndex = 0;
    this.jAnimTime = 0;
    this.game.sfxAt('Bounce Lite', this.worldX + 12, this.y + 12);
  }

  override update(dt: number): void {
    super.update(dt); // deadly/organic checks + default (single-frame "stop") animate
    if (this.inAir && this.bp) {
      const doubleJump = this.game.values.getPower(PowerNames.DoubleJump);
      this.vel += (doubleJump ? this.bp.extraGravity : this.bp.gravity) * dt;
      const ny = this.y + this.vel * dt;
      if (this.vel >= 0 && ny >= this.worldY) {
        this.inAir = false;
        this.y = this.worldY;
        if (this.sprite && this.stopFrame) this.sprite.texture = this.stopFrame;
        this.game.sfxAt('Bounce', this.worldX + 12, this.y + 12);
      } else {
        this.y = ny;
        if (this.jumpFrames.length && this.sprite) {
          this.jAnimTime += dt;
          const step = 1 / this.jumpFps;
          while (this.jAnimTime >= step && this.jAnimIndex < this.jumpFrames.length - 1) {
            this.jAnimTime -= step;
            this.jAnimIndex++;
          }
          this.sprite.texture = this.jumpFrames[this.jAnimIndex];
        }
      }
      this.container.y = Math.round(this.y);
    }
  }

  override destroy(): void {
    this.unsubscribe?.();
    super.destroy();
  }
}

class WinObject extends SpriteObject {
  override update(dt: number): void {
    super.update(dt);
    if (!this.game.player.dead && this.overlapsPlayer()) {
      const ending = this.area.getExtraData('Ending') ?? 'Ending';
      this.game.win(ending);
    }
  }
}

class SpringObject extends SpriteObject {
  override update(dt: number): void {
    super.update(dt);
    const p = this.game.player;
    // Spring.cs: only fires while Juni overlaps AND her origin sits above the spring's
    // top + 10, and never from Jump/Slide/Climb; she is snapped to the spring top and
    // launched via executeJump (Jumped signal → SmartBouncers; clears justClimbed etc.).
    if (!this.overlapsPlayer() || p.dead) return;
    if (p.y > this.worldY + 10) return;
    const s = p.state.name;
    if (s === 'Jump' || s === 'Slide' || s === 'Climb') return;
    const highJumpHold = p.power(PowerNames.HighJump) && this.game.input.isHeld('jump');
    p.springBounce(
      this.worldY,
      p.swim ? C.SWIM_SPRING_SPEED : highJumpHold ? C.SPRING_SPEED_HIGHJUMP_HOLD : C.SPRING_SPEED,
    );
    this.game.sfxAt('Bounce Platform', this.worldX + 12, this.worldY + 12);
  }
}

/**
 * A solid object whose sprite sheet is frame 0 = closed/idle, frames 1..n = a
 * one-shot "open" animation (doors, lock blocks). While closed it stays static on
 * frame 0 — the base SpriteObject would otherwise loop ALL frames and flicker
 * through the half-open frames. `open()` disables collision, plays the open
 * animation once, then removes itself. Ported from Door.cs / LockBlock.cs.
 */
abstract class OpeningObject extends SpriteObject {
  protected opening = false;
  private openTimer = 0;
  private openIndex = 0;
  protected static readonly OPEN_FPS = 20;

  /** Load as a closed, solid block sitting on frame 0. */
  protected async loadClosed(): Promise<void> {
    await super.load();
    if (this.sprite && this.frames.length) this.sprite.texture = this.frames[0];
    this.game.collision.stampRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
  }

  /** Begin opening: clear collision, play the open animation once, then remove. */
  open(silent = false): void {
    if (this.opening || this.deleted) return;
    this.opening = true;
    this.openIndex = 0;
    this.openTimer = 0;
    this.game.collision.clearRect(this.worldX, this.worldY, TILE_SIZE, TILE_SIZE);
    if (!silent) this.game.sfxAt('Electronic', this.worldX + 12, this.worldY + 12);
  }

  get isOpening(): boolean {
    return this.opening;
  }

  /** Advance the open animation. Returns true once the object has removed itself. */
  protected advanceOpen(dt: number): boolean {
    this.openTimer += dt;
    const step = 1 / OpeningObject.OPEN_FPS;
    while (this.openTimer >= step) {
      this.openTimer -= step;
      this.openIndex++;
      if (this.openIndex >= this.frames.length) {
        this.remove();
        return true;
      }
      if (this.sprite) this.sprite.texture = this.frames[this.openIndex];
    }
    return false;
  }
}

/**
 * A locked door (Door.cs): opens when Juni is near (72×72 area) AND owns its key.
 * A door created already-unlocked never appears (removed on load).
 */
abstract class DoorObject extends OpeningObject {
  /** Faithful to yknytt's 72×72 OpenArea (extents 36, centred on the tile). */
  private static readonly OPEN_PAD = 24;

  protected abstract isUnlocked(): boolean;

  override async load(): Promise<void> {
    if (this.isUnlocked()) {
      this.remove();
      return;
    }
    await this.loadClosed();
  }

  // Static while closed; only the open animation animates. Deliberately does NOT
  // call super.update() (no muff/path/ghost/emitter/frame-loop for a door).
  override update(dt: number): void {
    if (this.deleted) return;
    if (this.opening) {
      this.advanceOpen(dt);
      return;
    }
    if (this.isUnlocked()) {
      const p = DoorObject.OPEN_PAD;
      if (this.game.player.overlapsRect(this.worldX - p, this.worldY - p, TILE_SIZE + 2 * p, TILE_SIZE + 2 * p)) {
        this.open();
      }
    }
  }
}

/** A key door (RedDoor/YellowDoor/BlueDoor/PurpleDoor, 27-30): opens with its key power. */
class KeyDoorObject extends DoorObject {
  private get keyPower(): PowerNames {
    return (PowerNames.RedKey + (this.objectId.y - 27)) as PowerNames;
  }
  protected isUnlocked(): boolean {
    return this.game.values.getPower(this.keyPower);
  }
}

/** A door that opens once enough coins/creatures/artifacts are collected. */
class CollectionDoorObject extends DoorObject {
  private static THRESHOLDS: Record<number, number> = {
    31: 1,
    32: 5,
    33: 10,
    34: 20,
    35: 40,
    36: 50,
  };
  protected isUnlocked(): boolean {
    const need = CollectionDoorObject.THRESHOLDS[this.objectId.y] ?? 0;
    return this.game.values.getCoinCount() >= need;
  }
}

/**
 * A lock block (LockBlockHorizontal/Vertical, 23-24): a solid block opened when the
 * area's password puzzle is solved (Password/ClearBlock/HoldButton → openLocks()).
 * Ported from LockBlock.cs.
 */
class LockBlockObject extends OpeningObject {
  override async load(): Promise<void> {
    await this.loadClosed();
  }
  override update(dt: number): void {
    if (this.deleted) return;
    if (this.opening) this.advanceOpen(dt);
  }
}

/**
 * Password symbol (15,20/21): touch to cycle its symbol (13→21→13). When every base
 * Password in the area shows its correct symbol (current === objectId.y), all lock
 * blocks open. Ported from Password.cs.
 */
class PasswordObject extends KnyttObject {
  protected currentChar = 13;
  protected sprite: Sprite | null = null;
  private symbols = new Map<number, Texture>();
  private pressFrames: Texture[] = [];
  private over = false;
  private pressing = false;
  private pressTimer = 0;
  private pressIdx = 0;
  private static readonly PRESS_FPS = 20;

  /** First symbol shown (ClearBlock overrides to 22). */
  protected get startChar(): number {
    return 13;
  }
  /** Where the symbol/press animations live (ClearBlock borrows the 15,20 sheet). */
  protected get gfxId(): KnyttPoint {
    return this.objectId;
  }

  override async load(): Promise<void> {
    this.currentChar = this.startChar;
    for (let c = 13; c <= 22; c++) {
      const a = await loadAnim(this.game, this.gfxId, String(c));
      if (a?.frames.length) this.symbols.set(c, a.frames[0]);
    }
    const press = await loadAnim(this.game, this.gfxId, 'press');
    if (press) this.pressFrames = press.frames;
    this.sprite = new Sprite(this.symbols.get(this.currentChar) ?? Texture.WHITE);
    this.sprite.anchor.set(0, 0);
    this.container.addChild(this.sprite);
  }

  /** Public for ObjectLayers.allPasswordsCorrect(). */
  isCorrect(): boolean {
    return this.currentChar === this.objectId.y;
  }
  /** Base Password is solved only when every base Password is correct. */
  protected solved(): boolean {
    return this.game.objectLayers?.allPasswordsCorrect() ?? false;
  }

  private touch(): void {
    this.currentChar = this.currentChar === 21 ? 13 : this.currentChar < 21 ? this.currentChar + 1 : this.currentChar;
    if (this.solved()) this.game.objectLayers?.openLocks();
    this.pressing = this.pressFrames.length > 0;
    this.pressTimer = 0;
    this.pressIdx = 0;
    if (this.sprite) {
      this.sprite.texture = this.pressing
        ? this.pressFrames[0]
        : (this.symbols.get(this.currentChar) ?? this.sprite.texture);
    }
  }

  override update(dt: number): void {
    if (this.deleted) return;
    if (this.pressing) {
      this.pressTimer += dt;
      const step = 1 / PasswordObject.PRESS_FPS;
      while (this.pressTimer >= step) {
        this.pressTimer -= step;
        this.pressIdx++;
        if (this.pressIdx >= this.pressFrames.length) {
          this.pressing = false;
          if (this.sprite) this.sprite.texture = this.symbols.get(this.currentChar) ?? this.sprite.texture;
          break;
        }
        if (this.sprite) this.sprite.texture = this.pressFrames[this.pressIdx];
      }
    }
    const over = this.overlapsPlayer();
    if (over && !this.over) this.touch();
    this.over = over;
  }
}

/** ClearBlock (15,22): touch opens all the area's locks immediately. */
class ClearBlockObject extends PasswordObject {
  protected override get startChar(): number {
    return 22;
  }
  protected override get gfxId(): KnyttPoint {
    return new KnyttPoint(15, 20); // borrow the symbol sheet (its own gfx isn't extracted)
  }
  override isCorrect(): boolean {
    return true;
  }
  protected override solved(): boolean {
    return true;
  }
}

/** HoldButton (15,26): hold (stand on it ~5s) to open the area's locks. Ported from HoldButton.cs. */
class HoldButtonObject extends SpriteObject {
  private timer = 0;
  private fired = false;
  // yknytt: frame = min((int)(timer+1.9), 7); destroyWalls at frame 7 → timer ≈ 5.1s.
  private static readonly HOLD_TIME = 5.1;
  override update(dt: number): void {
    super.update(dt);
    if (this.fired || this.deleted) return;
    if (this.overlapsPlayer()) {
      this.timer += dt;
      if (this.timer >= HoldButtonObject.HOLD_TIME) {
        this.fired = true;
        this.game.objectLayers?.openLocks();
      }
    } else {
      this.timer = 0;
    }
  }
}

type ZoneEffect = 'noClimb' | 'noJump' | 'sticky' | 'updraft' | 'swim' | 'halt' | 'muffle';

class ZoneObject extends SpriteObject {
  private inside = false;
  constructor(
    game: Game,
    area: KnyttArea,
    layer: number,
    coords: KnyttPoint,
    objectId: KnyttPoint,
    private effect: ZoneEffect,
  ) {
    super(game, area, layer, coords, objectId);
  }
  private apply(delta: number): void {
    const p = this.game.player;
    switch (this.effect) {
      case 'noClimb':
        p.noClimbZones += delta;
        break;
      case 'noJump':
        p.noJumps += delta;
        break;
      case 'sticky':
        p.stickies += delta;
        break;
      case 'updraft':
        p.updrafts += delta;
        break;
      case 'swim':
        p.swimZones += delta;
        if (delta < 0) p.onSwimZoneExit();
        break;
      case 'halt': // Halt = NoJump + Sticky combined
        p.noJumps += delta;
        p.stickies += delta;
        break;
      case 'muffle':
        p.muffleZones += delta;
        this.game.audio.setMovementMuffle(p.muffleZones > 0);
        break;
    }
  }
  override update(dt: number): void {
    super.update(dt);
    // SwimZoneHalf (1,27): only the bottom half of the tile is water.
    const over =
      !this.neutralized &&
      (this.objectId.key === '1,27'
        ? this.game.player.overlapsRect(this.worldX, this.worldY + 12, TILE_SIZE, TILE_SIZE - 12)
        : this.overlapsPlayer());
    if (over && !this.inside) {
      this.inside = true;
      this.apply(1);
    } else if (!over && this.inside) {
      this.inside = false;
      this.apply(-1);
    }
  }
  override destroy(): void {
    if (this.inside) this.apply(-1);
    super.destroy();
  }
}

/**
 * Automation object (bank 0, 248–255): while Juni overlaps it, it auto-presses an
 * input on the altInput layer (hologram/walk/jump/umbrella/up/down/left/right).
 * Combined with a BlockUser object (which sets blockInput), this drives scripted
 * walk sequences. HoloAutomation (248) only presses while Juni is idle/walking.
 * Ported from Automation.cs / HoloAutomation.cs.
 */
const AUTOMATION_ACTIONS: Record<number, Action> = {
  248: 'hologram', // HoloAutomation
  249: 'walk',
  250: 'jump',
  251: 'umbrella',
  252: 'up',
  253: 'down',
  254: 'left',
  255: 'right',
};

class AutomationObject extends KnyttObject {
  private action: Action = 'walk';
  private holo = false;
  private inside = false;

  override async load(): Promise<void> {
    this.action = AUTOMATION_ACTIONS[this.objectId.y] ?? 'walk';
    this.holo = this.objectId.y === 248;
    this.game.input.hasAltInput = true;
  }

  override update(_dt: number): void {
    const over = this.overlapsPlayer();
    if (over) {
      if (this.holo && !this.game.player.canDeployHologram) {
        this.release();
        return;
      }
      this.game.input.setAlt(this.action, true);
      // left/right are mutually exclusive (Automation.cs body_entered)
      if (this.action === 'left') this.game.input.setAlt('right', false);
      if (this.action === 'right') this.game.input.setAlt('left', false);
      this.inside = true;
    } else if (this.inside) {
      this.release();
    }
  }

  private release(): void {
    this.game.input.setAlt(this.action, false);
    this.inside = false;
  }

  override destroy(): void {
    if (this.inside) this.game.input.setAlt(this.action, false);
    super.destroy();
  }
}

/**
 * AllowHologram (bank 0, 48): its presence restricts hologram deployment to the
 * tiles it covers — Juni can only deploy a hologram while standing in one.
 * Ported from AllowHologram.cs.
 */
class AllowHologramObject extends KnyttObject {
  private inside = false;

  override async load(): Promise<void> {
    this.game.player.allowHologramZones++;
  }

  override update(_dt: number): void {
    const over = this.overlapsPlayer();
    if (over && !this.inside) {
      this.inside = true;
      this.game.player.hologramPlaces++;
    } else if (!over && this.inside) {
      this.inside = false;
      this.game.player.hologramPlaces--;
    }
  }

  override destroy(): void {
    this.game.player.allowHologramZones--;
    if (this.inside) this.game.player.hologramPlaces--;
    super.destroy();
  }
}

/**
 * A sign: its text is hidden until Juni overlaps the sign tile, then fades in
 * (matches yknytt — Area2D extents 11.8 at the tile centre, 0.25s alpha fade).
 * The text is drawn in black inside the dialogue.png box, positioned above the
 * sign and clamped to the screen.
 */
class SignObject extends KnyttObject {
  private box: Container | null = null;
  private alpha = 0;
  private target = 0;
  forceShow = false; // set per-frame by a linked SignArea (0,29) overlapping Juni
  private texts: (string | null)[] = [];
  private messageIndex = -1;
  private shiftMessageIndex = 0; // SignShift(X): page index that fires the same-letter Shift
  private triggerMessageIndex = 0; // SignTrig(X): page index that fires the same-letter Trigger
  private near = false;
  private hintOn = false; // null first page → down-button hint while inside (Sign.cs)
  private active = false;
  private dialogueTex: Texture | null = null;
  private static readonly FADE = 1 / 0.25; // alpha units per second

  override async load(): Promise<void> {
    const letter = 'ABC'[this.objectId.y - 17] ?? 'A';
    this.texts.push(this.preprocess(this.area.getExtraData(`Sign(${letter})`)));
    for (let i = 2; ; i++) {
      const t = this.area.getExtraData(`Sign${i}(${letter})`);
      if (t == null) break;
      this.texts.push(this.preprocess(t));
    }
    // Sign.cs: a lone null page renders the literal "[SILENCE]".
    if (this.texts[0] == null && this.texts.length === 1) this.texts[0] = '[SILENCE]';
    this.shiftMessageIndex = parseIniInt(this.area.getExtraData(`SignShift(${letter})`) ?? '') ?? 0;
    this.triggerMessageIndex = parseIniInt(this.area.getExtraData(`SignTrig(${letter})`) ?? '') ?? 0;
    this.active =
      this.texts.some((t) => t != null && t.length > 0) ||
      this.shiftMessageIndex > 0 ||
      this.triggerMessageIndex > 0;
    if (!this.active) return;
    this.dialogueTex = await this.game.assets.getTexture('ui/img/dialogue.png');
    this.container.zIndex = 17; // signs render on top
  }

  /** Rebuild the dialogue box for one page of text. */
  private buildBox(text: string): void {
    this.box?.destroy({ children: true });
    const box = new Container();
    const label = new Text({
      text,
      style: {
        fontFamily: 'Magnificent, sans-serif',
        fontSize: 12,
        fill: 0x000000,
        align: 'center',
        wordWrap: true,
        wordWrapWidth: 150,
        lineHeight: 14,
      },
    });
    label.resolution = 2;

    const padX = 11;
    const padY = 8;
    const boxW = Math.ceil(label.width) + padX * 2;
    const boxH = Math.ceil(label.height) + padY * 2;

    if (this.dialogueTex) {
      const bg = new NineSliceSprite({
        texture: this.dialogueTex,
        leftWidth: 11,
        topHeight: 11,
        rightWidth: 8,
        bottomHeight: 10,
        width: boxW,
        height: boxH,
      });
      box.addChild(bg);
    } else {
      const bg = new Graphics().roundRect(0, 0, boxW, boxH, 4).fill({ color: 0xeae4cf });
      box.addChild(bg);
    }
    label.x = padX;
    label.y = padY;
    box.addChild(label);

    // Sign.cs DownArrow: a small indicator while more pages remain.
    if (this.messageIndex < this.texts.length - 1) {
      const ar = new Graphics()
        .poly([boxW / 2 - 3, boxH - 6, boxW / 2 + 3, boxH - 6, boxW / 2, boxH - 2])
        .fill({ color: 0x000000 });
      box.addChild(ar);
    }

    // Position the box centred above the sign tile, clamped to the area (600x240).
    const tileX = this.coords.x * TILE_SIZE;
    const tileY = this.coords.y * TILE_SIZE;
    let bx = TILE_SIZE / 2 - boxW / 2;
    let by = -boxH - 4;
    bx = Math.max(-tileX, Math.min(bx, 600 - tileX - boxW));
    by = Math.max(-tileY, Math.min(by, 240 - tileY - boxH));
    box.x = bx;
    box.y = by;
    box.alpha = this.alpha;
    this.box = box;
    this.container.addChild(box);
  }

  /**
   * Sign.cs nextMessage: advance the page; fire the linked Shift/Trigger when the
   * page counter reaches SignShift/SignTrig; past the last page wraps to hidden.
   * A null page hides the box but still counts.
   */
  private nextMessage(): void {
    this.messageIndex++;
    if (this.shiftMessageIndex > 0 && this.messageIndex === this.shiftMessageIndex) {
      const shift = this.game.objectLayers
        ?.findObjects(0, this.objectId.y - 3)
        .find((o) => o instanceof ShiftObject);
      (shift as ShiftObject | undefined)?.executeAnyway();
    }
    if (this.triggerMessageIndex > 0 && this.messageIndex === this.triggerMessageIndex) {
      const trig = this.game.objectLayers
        ?.findObjects(0, this.objectId.y + 15)
        .find((o) => o instanceof TriggerObject);
      (trig as TriggerObject | undefined)?.executeAnyway();
    }
    if (this.messageIndex >= this.texts.length) this.messageIndex = -1;
    const text = this.messageIndex !== -1 ? this.texts[this.messageIndex] : null;
    if (text != null) {
      this.buildBox(text);
      this.target = 1;
    } else {
      this.target = 0;
    }
  }

  /** Another sign was entered (Sign.cs force_exit): hide and reset paging. */
  forceExit(): void {
    this.near = false;
    this.messageIndex = -1;
    this.target = 0;
  }

  private leave(): void {
    if (this.hintOn) {
      this.hintOn = false;
      this.game.player.onShift--;
    }
    this.messageIndex = -1;
    this.target = 0;
  }

  override update(dt: number): void {
    if (!this.active) return;
    const p = this.game.player;
    // Detection box matches yknytt's Area2D (centre 12,12, extents 11.8); a linked
    // SignArea extends it (its overlap sets forceShow every frame).
    const near =
      p.overlapsRect(this.worldX + 0.2, this.worldY + 0.2, 23.6, 23.6) || this.forceShow;
    this.forceShow = false; // re-set each frame by an overlapping SignArea
    if (near && !this.near) {
      // Only one sign shows at a time (Sign.cs force-exits its peers on enter).
      for (const yid of [17, 18, 19]) {
        for (const s of this.game.objectLayers?.findObjects(0, yid) ?? []) {
          if (s instanceof SignObject && s !== this) s.forceExit();
        }
      }
      this.messageIndex = -1;
      this.nextMessage();
      if (this.texts[0] == null && !this.hintOn) {
        this.hintOn = true;
        p.onShift++;
      }
    } else if (!near && this.near) {
      this.leave();
    }
    this.near = near;
    // Down pages through multi-part signs (Juni.DownEvent connection in Sign.cs).
    if (
      near &&
      (this.texts.length > 1 || this.shiftMessageIndex > 0 || this.triggerMessageIndex > 0) &&
      this.game.input.justPressed('down')
    ) {
      this.nextMessage();
    }

    if (this.box && this.alpha !== this.target) {
      const step = SignObject.FADE * dt;
      this.alpha =
        this.target > this.alpha
          ? Math.min(this.target, this.alpha + step)
          : Math.max(this.target, this.alpha - step);
      this.box.alpha = this.alpha;
    }
  }

  override destroy(): void {
    if (this.hintOn) this.game.player.onShift--;
    super.destroy();
  }

  private preprocess(msg: string | null): string | null {
    if (msg == null) return null;
    let m = msg;
    if (m.startsWith('"') && m.endsWith('"')) m = m.substring(1, m.length - 1);
    m = m.replace(/\\n/g, '\n').replace(/\x7f/g, '\n');
    // Sign.cs (upstream 10ef3cf4, "more complex line break guess"): walk the message
    // line by line — a line runs to the next \n (if within 23 chars), else 22 chars,
    // or 23 when the 23rd char is a space or a backslash — and inside each line, if
    // the last space is the second half of a double space, that space becomes the
    // break. Replaces the old fixed every-22-chars scan.
    for (let i = 0; i < m.length; ) {
      const lineBreak = m.indexOf('\n', i);
      const len =
        lineBreak !== -1 && lineBreak + 1 - i <= 23
          ? lineBreak + 1 - i
          : m.length <= i + 22
            ? m.length - i
            : m[i + 22] === ' ' || m[i + 22] === '\\'
              ? 23
              : 22;
      let line = m.substring(i, i + len);
      const lastSpace = line.lastIndexOf(' ');
      if (!line.endsWith('\n') && lastSpace === line.lastIndexOf('  ') + 1) {
        m = m.substring(0, i + lastSpace) + '\n' + m.substring(i + lastSpace + 1);
        line = line.substring(0, lastSpace + 1);
      }
      i += line.length;
    }
    return m;
  }
}

function posMod(a: number, m: number): number {
  return ((a % m) + m) % m;
}

/**
 * Custom object (banks 255/254): defined in the world's `[Custom Object N]` INI
 * section. Either delegates to a built-in object (Bank+Object, with Hurts=false
 * making it safe) or renders a custom animated image from the Custom Objects folder.
 */
class CustomObjectObject extends KnyttObject {
  private delegate: KnyttObject | null = null;
  private sprite: Sprite | null = null;
  private frames: Texture[] = [];
  private fps = 10;
  private loop = true;
  private animTime = 0;
  private animIndex = 0;

  override async load(): Promise<void> {
    const key =
      this.objectId.x === 254 ? `Custom Object B${this.objectId.y}` : `Custom Object ${this.objectId.y}`;
    const sec = this.game.world.iniData.getSection(key);
    if (!sec) {
      this.remove();
      return;
    }
    const num = (n: string, d: number): number => parseIniInt(sec.get(n)) ?? d;
    const bank = num('Bank', -1);
    const obj = num('Object', -1);
    const safe = sec.get('Hurts')?.toLowerCase() === 'false';
    const image = sec.get('Image');

    // Mode 1: behave as a built-in object (optionally non-lethal), optionally
    // with its sprite frames replaced by a custom Image (yknytt overrideAnimation).
    if (bank !== -1 && obj !== -1) {
      const d = createObject(this.game, this.area, this.layerIndex, this.coords, new KnyttPoint(bank, obj));
      if (d) {
        this.delegate = d;
        d.neutralized = this.neutralized; // Overlay applies to the delegate too
        d.container.zIndex = this.neutralized ? 15 : this.zIndex;
        this.game.worldLayer.addChild(d.container);
        await d.load();
        if (safe) d.makeSafe();
        if (image) {
          const built = await this.buildFrames(num, image);
          if (built) {
            this.frames = built;
            const overrideSprite = d.container.children.find((c): c is Sprite => c instanceof Sprite);
            if (overrideSprite) {
              this.sprite = overrideSprite;
              this.sprite.texture = this.frames[0];
            }
          }
        }
      }
      return;
    }

    // Mode 2: a custom animated image from Custom Objects/.
    if (!image) {
      this.remove();
      return;
    }
    const built = await this.buildFrames(num, image);
    if (!built) {
      this.remove();
      return;
    }
    this.frames = built;
    this.sprite = new Sprite(this.frames[0]);
    this.sprite.anchor.set(0, 0);
    this.sprite.x = num('Offset X', 0);
    this.sprite.y = num('Offset Y', 0);
    this.container.addChild(this.sprite);
  }

  private async buildFrames(
    num: (n: string, d: number) => number,
    image: string,
  ): Promise<Texture[] | null> {
    const tw = num('Tile Width', 24);
    const th = num('Tile Height', 24);
    const animFrom = num('Init AnimFrom', 0);
    const animTo = num('Init AnimTo', -1);
    const tex = await this.game.assets.getTexture(`Custom Objects/${image}`);
    if (!tex) return null;
    const src = tex.source as TextureSource;
    const cols = Math.max(1, Math.floor(src.width / tw));
    const rows = Math.max(1, Math.floor(src.height / th));
    const all: Texture[] = [];
    for (let r = 0; r < rows; r++)
      for (let c = 0; c < cols; c++)
        all.push(new Texture({ source: src, frame: new Rectangle(c * tw, r * th, tw, th) }));
    const to = animTo < 0 ? all.length - 1 : Math.min(animTo, all.length - 1);
    let frames = all.slice(animFrom, to + 1);
    if (frames.length === 0) frames = all;
    this.fps = Math.max(1, num('Init AnimSpeed', 500) / 20);
    this.loop = num('Init AnimRepeat', 0) === 0 && num('Init AnimLoopback', 0) === 0;
    return frames;
  }

  override update(dt: number): void {
    if (this.delegate && !this.delegate.deleted) this.delegate.update(dt);
    if (!this.sprite || this.frames.length <= 1) return;
    this.animTime += dt;
    const step = 1 / this.fps;
    while (this.animTime >= step) {
      this.animTime -= step;
      this.animIndex++;
      if (this.animIndex >= this.frames.length) this.animIndex = this.loop ? 0 : this.frames.length - 1;
      this.sprite.texture = this.frames[this.animIndex];
    }
  }

  override destroy(): void {
    this.delegate?.destroy();
    super.destroy();
  }
}

interface ShiftCfg {
  area: KnyttPoint; // AbsoluteArea (target area, incl. any position-wrap correction)
  pos: KnyttPoint; // AbsolutePosition (target tile within the area, mod 25x10)
  areaCorrection: KnyttPoint; // area shift caused by an out-of-bounds XPos/YPos
  relativePos: KnyttPoint; // AbsolutePosition - Pos (for non-quantized pixel moves)
  absoluteTarget: boolean;
  onTouch: boolean;
  denyHologram: boolean;
  flagOn: Flag | null;
  flagOff: Flag | null;
  save: boolean;
  coin: number;
  sound: string | null; // resolved per Switch.cs (teleport default / null for none/false)
  effect: boolean;
  cutscene: string | null;
  quantize: boolean;
  stopMusic: boolean;
  delay: number; // Time, milliseconds
  character: string | null;
  hide: boolean;
  saveFile: string | null; // Save(A)=<name> → savegame<name>.ini
}

const AREA_PX_W = AREA_WIDTH * TILE_SIZE;
const AREA_PX_H = AREA_HEIGHT * TILE_SIZE;

/**
 * Switch shape hitboxes (Switch.tscn's four Area2D shapes, KnyttSwitch.SwitchShape):
 * 0 SPOT (12,20.7)±(6.1,3.3), 1 FLOOR (12,22)±(12,2), 2 CIRCLE (12.5,12.5) r5.5,
 * 3 SQUARE (12,12)±(11.8,11.8). Local to the switch tile's top-left.
 */
function switchOverlaps(game: Game, x: number, y: number, shape: number): boolean {
  const p = game.player;
  switch (shape) {
    case 1:
      return p.overlapsRect(x, y + 20, 24, 4);
    case 2:
      return p.overlapsCircle(x + 12.5, y + 12.5, 5.5);
    case 3:
      return p.overlapsRect(x + 0.2, y + 0.2, 23.6, 23.6);
    default:
      return p.overlapsRect(x + 5.9, y + 17.4, 12.2, 6.6);
  }
}

/**
 * Switch.cs:17-19: default "teleport" when Sound is absent/empty; "none"/"false" →
 * null (silent); otherwise the raw name (resolved by Game.playSwitchSound).
 */
function resolveSwitchSound(raw: string | null): string | null {
  const s = raw?.toLowerCase();
  return s == null || s === '' ? 'teleport' : s === 'none' || s === 'false' ? null : raw;
}

/**
 * Shift (bank-0 ids 14-16): warps Juni to a target area/position when she presses
 * Down on it (or on touch). Ported from Shift.cs / KnyttShift / KnyttSwitch / Switch.cs.
 */
class ShiftObject extends SpriteObject {
  private cfg: ShiftCfg | null = null;
  private cooldown = 0;
  private inZone = false; // Selector registration mirror (Switch.cs AsOne — always on for shifts)
  private hintOn = false; // down-button hint counted on the player
  private shape = 0; // KnyttSwitch.SwitchShape: SPOT/FLOOR/CIRCLE/SQUARE
  private pendingHolo = false; // DenyHologram: touch fire deferred until hologram dismissed
  private delayActive = false; // Time: the DelayTimer is one-shot, no re-entry

  /** Shift.executeAnyway: fired externally (a Sign reaching its SignShift page). */
  executeAnyway(): void {
    if (this.cfg) void this.execute();
  }

  override async load(): Promise<void> {
    await super.load();
    this.parseShift();
  }

  private parseShift(): void {
    const ed = this.area.extraData;
    const letter = 'ABC'[this.objectId.y - 14] ?? 'A';
    const get = (n: string): string | null => ed?.get(`Shift${n}(${letter})`) ?? null;
    const bool = (n: string, d = false): boolean => {
      const v = get(n);
      return v == null ? d : v.toLowerCase() === 'true';
    };
    const intv = (n: string): number => parseIniInt(get(n)) ?? 0;

    // Visible (KnyttSwitch:54) + Type/shape (KnyttSwitch:57-58) apply even without
    // ExtraData (KnyttShift still "shows the shift spot").
    if (!bool('Visible', true) && this.sprite) this.sprite.visible = false;
    const shapeRaw = intv('Type');
    this.shape = shapeRaw >= 0 && shapeRaw < 4 ? shapeRaw : 0;

    if (!ed) return; // no warp target without ExtraData

    const sound = resolveSwitchSound(get('Sound'));

    const absoluteTarget = bool('AbsoluteTarget', false);
    const fXMap = intv('XMap');
    const fYMap = intv('YMap');
    const fXPos = intv('XPos');
    const fYPos = intv('YPos');
    let areaX: number;
    let areaY: number;
    let rawX: number;
    let rawY: number;
    if (absoluteTarget) {
      areaX = fXMap;
      areaY = fYMap;
      rawX = fXPos;
      rawY = fYPos;
    } else {
      areaX = this.area.position.x + fXMap;
      areaY = this.area.position.y + fYMap;
      rawX = this.coords.x + fXPos;
      rawY = this.coords.y + fYPos;
    }
    // Positions beyond the 25x10 area wrap to the adjacent area (KnyttShift AreaCorrection).
    const corrX = Math.floor(rawX / AREA_WIDTH);
    const corrY = Math.floor(rawY / AREA_HEIGHT);
    areaX += corrX;
    areaY += corrY;
    const posX = posMod(rawX, AREA_WIDTH);
    const posY = posMod(rawY, AREA_HEIGHT);

    this.cfg = {
      area: new KnyttPoint(areaX, areaY),
      pos: new KnyttPoint(posX, posY),
      areaCorrection: new KnyttPoint(corrX, corrY),
      relativePos: new KnyttPoint(posX - this.coords.x, posY - this.coords.y),
      absoluteTarget,
      onTouch: bool('Touch'),
      denyHologram: bool('DenyHologram'),
      flagOn: parseFlag(get('FlagOn')),
      flagOff: parseFlag(get('FlagOff')),
      save: bool('Save', false),
      coin: intv('Coin'),
      sound,
      effect: bool('Effect', true),
      cutscene: get('Cutscene'),
      quantize: bool('Quantize', true),
      stopMusic: bool('StopMusic', false),
      delay: intv('Time'),
      character: get('Character'),
      hide: bool('Hide', false),
      saveFile: get('Save'),
    };
  }

  private overlaps(): boolean {
    return switchOverlaps(this.game, this.worldX, this.worldY, this.shape);
  }

  override update(dt: number): void {
    super.update(dt);
    if (this.cooldown > 0) this.cooldown -= dt;
    if (!this.cfg || this.game.player.dead) return;
    const sel = this.game.objectLayers?.selector;
    const over = this.overlaps();
    // Switch.cs: a non-touch shift shows the down-button hint while Juni is inside.
    const wantHint = over && !this.cfg.onTouch;
    if (wantHint !== this.hintOn) {
      this.hintOn = wantHint;
      this.game.player.onShift += wantHint ? 1 : -1;
    }
    // Switch.cs AsOne (always true for shifts): overlapped same-id shifts act as one.
    if (over && !this.inZone) {
      this.inZone = true;
      sel?.register(this);
      if (this.cfg.onTouch && this.cooldown <= 0) {
        // DenyHologram (Switch.cs:31-34): defer the touch fire until the hologram is dismissed.
        if (this.cfg.denyHologram && this.game.player.hologramActive) {
          this.pendingHolo = true;
        } else if ((sel?.size(this.objectId.key) ?? 1) <= 1) {
          // Touch fires on the enter edge; a second same-id shift entered the same
          // moment (Juni straddling two tiles) backs out (Switch.cs GetSize > 1).
          void this.execute();
        }
      }
    } else if (!over && this.inZone) {
      this.inZone = false;
      this.pendingHolo = false;
      sel?.unregister(this);
    }
    // Deferred touch fire once the hologram is gone (Juni.HologramStopped).
    if (this.pendingHolo && !this.game.player.hologramActive) {
      this.pendingHolo = false;
      void this.execute();
    }
    if (over && this.cooldown <= 0 && !this.cfg.onTouch && this.game.input.isHeld('down') && !this.game.shiftLock) {
      // The Selector round picks one of the straddled same-id shifts per press.
      if (sel?.isSelected(this) ?? true) {
        this.game.shiftLock = true;
        void this.execute();
      }
    }
  }

  override destroy(): void {
    if (this.inZone) this.game.objectLayers?.selector.unregister(this);
    if (this.hintOn) this.game.player.onShift--;
    super.destroy();
  }

  private async execute(): Promise<void> {
    const cfg = this.cfg!;
    const g = this.game;
    // Shift.cs:22: only fire while this shift's area is still the current area.
    if (!g.currentArea.position.equals(this.area.position)) return;
    g.shiftLock = true; // juniInput.SwitchHeld = true
    if (cfg.coin && g.values.getCoinCount() < cfg.coin) return;
    this.cooldown = 0.7;
    const prevSong = this.area.song; // currently-playing track (source area)

    // Time / Hide (Shift.cs:27-61): during the delay Juni is hidden + input-blocked +
    // frozen, and the paired Trigger at (0,y+18) fires immediately.
    if (cfg.delay > 0) {
      if (this.delayActive) return; // DelayTimer already running (Shift.cs:49)
      this.delayActive = true;
      if (cfg.hide) {
        g.player.container.visible = false;
        g.input.blockInput = true;
        g.player.stopMotion = true;
        const trig = g.objectLayers
          ?.findObjects(0, this.objectId.y + 18)
          .find((o) => o instanceof TriggerObject) as TriggerObject | undefined;
        trig?.executeAnyway();
      }
      await new Promise<void>((r) => setTimeout(r, cfg.delay));
      this.delayActive = false;
      if (cfg.hide) {
        g.player.container.visible = true;
        g.input.blockInput = false;
        g.player.stopMotion = false;
      }
      // Shift.cs:60: bail if the area changed during the wait.
      if (this.deleted || !g.currentArea.position.equals(this.area.position)) return;
    }

    if (cfg.coin) g.values.coinsSpent += cfg.coin;

    if (cfg.flagOn) {
      if (cfg.flagOn.power) g.setPower(cfg.flagOn.number as PowerNames, true);
      else g.values.setFlag(cfg.flagOn.number, true);
    }
    if (cfg.flagOff) {
      if (cfg.flagOff.power) g.setPower(cfg.flagOff.number as PowerNames, false);
      else g.values.setFlag(cfg.flagOff.number, false);
    }

    // Area change with flag-warp + Warp correction (Shift.cs:102-113).
    let relX = cfg.area.x - this.area.position.x;
    let relY = cfg.area.y - this.area.position.y;
    const areaExists = !!g.world.getArea(cfg.area);
    if (relX !== 0 || relY !== 0) {
      const fw = g.getFlagWarp(cfg.area);
      if (fw) {
        relX += fw.x;
        relY += fw.y;
      }
      if (!cfg.absoluteTarget && !cfg.areaCorrection.isZero) {
        const wc = this.area.warp.getWarpCoords(
          new KnyttPoint(
            this.area.position.x + cfg.areaCorrection.x,
            this.area.position.y + cfg.areaCorrection.y,
          ),
          this.area.position,
        );
        relX += wc.x;
        relY += wc.y;
      }
      if (areaExists) {
        await g.changeArea(new KnyttPoint(this.area.position.x + relX, this.area.position.y + relY), true);
      }
    }

    // Move Juni (Shift.cs:117-133). Quantized/absolute snap to the tile (BaseCorrection
    // up when rising); non-quantized keeps her sub-tile offset via a raw pixel delta.
    // For a nonexistent target the area didn't change, so this places her within the
    // source area (C# CurrentArea is unchanged) — she still moves, and Save below
    // still records the destination.
    if (cfg.quantize || cfg.absoluteTarget) {
      g.player.moveToPosition(g.currentArea, cfg.pos, true);
    } else {
      g.player.x += relX * AREA_PX_W + cfg.relativePos.x * TILE_SIZE;
      g.player.y += relY * AREA_PX_H + cfg.relativePos.y * TILE_SIZE;
    }

    // Effect at Juni's landing tile centre (Shift.cs:156 playEffect(juni.AreaPosition)).
    if (cfg.effect)
      g.objectLayers?.playEffect(
        Math.floor(g.player.x / TILE_SIZE) * TILE_SIZE + TILE_SIZE / 2,
        Math.floor(g.player.y / TILE_SIZE) * TILE_SIZE + TILE_SIZE / 2,
      );

    // In-world sound, suppressed when a cutscene follows (Shift.cs:159-162).
    if (cfg.sound != null && cfg.cutscene == null) g.playSwitchSound(cfg.sound);

    // StopMusic / cutscene music (Shift.cs:164-180).
    if (cfg.stopMusic || ((relX !== 0 || relY !== 0) && cfg.cutscene != null)) {
      const newSong = g.currentArea.song;
      if (newSong !== prevSong) g.audio.playQueued();
      else g.audio.fadeInMusic(3);
    }
    if ((relX !== 0 || relY !== 0) && cfg.cutscene != null) g.audio.stopAmbianceFade();

    // Character (KnyttShift:81): swap Juni's sprite/umbrella sheets + record in the save.
    if (cfg.character != null) void g.player.changeCharacter(cfg.character);

    // Shift.cs:187-191: a Shift cutscene is ALWAYS a Middle cutscene — it's recorded
    // (in Endings when named "ending", else Cutscenes) and returns to the game with a
    // respawn; only the Win object ends the game. An "ending" name also persists into
    // the save so the menu's completed tint can see it (Win.checkLevelCompleted).
    if (cfg.cutscene) {
      if (cfg.cutscene.toLowerCase() === 'ending') {
        g.values.endings.add(cfg.cutscene);
        g.save.addEnding(cfg.cutscene);
        g.onSaveWrite?.(g.save);
      } else {
        g.values.cutscenes.add(cfg.cutscene);
      }
      // The resolved Shift sound plays inside the cutscene (Cutscene.cs:16-20).
      g.playCutscene(cfg.cutscene, () => {}, cfg.sound);
    }

    // Save (Shift.cs:193): always persist the shift DESTINATION area/position.
    if (cfg.save) g.saveGame(true, cfg.area, cfg.pos);

    // SaveFile (Shift.cs:203-215): Save(A)=<name> loads savegame<name>.ini as the new save.
    if (cfg.saveFile != null && cfg.saveFile !== 'true' && cfg.saveFile !== 'false') {
      g.loadShiftSaveFile(cfg.saveFile);
    }
  }
}

/** Trigger (bank-0 ids 32-34): sets a flag / saves when touched. */
/**
 * Trigger (bank-0 32-34): on touch / Down-press, spends its SwitchID to **spawn** a target
 * object (at matching SpawnPoints `0,y+10`, else at its SpawnX/Y), play a `collect` effect,
 * and **activate** matching DeletePoints (`0,y+13`). Ports Trigger.cs / KnyttTrigger (keys
 * `Trig…(A|B|C)` in the area ExtraData). Fires once unless `Repeat`. Absent from bundled
 * worlds — verified synthetically.
 */
class TriggerObject extends SpriteObject {
  private executed = false;
  private touchedLast = false;
  private inZone = false; // Selector registration mirror (Switch.cs AsOne)
  private shape = 0; // KnyttSwitch.SwitchShape
  private pendingHolo = false; // DenyHologram deferred touch fire
  private readonly letter = 'ABC'[this.objectId.y - 32] ?? 'A';
  private cfg: {
    bank: number;
    object: number;
    spawn: KnyttPoint; // AbsolutePosition (SpawnX/Y, relative to the trigger tile unless AbsoluteTarget)
    effectX: number;
    effectY: number;
    effect: boolean;
    sound: string | null; // resolved per Switch.cs
    repeat: boolean;
    touch: boolean;
    asOne: boolean;
    denyHologram: boolean;
  } | null = null;

  override async load(): Promise<void> {
    await super.load();
    const ed = this.area.extraData;
    const get = (n: string) => ed?.get(`Trig${n}(${this.letter})`) ?? null;
    const intv = (n: string) => parseIniInt(get(n) ?? '') ?? 0;
    const bool = (n: string, def = false) => {
      const v = get(n);
      return v == null ? def : v.toLowerCase() === 'true';
    };
    if (!bool('Visible', true) && this.sprite) this.sprite.visible = false;
    const shapeRaw = intv('Type');
    this.shape = shapeRaw >= 0 && shapeRaw < 4 ? shapeRaw : 0;
    if (!ed) return;
    // KnyttTrigger: AbsoluteTarget defaults TRUE; false makes SpawnX/Y relative
    // to the trigger's own tile (KnyttSwitch.FormattedPosition).
    const absoluteTarget = bool('AbsoluteTarget', true);
    const sx = intv('SpawnX');
    const sy = intv('SpawnY');
    this.cfg = {
      bank: intv('Bank'),
      object: intv('Object'),
      spawn: absoluteTarget
        ? new KnyttPoint(sx, sy)
        : new KnyttPoint(this.coords.x + sx, this.coords.y + sy),
      effectX: intv('EffectX'),
      effectY: intv('EffectY'),
      effect: bool('Effect', true),
      sound: resolveSwitchSound(get('Sound')),
      repeat: bool('Repeat', false),
      touch: bool('Touch', false),
      asOne: bool('AsOne', true),
      denyHologram: bool('DenyHologram', false),
    };
  }

  override update(dt: number): void {
    super.update(dt);
    if (!this.cfg || this.game.player.dead) return;
    const sel = this.cfg.asOne ? this.game.objectLayers?.selector : undefined;
    const over = switchOverlaps(this.game, this.worldX, this.worldY, this.shape);
    // Switch.cs AsOne: overlapped same-id triggers act as one.
    if (over && !this.inZone) {
      this.inZone = true;
      sel?.register(this);
    } else if (!over && this.inZone) {
      this.inZone = false;
      this.pendingHolo = false;
      sel?.unregister(this);
    }
    if (this.executed && !this.cfg.repeat) return;
    if (this.cfg.touch) {
      if (over && !this.touchedLast) {
        // DenyHologram (Switch.cs:31-34): defer until the hologram is dismissed.
        if (this.cfg.denyHologram && this.game.player.hologramActive) this.pendingHolo = true;
        // A second same-id trigger entered the same moment backs out (GetSize > 1).
        else if ((sel?.size(this.objectId.key) ?? 1) <= 1) this.execute();
      }
      if (this.pendingHolo && over && !this.game.player.hologramActive) {
        this.pendingHolo = false;
        this.execute();
      }
      this.touchedLast = over;
    } else if (over && this.game.input.isHeld('down') && !this.game.shiftLock) {
      if (sel?.isSelected(this) ?? true) {
        this.game.shiftLock = true;
        this.execute();
      }
    }
  }

  override destroy(): void {
    if (this.inZone && this.cfg?.asOne) this.game.objectLayers?.selector.unregister(this);
    super.destroy();
  }

  private worldOf(c: KnyttPoint): { x: number; y: number } {
    return {
      x: this.area.position.x * AREA_WIDTH * TILE_SIZE + c.x * TILE_SIZE,
      y: this.area.position.y * AREA_HEIGHT * TILE_SIZE + c.y * TILE_SIZE,
    };
  }

  /** Trigger.executeAnyway: fired externally (a Sign reaching its SignTrig page). */
  executeAnyway(): void {
    if (this.cfg) this.execute();
  }

  private execute(): void {
    const cfg = this.cfg!;
    const ol = this.game.objectLayers;
    // Switch.cs executeAnyway: an AsOne execution marks every same-id trigger in the
    // area as executed (a non-Repeat trigger row can only ever fire once, total).
    if (cfg.asOne && ol) {
      for (const o of ol.findObjects(this.objectId.x, this.objectId.y)) {
        if (o instanceof TriggerObject) o.executed = true;
      }
    }
    this.executed = true;
    if (!ol) return;
    // StandartSoundPlayer via Switch.cs's pre-resolved name (null = silent).
    this.game.playSwitchSound(cfg.sound);

    let effectCoords = this.coords;
    if (cfg.bank !== 0 || cfg.object !== 0) {
      const target = new KnyttPoint(cfg.bank, cfg.object);
      const spawnPoints = ol.findObjects(0, this.objectId.y + 10);
      if (spawnPoints.length) {
        for (const sp of spawnPoints) ol.spawnObject(this.layerIndex, sp.coords, target);
        effectCoords = spawnPoints[0].coords;
      } else {
        ol.spawnObject(this.layerIndex, cfg.spawn, target);
        effectCoords = cfg.spawn;
      }
    }

    if (cfg.effect) {
      const w = this.worldOf(effectCoords);
      ol.playEffect(w.x + TILE_SIZE / 2 + cfg.effectX, w.y + TILE_SIZE / 2 + cfg.effectY);
    }

    for (const dp of ol.findObjects(0, this.objectId.y + 13)) {
      if (dp instanceof DeletePointObject) dp.activate();
    }
  }
}

/**
 * DeletePoint (bank-0 45-47): inert until a matching Trigger activates it, then removes
 * every object whose centre lies in its tile (except itself). Ports DeletePoint.cs.
 */
class DeletePointObject extends KnyttObject {
  activate(): void {
    this.game.objectLayers?.deleteObjectsInTile(this.worldX, this.worldY, this);
  }
}

/**
 * Warp (bank-0 20): the area's Warp INI offsets only apply while this object is
 * present — it loads them at area load (Warp.cs), and a DeletePoint removing it
 * resets the area to a fresh zero warp (Warp.cs customDeletion).
 */
class WarpObject extends KnyttObject {
  override load(): Promise<void> {
    if (this.area.extraData) this.area.warp.loadWarpData();
    return Promise.resolve();
  }
  override customDeletion(): void {
    this.area.warp = new KnyttWarp(this.area);
  }
}

/**
 * SwimScreen (bank-1): marks the whole area as underwater; deleting it via a
 * DeletePoint turns the area's swim off (SwimScreen.cs).
 */
class SwimScreenObject extends KnyttObject {
  override load(): Promise<void> {
    this.game.areaSwim = true;
    return Promise.resolve();
  }
  override customDeletion(): void {
    this.game.areaSwim = false;
  }
}

/**
 * BlockUser (bank-0 247): real input is ignored while present; a DeletePoint
 * removing it restores input (BlockUser.cs customDeletion). GhostMusic has no
 * customDeletion upstream, so it stays a load-scan flag.
 */
class BlockUserObject extends KnyttObject {
  override load(): Promise<void> {
    this.game.input.blockInput = true;
    return Promise.resolve();
  }
  override customDeletion(): void {
    this.game.input.blockInput = false;
  }
}

/**
 * SignArea (bank-0 29): an extended trigger region for a Sign — while Juni overlaps it,
 * the linked Sign (id `0,y-12` → 0,17) fades in. Ports SignArea.cs.
 */
class SignAreaObject extends KnyttObject {
  override update(): void {
    if (!this.overlapsPlayer()) return;
    for (const s of this.game.objectLayers?.findObjects(0, this.objectId.y - 12) ?? []) {
      if (s instanceof SignObject) s.forceShow = true;
    }
  }
}

/** SpawnPoint (bank-0 ids 42-44): moves the respawn point here on touch. */
class SpawnPointObject extends SpriteObject {
  override update(dt: number): void {
    super.update(dt);
    if (this.overlapsPlayer() && !this.game.player.dead) {
      this.game.save.setArea(this.area.position);
      this.game.save.setAreaPosition(this.coords);
    }
  }
}

/**
 * Title (bank-0 id 36, Title.cs): shows the AREA's `Title`/`Subtitle` extra-data
 * as a screen-space banner (Game.uiLayer), aligned by the object's tile x and
 * animated by TitleNode.tscn's fade keys. Deduped if the label already shows
 * the same text.
 */
class TitleObject extends KnyttObject {
  override load(): Promise<void> {
    this.game.titleBanner.show(
      this.area.getExtraData('Title'),
      this.area.getExtraData('Subtitle'),
      this.coords.x,
      this.coords.y,
    );
    return Promise.resolve();
  }
}

// Invisible zones/blocks have no sprite, so they're routed by name. Everything
// visible (spikes, blocks, ghosts, enemies) is data-driven via the gfx entry's
// deadly / solid / ghost / muff flags.
const DEATHZONE = /^DeathZone/;
const INVISIBLE_SOLID = /^InvisibleBlock$|^Barrier$|^InvisibleBarrier$|^FlyersBarrier$/;

function createObject(
  game: Game,
  area: KnyttArea,
  layer: number,
  coords: KnyttPoint,
  objectId: KnyttPoint,
): KnyttObject | null {
  const name = objectName(objectId.x, objectId.y);
  if (!name) return null;

  if (objectId.x === 255 || objectId.x === 254) {
    return new CustomObjectObject(game, area, layer, coords, objectId);
  }

  if (objectId.x === 0) {
    switch (name) {
      case 'SavePoint':
        return new SavePointObject(game, area, layer, coords, objectId);
      case 'Win':
        return new WinObject(game, area, layer, coords, objectId);
      case 'PowerItem':
        return new PowerItemObject(game, area, layer, coords, objectId);
      case 'Sign':
        return new SignObject(game, area, layer, coords, objectId);
      case 'SignArea':
        return new SignAreaObject(game, area, layer, coords, objectId);
      case 'NoClimb':
        return new ZoneObject(game, area, layer, coords, objectId, 'noClimb');
      case 'NoJump':
        return new ZoneObject(game, area, layer, coords, objectId, 'noJump');
      case 'Sticky':
        return new ZoneObject(game, area, layer, coords, objectId, 'sticky');
      case 'Shift':
        return new ShiftObject(game, area, layer, coords, objectId);
      case 'Trigger':
        return new TriggerObject(game, area, layer, coords, objectId);
      case 'SpawnPoint':
        return new SpawnPointObject(game, area, layer, coords, objectId);
      case 'DeletePoint':
        return new DeletePointObject(game, area, layer, coords, objectId);
      case 'Warp':
        return new WarpObject(game, area, layer, coords, objectId);
      case 'Title':
        return new TitleObject(game, area, layer, coords, objectId);
      case 'Halt': // NoJump + Sticky
        return new ZoneObject(game, area, layer, coords, objectId, 'halt');
      case 'Automation':
      case 'HoloAutomation':
        return new AutomationObject(game, area, layer, coords, objectId);
      case 'AllowHologram':
        return new AllowHologramObject(game, area, layer, coords, objectId);
      case 'SecretDetector':
        return new SecretDetectorObject(game, area, layer, coords, objectId);
      case 'EnemyDetector':
        return new EnemyDetectorObject(game, area, layer, coords, objectId);
      case 'LocalAmbient':
        return new LocalAmbientObject(game, area, layer, coords, objectId);
      case 'MuteAmbient':
        return new MuteAmbientObject(game, area, layer, coords, objectId);
      case 'BlockUser':
        return new BlockUserObject(game, area, layer, coords, objectId);
      default:
        // GhostMusic / Cloud handled as area-scan flags.
        return null;
    }
  }

  // Bank-7 weather: rain generator / single raindrop, sun rays, then leaf/snow/dust.
  if (objectId.x === 7 && (objectId.y === 8 || objectId.y === 9))
    return new RainObject(game, area, layer, coords, objectId);
  if (objectId.x === 7 && objectId.y === 5)
    return new SunRayObject(game, area, layer, coords, objectId);
  if (name === 'NoWall') return new NoWallObject(game, area, layer, coords, objectId);
  if (name === 'WhiteExplosion') return new WhiteExplosionObject(game, area, layer, coords, objectId);
  if (name === 'Bubble') return new BubbleObject(game, area, layer, coords, objectId);
  const particleCfg = getParticleConfig(objectId.x, objectId.y);
  if (particleCfg) return new LeafObject(game, area, layer, coords, objectId, particleCfg);

  if (name === 'Collectable' || name === 'Coin' || name === 'Artifact') {
    return new CollectableObject(game, area, layer, coords, objectId);
  }
  if (name === 'Spring') return new SpringObject(game, area, layer, coords, objectId);
  if (name === 'Updraft') return new ZoneObject(game, area, layer, coords, objectId, 'updraft');
  if (name === 'Muffle') return new ZoneObject(game, area, layer, coords, objectId, 'muffle');
  if (objectId.x === 13 && objectId.y >= 7 && objectId.y <= 12)
    return new LaserObject(game, area, layer, coords, objectId);
  if (objectId.x === 13 && objectId.y === 3)
    return new LaserRobotObject(game, area, layer, coords, objectId);
  if (objectId.x === 16 && objectId.y === 29)
    return new OneWayPlatformObject(game, area, layer, coords, objectId);
  if (objectId.x === 16 && objectId.y === 30)
    return new TwoWayPlatformObject(game, area, layer, coords, objectId);
  if (objectId.x === 10 && (objectId.y === 4 || objectId.y === 5))
    return new BouncerEnemyObject(game, area, layer, coords, objectId);
  if (name === 'SwimZoneFull' || name === 'SwimZoneHalf')
    return new ZoneObject(game, area, layer, coords, objectId, 'swim');
  if (name === 'SwimScreen') return new SwimScreenObject(game, area, layer, coords, objectId);
  if (objectId.x === 15 && objectId.y >= 13 && objectId.y <= 21)
    return new PasswordObject(game, area, layer, coords, objectId);
  if (objectId.x === 15 && objectId.y === 22)
    return new ClearBlockObject(game, area, layer, coords, objectId);
  if (objectId.x === 15 && (objectId.y === 23 || objectId.y === 24))
    return new LockBlockObject(game, area, layer, coords, objectId);
  if (objectId.x === 15 && objectId.y === 26)
    return new HoldButtonObject(game, area, layer, coords, objectId);
  if (objectId.x === 15 && objectId.y >= 27 && objectId.y <= 30)
    return new KeyDoorObject(game, area, layer, coords, objectId);
  if (objectId.x === 15 && objectId.y >= 31 && objectId.y <= 36)
    return new CollectionDoorObject(game, area, layer, coords, objectId);
  if (INVISIBLE_SOLID.test(name)) return new InvisibleSolidObject(game, area, layer, coords, objectId);
  if (DEATHZONE.test(name)) return new HazardZoneObject(game, area, layer, coords, objectId);

  // Everything else: decorative / enemy sprite, driven by the gfx entry flags
  // (ghost = Eye-gated + flicker, muff = patrol, deadly = lethal, solid = block).
  return new SpriteObject(game, area, layer, coords, objectId);
}

/**
 * Warms the asset cache for an area before Juni actually enters it (called for
 * the neighbours of the area she's currently in), so `ObjectLayers.load()`'s
 * texture fetches are cache hits instead of first-time decodes/GPU uploads —
 * the source of the screen-crossing hitch. Data-only: reads placed object ids
 * + gfx entries without instantiating any KnyttObject.
 */
export function prefetchAreaAssets(game: Game, area: KnyttArea): void {
  if (area.empty) return;
  void game.assets.getGradient(area.background);
  const sheets = new Set<string>();
  for (const data of area.objectLayers) {
    if (!data) continue;
    for (let y = 0; y < AREA_HEIGHT; y++) {
      for (let x = 0; x < AREA_WIDTH; x++) {
        const oid = getObjectId(data, x, y);
        if (oid.isZero) continue;
        if (oid.x === 254 || oid.x === 255) {
          const key = oid.x === 254 ? `Custom Object B${oid.y}` : `Custom Object ${oid.y}`;
          const sec = game.world.iniData.getSection(key);
          if (!sec) continue;
          const image = sec.get('Image');
          if (image) sheets.add(`Custom Objects/${image}`);
          const bank = parseIniInt(sec.get('Bank')) ?? -1;
          const obj = parseIniInt(sec.get('Object')) ?? -1;
          if (bank !== -1 && obj !== -1) {
            const delegateEntry = getEntry(new KnyttPoint(bank, obj));
            if (delegateEntry) collectEntrySheets(delegateEntry, sheets);
          }
          continue;
        }
        const entry = getEntry(oid);
        if (entry) collectEntrySheets(entry, sheets);
      }
    }
  }
  for (const path of sheets) void game.assets.getTexture(path);
}

/**
 * ObjectSelector.cs: rotates which member of a same-id group is "selected" each
 * round — pickOne timers / limitBullets fire gates, so a screenful of identical
 * emitters fires like one, not N. A round is one isSelected() poll per member;
 * members stay in lockstep via a shared random initial timer delay
 * (TimerExt syncedRandomDelay). One instance per area (reset by construction).
 */
class Selector {
  private groups = new Map<
    string,
    { members: KnyttObject[]; counter: number; selection: KnyttObject | null; delay: number | null }
  >();

  private group(key: string) {
    let g = this.groups.get(key);
    if (!g) {
      g = { members: [], counter: 0, selection: null, delay: null };
      this.groups.set(key, g);
    }
    return g;
  }

  register(o: KnyttObject, key = o.objectId.key): void {
    const g = this.group(key);
    if (!g.members.includes(o)) g.members.push(o);
  }

  unregister(o: KnyttObject, key = o.objectId.key): void {
    const g = this.groups.get(key);
    if (!g) return;
    const i = g.members.indexOf(o);
    if (i >= 0) g.members.splice(i, 1);
    g.counter = 0;
    g.selection = null;
  }

  /** Lazy round: pick a random member, hold it until every member has polled once. */
  isSelected(o: KnyttObject, key = o.objectId.key): boolean {
    const g = this.groups.get(key);
    if (!g || !g.members.includes(o)) return false;
    if (!g.selection) {
      g.counter = 0;
      g.selection = g.members[Math.floor(Math.random() * g.members.length)];
    }
    const selected = g.selection === o;
    g.counter++;
    if (g.counter >= g.members.length) g.selection = null;
    return selected;
  }

  /** Shared random initial delay per group (TimerExt syncedRandomDelay). */
  sharedDelay(o: KnyttObject, max: number): number {
    const g = this.group(o.objectId.key);
    if (g.delay == null) g.delay = Math.random() * max;
    return g.delay;
  }

  /** Current member count (ObjectSelector.cs GetSize). */
  size(key: string): number {
    return this.groups.get(key)?.members.length ?? 0;
  }
}

/** Manages all objects for the current area. */
export class ObjectLayers {
  readonly selector = new Selector();
  private objects: KnyttObject[] = [];
  playNoMusic = false;
  // Per-ambiance-slot custom-volume flag (LocalAmbient/MuteAmbient present → driven
  // per-frame, defaulting to muted). Consumed by Game when setting the area's ambiance.
  customAmbiance: [boolean, boolean] = [false, false];
  // Leaf-generator groups keyed by object id: yknytt's `pickOne` Selector — one member
  // spawns a particle per timer tick, rotating, so dense leaf tiles don't over-spawn.
  private leafGroups: { members: LeafObject[]; timer: number; idx: number; interval: number }[] = [];
  // Rain generators: yknytt's Selector picks one per frame to attempt a probabilistic
  // drop (NextFloat(.025) < delta → ~dt/0.025 chance). We rotate through them similarly.
  private rainGen: RainObject[] = [];
  private rainIdx = 0;
  // Cloud (7,16) surface tiles, keyed "globalTileX,globalTileY" — Juni emits cloud
  // puffs when walking/climbing over one (JuniMotionParticles.ParticleType).
  private cloudCells = new Set<string>();
  // Deadly LiquidPool wave-band rects (world coords) — bullets entering one disappear
  // (LiquidPoolDeadly.cs body_entered destroys BaseBullets, not just Juni).
  private deadlyPoolRects: [number, number, number, number][] = [];
  // Halt (0,49) / NoClimb (0,25) zone tiles: both sit on Godot collision layer 16,
  // which Juni's Climb checker mask (22) sees — any overlapping area on that layer
  // forces Checkers.Colliding false (Juni.tscn:387 + Checkers.cs), blocking climb.
  private climbBlockCells: [number, number][] = [];

  /** Is the world pixel (wx,wy) over a Cloud surface tile? */
  cloudAt(wx: number, wy: number): boolean {
    return this.cloudCells.has(`${Math.floor(wx / TILE_SIZE)},${Math.floor(wy / TILE_SIZE)}`);
  }

  /** Is the world pixel inside a deadly LiquidPool's wave band? (bullet destruction) */
  deadlyPoolAt(wx: number, wy: number): boolean {
    for (const [x, y, w, h] of this.deadlyPoolRects) {
      if (wx >= x && wx < x + w && wy >= y && wy < y + h) return true;
    }
    return false;
  }

  /** Does a rect (Juni's climb-checker strip) overlap a Halt/NoClimb zone tile? */
  climbBlockedRect(x: number, y: number, w: number, h: number): boolean {
    for (const [cx, cy] of this.climbBlockCells) {
      if (x < cx + TILE_SIZE && x + w > cx && y < cy + TILE_SIZE && y + h > cy) return true;
    }
    return false;
  }

  // Active "collect" effect sparkles (Trigger playEffect): pickup.png, alpha 0→bright→0.
  private effects: { sprite: Sprite; age: number }[] = [];

  /** Objects in this area whose id matches (x,y) — for Trigger SpawnPoint/DeletePoint lookup. */
  findObjects(x: number, y: number): KnyttObject[] {
    return this.objects.filter((o) => !o.deleted && o.objectId.x === x && o.objectId.y === y);
  }

  /**
   * GDObjectLayers.cs checkCollectables: marks this area's still-uncollected
   * power-ups/doors/creatures/coins/artifacts for the Map power's reveal.
   * PowerItemObject/DoorObject/CollectableObject already self-remove once
   * collected/unlocked, so any live instance here is inherently uncollected.
   */
  checkCollectables(values: JuniValues): void {
    const pos = this.area.mapPosition;
    values.resetMarks(pos);
    for (const o of this.objects) {
      if (o.deleted) continue;
      // Ownership is re-checked against the *passed* values — they may be the save
      // snapshot's powers (GDKnyttGame.cs:290 SourcePowers), not the live ones.
      if (o instanceof PowerItemObject) {
        const power = PowerItemObject.OBJ2POWER[o.objectId.y] ?? 0;
        if (values.getPower(power as PowerNames)) continue;
        const collectable =
          power >= PowerNames.RedKey && power <= PowerNames.PurpleKey
            ? Collectable.Key + (power - PowerNames.RedKey + 1)
            : Collectable.Powerup;
        values.setMark(pos, collectable);
      } else if (o instanceof KeyDoorObject) {
        values.setMark(pos, Collectable.Door + (o.objectId.y - 27 + 1));
      } else if (o instanceof DoorObject) {
        values.setMark(pos, Collectable.Door);
      } else if (o.objectId.x === 19) {
        const y = o.objectId.y;
        if (values.getCollectable(y)) continue;
        values.setMark(pos, y <= 50 ? Collectable.Creature : y <= 150 ? Collectable.Coin : Collectable.Artifact);
      }
    }
  }

  /** Spawn a built-in object at a tile (Trigger target). Loads + adds it to the area. */
  spawnObject(layerIndex: number, coords: KnyttPoint, targetId: KnyttPoint): void {
    const obj = createObject(this.game, this.area, layerIndex, coords, targetId);
    if (!obj) return;
    if (!(this.overlay && layerIndex === 3)) {
      const band = LIQUID_POOL_DEADLY_BAND[targetId.key];
      if (band) this.deadlyPoolRects.push([obj.worldX, obj.worldY + band[0], TILE_SIZE, band[1]]);
      const name = objectName(targetId.x, targetId.y);
      if (name === 'Halt' || name === 'NoClimb') this.climbBlockCells.push([obj.worldX, obj.worldY]);
    }
    if (this.overlay && layerIndex === 3) obj.neutralized = true;
    obj.container.zIndex = this.overlay && layerIndex === 3 ? 15 : obj.zIndex;
    this.game.worldLayer.addChild(obj.container);
    this.objects.push(obj);
    void obj.load();
  }

  /** Remove an object from the area (DeletePoint). */
  removeObject(obj: KnyttObject): void {
    obj.deleted = true;
    obj.customDeletion(); // e.g. Warp resets the area warp; SwimScreen turns swim off
    obj.destroy();
    const i = this.objects.indexOf(obj);
    if (i >= 0) this.objects.splice(i, 1);
  }

  /** Delete every object whose centre lies in the tile at (wx,wy), except `self`. */
  deleteObjectsInTile(wx: number, wy: number, self: KnyttObject): void {
    for (const o of [...this.objects]) {
      if (o === self || o.deleted) continue;
      const cx = o.worldX + TILE_SIZE / 2;
      const cy = o.worldY + TILE_SIZE / 2;
      if (cx >= wx && cx < wx + TILE_SIZE && cy >= wy && cy < wy + TILE_SIZE) this.removeObject(o);
    }
  }

  /** Play the bright pickup sparkle (GDKnyttArea.playEffect "collect") at a world pixel. */
  playEffect(wx: number, wy: number): void {
    void this.game.assets.getTexture('objects/bank0/img/pickup.png').then((tex) => {
      if (!tex) return;
      const s = new Sprite(tex);
      s.anchor.set(0.5, 0.5);
      s.scale.set(1.5);
      s.position.set(wx, wy);
      s.alpha = 0;
      s.zIndex = 16;
      this.game.worldLayer.addChild(s);
      this.effects.push({ sprite: s, age: 0 });
    });
  }

  constructor(
    private game: Game,
    private area: KnyttArea,
    private view: AreaView,
  ) {}

  // Area Overlay=True: object layer 3 renders at z15 and is neutralized (can't kill,
  // no solid stamps, safe bullets) — GDObjectLayers.cs:69-100.
  private overlay = false;

  async load(): Promise<void> {
    // Fresh automation state for this area (set below if such objects exist).
    this.game.input.resetAlt();
    this.game.areaSwim = false; // set by a SwimScreenObject during load
    if (this.area.empty) return;
    this.overlay = this.area.getExtraData('Overlay')?.toLowerCase() === 'true';
    // Iterate layers back-to-front (matches original) so z-order is consistent.
    for (let layer = 3; layer >= 0; layer--) {
      const data = this.area.objectLayers[layer];
      if (!data) continue;
      for (let y = 0; y < AREA_HEIGHT; y++) {
        for (let x = 0; x < AREA_WIDTH; x++) {
          const oid = getObjectId(data, x, y);
          if (oid.isZero) continue;
          const name = objectName(oid.x, oid.y);
          // GhostMusic silences the area's music unless Juni has the Eye power.
          if (name === 'GhostMusic' && !this.game.values.getPower(PowerNames.Eye))
            this.playNoMusic = true;
          // Cloud (7,16): a surface that makes Juni kick up cloud puffs when she moves
          // on it (JuniMotionParticles). Register the tile; it has no sprite of its own.
          if (name === 'Cloud') {
            const tx = this.area.position.x * AREA_WIDTH + x;
            const ty = this.area.position.y * AREA_HEIGHT + y;
            this.cloudCells.add(`${tx},${ty}`);
            continue;
          }
          // LocalAmbient/MuteAmbient mark their atmosphere slot custom-volume (37/39 → A, 38/40 → B).
          if (name === 'LocalAmbient' || name === 'MuteAmbient')
            this.customAmbiance[oid.y === 37 || oid.y === 39 ? 0 : 1] = true;
          const neutral = this.overlay && layer === 3;
          const wx = (this.area.position.x * AREA_WIDTH + x) * TILE_SIZE;
          const wy = (this.area.position.y * AREA_HEIGHT + y) * TILE_SIZE;
          // Deadly pools destroy bullets over their wave band (LiquidPoolDeadly.cs).
          const band = LIQUID_POOL_DEADLY_BAND[oid.key];
          if (band && !neutral) this.deadlyPoolRects.push([wx, wy + band[0], TILE_SIZE, band[1]]);
          // Halt/NoClimb areas block the Climb checker (collision layer 16, see fields).
          if ((name === 'Halt' || name === 'NoClimb') && !neutral)
            this.climbBlockCells.push([wx, wy]);
          const obj = createObject(this.game, this.area, layer, new KnyttPoint(x, y), oid);
          if (!obj) continue;
          if (this.overlay && layer === 3) obj.neutralized = true;
          obj.container.zIndex = this.overlay && layer === 3 ? 15 : obj.zIndex;
          this.game.worldLayer.addChild(obj.container);
          this.objects.push(obj);
        }
      }
    }
    await Promise.all(this.objects.map((o) => o.load()));
    // Drop sprites for objects that removed themselves during load.
    void this.view;

    // Group leaf generators by object id (pickOne rotation per type).
    const byId = new Map<string, LeafObject[]>();
    for (const o of this.objects) {
      if (o instanceof LeafObject && o.isGenerator) {
        const k = o.objectId.key;
        (byId.get(k) ?? byId.set(k, []).get(k)!).push(o);
      }
    }
    for (const members of byId.values()) {
      const interval = members[0].cfg.spawnInterval;
      // syncedRandomDelay: stagger the first tick within the interval.
      this.leafGroups.push({ members, timer: Math.random() * interval, idx: 0, interval });
    }
    this.rainGen = this.objects.filter(
      (o): o is RainObject => o instanceof RainObject && o.isGenerator,
    );
  }

  update(dt: number): void {
    for (const o of this.objects) {
      if (!o.deleted) o.update(dt);
    }
    // Leaf generators: one rotating member spawns a particle per interval (pickOne).
    for (const g of this.leafGroups) {
      g.timer -= dt;
      if (g.timer <= 0) {
        g.timer += g.interval;
        const m = g.members[g.idx % g.members.length];
        g.idx++;
        if (!m.deleted) m.spawnOne();
      }
    }
    // Rain: one rotating generator per frame attempts a drop (~dt/0.025 chance).
    if (this.rainGen.length && Math.random() < dt / 0.025) {
      const r = this.rainGen[this.rainIdx % this.rainGen.length];
      this.rainIdx++;
      if (!r.deleted) r.spawnDrop();
    }
    // Apply any per-frame ambiance volume requests (LocalAmbient) or hold the mute.
    if (this.customAmbiance[0] || this.customAmbiance[1]) this.game.audio.commitAmbiance();
    // Collect-effect sparkles: alpha 0→bright(0.1s)→0(by 2s), then remove.
    for (let i = this.effects.length - 1; i >= 0; i--) {
      const e = this.effects[i];
      e.age += dt;
      e.sprite.alpha = e.age < 0.1 ? e.age / 0.1 : Math.max(0, 1 - (e.age - 0.1) / 1.9);
      if (e.age >= 2) {
        e.sprite.destroy();
        this.effects.splice(i, 1);
      }
    }
  }

  /** Every base Password in the area shows its correct symbol (Password.checkPassword). */
  allPasswordsCorrect(): boolean {
    for (const o of this.objects) {
      // exact type only — ClearBlock/HoldButton subclasses don't count (yknytt: GetType()==typeof(Password))
      if (o.constructor === PasswordObject && !(o as PasswordObject).isCorrect()) return false;
    }
    return true;
  }

  /** Open every lock block in the area (Password.destroyWalls). */
  openLocks(): void {
    let any = false;
    for (const o of this.objects) {
      if (o instanceof LockBlockObject && !o.deleted && !o.isOpening) {
        any = true;
        o.open(true);
      }
    }
    if (any) this.game.audio.sfx('Right Combination');
  }

  destroy(): void {
    for (const o of this.objects) o.destroy();
    this.objects = [];
    for (const e of this.effects) e.sprite.destroy();
    this.effects = [];
  }
}
